/**
 * Bodhi (बोधिः) — Gurukul Mascot & Learning Guide
 * Comprehensive knowledge base, contextual tips, Subhāṣitas,
 * spoken Sanskrit phrases, and student Q&A library.
 */

export interface Subhashita {
  id: string;
  verseDevanagari: string;
  verseIast: string;
  englishMeaning: string;
  bodhiReflection: string;
  source?: string;
  theme: string;
}

export interface SpokenPhrase {
  id: string;
  devanagari: string;
  iast: string;
  english: string;
  context: string;
  category: 'greeting' | 'polite' | 'classroom' | 'praise';
}

export interface BodhiContextTip {
  viewId: string;
  title: string;
  sanskritTitle: string;
  summary: string;
  bulletPoints: string[];
  bodhiAdvice: string;
  suggestedAction?: {
    label: string;
    targetView?: string;
  };
}

export interface BodhiQAItem {
  id: string;
  category: 'pronunciation' | 'grammar' | 'cbse' | 'vedic_math' | 'philosophy' | 'engineering' | 'anecdote';
  question: string;
  sanskritQuestion?: string;
  shortAnswer: string;
  detailedAnswer: string;
  audioDevanagari?: string;
  tip?: string;
  relatedView?: string;
  /** Opens this article anchor on /vedic-maths (same landing as site search). */
  relatedVedicAnchor?: string;
  /** Opens this grammar-shelf article (same landing as site search). */
  relatedGrammarArticleId?: string;
  /** Opens this quiz anchor on /quiz (same landing as site search). */
  relatedQuizAnchor?: string;
  /** Opens this Vijñāna Lab segment on /science-lab (same landing as site search). */
  relatedLabAnchor?: string;
  /** Micro-Anecdotes only: small inline emoji graphic, the science bit, the source line and a custom link label. */
  emoji?: string;
  science?: string;
  source?: string;
  relatedLabel?: string;
  /** Opens this grammar-shelf guide (same landing as site search). */
  relatedGrammarTopic?:
    | 'home'
    | 'vibhakti'
    | 'linga-vachana'
    | 'numbers'
    | 'samyukta'
    | 'sound-teams'
    | 'science-of-sound'
    | 'dhatupatha'
    | 'article';
}

export interface BodhiWordSuggestion {
  id: string;
  devanagari: string;
  iast: string;
  english: string;
  category: 'root' | 'tech' | 'grammar' | 'vocab';
  breakdown?: string;
  modernConcept?: string;
}

export const BODHI_PROFILE = {
  name: 'Bodhi',
  devanagariName: 'बोधिः',
  title: 'Gurukul Mascot & Learning Guide',
  sanskritTitle: 'गुरुकुल-सखा एवं ज्ञान-मार्गदर्शकः',
  tagline: 'Illuminating your path through Sanskrit, grammar, and Vedic mathematics with heart, breath, and joy.',
  persona:
    'A calm, unhurried Gurukul guru who speaks slowly and clearly for children — reciting each phrase and half-verse with a gentle pause so young learners can listen and repeat.',
  greetingText: 'नमस्ते। अहं बोधिः — भवतः संस्कृत-सखा। (Namaste. I am Bodhi — your Gurukul Sanskrit friend. Ask me anything.)',
  audioGreeting: 'नमस्ते। अहं बोधिः।',
};

/**
 * ✨ Did you know? — Bodhi’s Micro-Anecdotes.
 * Short, playful stories with a hook, the science bit and a source line.
 * Merged into the Ask Bodhi library (category 'anecdote') and searchable via site search.
 */
export const BODHI_ANECDOTES: BodhiQAItem[] = [
  {
    id: 'qa-anecdote-indus-spheres',
    category: 'anecdote',
    emoji: '🏺📏',
    question: 'The 3.5 cm Spheres: A 5,000-year mystery solved by a ruler',
    sanskritQuestion: 'प्रमाण-गोलकौ · the twin Indus spheres',
    shortAnswer: '✨ Did you know two clay balls found hundreds of miles and millennia apart match to the exact millimetre?',
    detailedAnswer:
      'In Bhirrana (Haryana) from the early horizon, and at Mohenjo-daro (Sindh) over a thousand years later, archaeologists excavated two terracotta spheres. When placed under precision calipers, both measured exactly 3.5 centimeters in diameter! One has equidistant circular holes; the other has incised spiral grooves. Why 3.5 cm? Because 3.5 cm equals exactly 2 Indus Aṅgulas on the Lothal ivory scale (1.763 cm per unit). It was a civilization-wide metrology standard that lasted 5,000 years! 📏 Level unlocked: Master of Measures!',
    science:
      'Standardized sphericity in damp clay requires multi-axis compression molds and rotational templates. The Indus metrology system combined binary ratios (1, 2, 4, 8, 16 for chert stone weights) with a decimal linear scale, producing architectural bricks in strict 1:2:4 ratios and 16-Aṅgula lengths.',
    source: 'Excavation reports from Bhirrana (Archaeological Survey of India) and Mohenjo-daro (Mackay / Marshall); Lothal Ivory Scale (S.R. Rao).',
    tip: 'Notice how the 16-ratio survived into modern India: 16 annas in a rupee, and 16 aṅgulas in traditional mason scales!',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-the-spheres-of-certainty-5000-years-indus-metrology',
    relatedLabel: '📏 Explore Indus Metrology & Calibration ➔',
    audioDevanagari: 'प्रमाण-गोलकौ मानशास्त्रम्',
  },
  {
    id: 'qa-anecdote-nana-moutou-1768',
    category: 'anecdote',
    emoji: '🐚✨',
    question: 'The Cowrie Shell Computer: How a Tamil scholar predicted an eclipse in 45 minutes',
    sanskritQuestion: 'वाक्यकरणम् · वराटिका-गणनम् (Pondicherry 1768)',
    shortAnswer: '✨ Did you know a Tamil calculator in 1768 matched Europe’s Royal Academy on a dirt floor with shells?',
    detailedAnswer:
      'In December 1768, French Royal astronomer Guillaume Le Gentil challenged Tamil calculator Nana Moutou in Pondicherry to predict an upcoming lunar eclipse. Sitting on the dirt floor with a bag of cowrie shells and small palm-leaf booklets, Nana Moutou chanted rhythmic Sanskrit and Tamil astronomical verses, sliding shells across place-value columns and wiping the floor clean after every step. In less than 45 minutes, he completed the full prediction—matching the Paris Observatory’s ephemeris to the exact minute! 🐚 Level unlocked: Cognitive Computer!',
    science:
      'Nana Moutou used the Vākyakaraṇa system (c. 1282–1306 CE) containing 247 Cāndravākyas of Vararuci and Mādhava, encoding orbital parameters via the Kaṭapayādi cipher. The cowrie shells acted as ephemeral RAM registers, freeing up working memory for the human brain.',
    source: 'Guillaume Le Gentil, Voyage dans les mers de l\'Inde (1779); Vākyakaraṇa; Cāndravākyas of Vararuci & Mādhava.',
    tip: 'Cowrie shells were the physical variables, the dirt floor was the monitor, and wiping the floor clean was flushing the RAM!',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-the-astronomer-and-the-astrologer-tamil-scholar-1768',
    relatedLabel: '🐚 Read The Astronomer & The Astrologer (1768) ➔',
    audioDevanagari: 'वाक्यकरणम् वराटिका-गणितम् कटपयादि ली जेन्तिल नाना मुतु',
  },
  {
    id: 'qa-anecdote-sarpakavu',
    category: 'anecdote',
    emoji: '🐍🌳',
    question: 'Sarpakāvu: the little forests nobody was allowed to cut',
    sanskritQuestion: 'Sarpakāvu · the sacred serpent groves of Kerala',
    shortAnswer: '✨ Did you know Kerala families kept little wild forests that no one was allowed to cut?',
    detailedAnswer:
      'Picture a house in Kerala with a shady, tangled patch of trees in one corner of the compound. That is a sarpakāvu, a sacred serpent grove (kāvu means grove). Families and temples left these patches untouched for the nāgas, the serpent deities, often with a small shrine of stone snake images where lamps were lit. The custom was simple and strict: no cutting branches, no clearing the undergrowth, and no harming the snakes. 🐍 Level unlocked: Grove Guardian!',
    science:
      'Today ecologists study sacred groves like these as small pockets of biodiversity. Because people left them alone for generations, they can shelter rare and native plants, insects, birds, frogs and, yes, snakes. Their shade and thick leaf litter help keep the soil moist and help rainwater soak in, which helps keep nearby ponds and wells recharged. A custom of respect ended up working like a tiny conservation plan.',
    source: 'Sarpakāvu is a Malayalam word. Sacred groves are found across India; Kerala’s serpent groves are among the best known.',
    tip: 'Next time you see an old tree that nobody cuts, ask whose home it is: birds, bees, or a whole hidden ecosystem!',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-kerala-school-calculus-and-infinite-series',
    relatedLabel: '📐 Another Kerala story: the Kerala School of Mathematics ➔',
  },
  {
    id: 'qa-anecdote-valmika',
    category: 'anecdote',
    emoji: '🐜💧',
    question: 'Valmīka: the anthill that hinted at hidden water',
    sanskritQuestion: 'वल्मीकः (valmīka) · anthill, termite mound',
    shortAnswer: '✨ Did you know? How did people look for hidden underground water without machines?',
    detailedAnswer:
      'Meet the ancient water detective! The Bṛhat Saṃhitā, an encyclopaedic work attributed to Varāhamihira (c. 6th century CE), has a whole chapter on finding groundwater called Dakārgala, on underground water veins (chapter 54 in N. Chidambaram Iyer’s English translation; some Sanskrit editions number it 53). It reads the land like a map of clues: which trees grow there, the colour of the soil, rocks and animals. One clue (54.9): “If there be an ant-hill to the east of the Jambū and near it, there runs a current to the south of the ant-hill at the depth of two men. The water will be very sweet.” A “man” (puruṣa) is a unit of depth, roughly a person’s height. 🔍 Clue found: +10 Detective XP!',
    science:
      'Why would an anthill be a clue? Termites need moisture to survive, and some kinds dig tunnels down toward damp soil and build their mounds from that moist earth, so a mound can hint that moisture is near. Modern researchers have tested such traditional signs, with mixed results. So it is a clue, not a guarantee: today groundwater experts confirm with surveys and test drilling.',
    source: 'Bṛhat Saṃhitā 54.9 (Dakārgala): जम्बूवृक्षस्य प्राग्वल्मीको यदि भवेत्समीपस्थः । तस्माद्दक्षिणपार्श्वे सलिलं पुरुषद्वये स्वादु ॥ Tr. N. Chidambaram Iyer (1884), via wisdomlib.org.',
    tip: 'Fun fact: the poet Vālmīki’s name is traditionally linked to valmīka, after the legend that an anthill grew over him while he sat in meditation.',
    audioDevanagari: 'वल्मीकः',
  },
  {
    id: 'qa-anecdote-bhrigu-plants',
    category: 'anecdote',
    emoji: '🌿👂',
    question: 'Plants that sense: Bhṛgu’s answer to Bharadvāja',
    sanskritQuestion: 'जीवं पश्यामि वृक्षाणाम् (jīvaṃ paśyāmi vṛkṣāṇām) · “I see life in trees”',
    shortAnswer: '✨ Did you know? Can a plant sense the world? A Mahābhārata sage argued it can!',
    detailedAnswer:
      'In a dialogue preserved in the Mahābhārata’s Śānti Parva (the Mokṣadharma section), the sage Bharadvāja throws down a challenge: trees don’t move, don’t hear, don’t see, can’t smell or taste, so how can they be living things made of the five elements? Sage Bhṛgu answers point by point. 🍂 Touch: in heat, leaves, bark, fruit and flowers wilt and dry up. 🌩️ Hearing: the crash of wind, fire and thunder makes fruit and flowers drop. 👀 Sight: a creeper winds its way all around a tree, and a blind thing cannot find its way. 👃 Smell: good and bad odours and incense smoke affect how trees thrive and flower. 💧 Taste: trees drink water through their roots, like sipping through a lotus stalk, and they fall ill and can be cured. They feel pleasure and pain, he says, and grow back when cut. His verdict: “I see life in trees; they are not without awareness.” 🏆 Debate won: +1 Wisdom!',
    science:
      'In these texts plants are sthāvara (immobile beings) and yet jīva (living). Modern research agrees plants are far from passive: they sense light, touch and damage, and signal with chemicals and electrical signals. In the early 1900s the Indian scientist Jagadish Chandra Bose built sensitive instruments to measure the electrical responses of plants. Whether plants actually feel anything is still debated, so Bhṛgu’s argument is a brilliant observation, not a lab proof.',
    source: 'Mahābhārata, Śānti Parva, Bhṛgu–Bharadvāja dialogue: 12.177.10–18 in the Critical Edition; Section CLXXXIV (184) in K. M. Ganguli’s translation; 12.185 in the Gita Press edition.',
    tip: 'Watch a creeper for a week and mark where its tip points each day. What do you think it is “looking” for?',
    relatedView: 'science-lab',
    relatedLabAnchor: 'chaturyoni',
    relatedLabel: '🔬 Open this lab: Chaturyoni Spawn Map (Sthāvara–Jaṅgama) ➔',
  },
];

export const BODHI_SUBHASHITAS: Subhashita[] = [
  {
    id: 'subh-1',
    verseDevanagari: 'विद्या ददाति विनयं विनयाद्याति पात्रताम् ।\nपात्रत्वाद्धनमाप्नोति धनाद्धर्मं ततः सुखम् ॥',
    verseIast: 'vidyā dadāti vinayaṁ vinayādyāti pātratām |\npātratvāddhanamāpnoti dhanāddharmaṁ tataḥ sukham ||',
    englishMeaning: 'Knowledge bestows humility; from humility comes worthiness; from worthiness one acquires prosperity; from prosperity comes righteousness, and thence lasting joy.',
    bodhiReflection: 'True learning never makes us arrogant. When you learn even a single new Sanskrit word today, let it make you gentler and more curious about the world around you!',
    source: 'Hitopadeśa (हितोपदेशः)',
    theme: 'Humility & Knowledge',
  },
  {
    id: 'subh-2',
    verseDevanagari: 'उद्यमेन हि सिध्यन्ति कार्याणि न मनोरथैः ।\nन हि सुप्तस्य सिंहस्य प्रविशन्ति मुखे मृगाः ॥',
    verseIast: 'udyamena hi sidhyanti kāryāṇi na manorathaiḥ |\nna hi suptasya siṁhasya praviśanti mukhe mṛgāḥ ||',
    englishMeaning: 'Tasks are accomplished only by diligent effort, never by mere daydreaming. Deer do not enter the mouth of a sleeping lion on their own.',
    bodhiReflection: 'Even the king of the jungle must take action! Reading just 10 minutes a day and speaking the syllables aloud with me will build your fluency faster than wishing for it.',
    source: 'Pañcatantra (पञ्चतन्त्रम्)',
    theme: 'Perseverance & Action',
  },
  {
    id: 'subh-3',
    verseDevanagari: 'अयं निजः परो वेति गणना लघुचेतसाम् ।\nउदारचरितानां तु वसुधैव कुटुम्बकम् ॥',
    verseIast: 'ayaṁ nijaḥ paro veti gaṇanā laghucetasām |\nudāracaritānāṁ tu vasudhaiva kuṭumbakam ||',
    englishMeaning: '"This is mine, that belongs to another" is the narrow reckoning of small minds. For the noble-hearted, the entire earth is one single family.',
    bodhiReflection: 'Sanskrit literature always reminds us that all human beings share the same heartbeat and the same breath. We learn ancient wisdom to bring everyone closer together.',
    source: 'Mahā-Upaniṣad (महोपनिषद्)',
    theme: 'Universal Harmony (वसुधैव कुटुम्बकम्)',
  },
  {
    id: 'subh-4',
    verseDevanagari: 'भाषासु मुख्या मधुरा दिव्या गीर्वाणभारती ।\nतस्यां हि काव्यं मधुरं तस्मादपि सुभाषितम् ॥',
    verseIast: 'bhāṣāsu mukhyā madhurā divyā gīrvāṇabhāratī |\ntasyāṁ hi kāvyaṁ madhuraṁ tasmādapi subhāṣitam ||',
    englishMeaning: 'Among languages, the divine Sanskrit tongue is foremost, sweet, and luminous. Even sweeter within it is poetry, and sweetest of all are its wise sayings (subhāṣitas).',
    bodhiReflection: 'Notice how melodious Sanskrit sounds when spoken slowly! That sweetness comes from the systematic harmony between vowel lengths and consonant vibration points.',
    source: 'Subhāṣita-ratna-bhāṇḍāgāra',
    theme: 'Beauty of Sanskrit',
  },
  {
    id: 'subh-5',
    verseDevanagari: 'क्षणशः कणशश्चैव विद्यामर्थं च साधयेत् ।\nक्षणत्यागे कुतो विद्या कणत्यागे कुतो धनम् ॥',
    verseIast: 'kṣaṇaśaḥ kaṇaśaścaiva vidyāmarthaṁ ca sādhayet |\nkṣaṇatyāge kuto vidyā kaṇatyāge kuto dhanam ||',
    englishMeaning: 'One should acquire knowledge moment by moment, and wealth grain by grain. If you squander moments, where is knowledge? If you squander grains, where is wealth?',
    bodhiReflection: 'You do not need 3 hours at once. Even 5 mindful minutes tracing one Devanagari letter or practicing one Sandhi formula is a golden grain stored in your mind.',
    source: 'Cāṇakya Nīti (चाणक्य-नीतिः)',
    theme: 'Consistency & Micro-Habits',
  },
  {
    id: 'subh-6',
    verseDevanagari: 'सत्यमेव जयते नानृतं सत्येन पन्था विततो देवयानः ।',
    verseIast: 'satyameva jayate nānṛtaṁ satyena panthā vitato devayānaḥ |',
    englishMeaning: 'Truth alone triumphs, not untruth. By truth is the divine path laid out.',
    bodhiReflection: 'The sacred motto of India! Truth (Satya) in speech means speaking what is true, gentle, and beneficial for all living beings.',
    source: 'Muṇḍaka Upaniṣad (मुण्डकोपनिषद् 3.1.6)',
    theme: 'Truth & Integrity',
  },
  {
    id: 'subh-7',
    verseDevanagari: 'गुरुर्ब्रह्मा गुरुर्विष्णुर्गुरुर्देवो महेश्वरः ।\nगुरुः साक्षात् परं ब्रह्म तस्मै श्रीगुरवे नमः ॥',
    verseIast: 'gururbrahmā gururviṣṇurgururdevo maheśvaraḥ |\nguruḥ sākṣāt paraṁ brahma tasmai śrīgurave namaḥ ||',
    englishMeaning: 'The Guru is Brahmā (creator of wisdom), the Guru is Viṣṇu (sustainer of learning), the Guru is Śiva (dispeller of ignorance). The Guru is verily the Supreme Reality itself — salutations to that revered Guru.',
    bodhiReflection: 'In our Gurukul, the Guru is not a faster search engine or generative model. The Guru is a living presence — the living breath and constraint that transforms mere information into living realization.',
    source: 'Guru Gītā (गुरुगीता)',
    theme: 'The Living Teacher (गुरु-महिमा)',
  },
];

export const BODHI_SPOKEN_PHRASES: SpokenPhrase[] = [
  {
    id: 'phr-1',
    devanagari: 'नमस्ते',
    iast: 'Namaste',
    english: 'Hello / Greetings (I bow to the divine in you)',
    context: 'Traditional universal greeting at any time of day',
    category: 'greeting',
  },
  {
    id: 'phr-2',
    devanagari: 'सुप्रभातम्',
    iast: 'Suprabhātam',
    english: 'Good Morning',
    context: 'Morning greeting for teachers, elders, and peers',
    category: 'greeting',
  },
  {
    id: 'phr-3',
    devanagari: 'शुभरात्रिः',
    iast: 'Śubharātriḥ',
    english: 'Good Night',
    context: 'Parting wish before going to sleep',
    category: 'greeting',
  },
  {
    id: 'phr-4',
    devanagari: 'धन्यवादः',
    iast: 'Dhanyavādaḥ',
    english: 'Thank you',
    context: 'Expressing gratitude for help or kindness',
    category: 'polite',
  },
  {
    id: 'phr-5',
    devanagari: 'स्वागतम्',
    iast: 'Swāgatam',
    english: 'Welcome',
    context: 'Welcoming someone to your home or classroom',
    category: 'polite',
  },
  {
    id: 'phr-6',
    devanagari: 'क्षम्यताम्',
    iast: 'Kṣamyatām',
    english: 'Excuse me / Please forgive me',
    context: 'Polite apology or asking for someone’s attention',
    category: 'polite',
  },
  {
    id: 'phr-7',
    devanagari: 'पुनर्मिलामः',
    iast: 'Punarmilāmaḥ',
    english: 'We will meet again (See you later)',
    context: 'Warm farewell instead of "goodbye"',
    category: 'greeting',
  },
  {
    id: 'phr-8',
    devanagari: 'अस्तु',
    iast: 'Astu',
    english: 'Alright / So be it / Okay',
    context: 'Expressing agreement or acknowledgment',
    category: 'classroom',
  },
  {
    id: 'phr-9',
    devanagari: 'साधु साधु!',
    iast: 'Sādhu sādhu!',
    english: 'Well done! Splendid!',
    context: 'Expressing praise and joy when someone succeeds',
    category: 'praise',
  },
  {
    id: 'phr-10',
    devanagari: 'उत्तमम्!',
    iast: 'Uttamam!',
    english: 'Excellent! Perfect!',
    context: 'Top praise for correct answers or graceful recitation',
    category: 'praise',
  },
  {
    id: 'phr-11',
    devanagari: 'कथमस्ति भवान्?',
    iast: 'Katham asti bhavān?',
    english: 'How are you? (to a male)',
    context: 'Friendly inquiry about someone’s well-being',
    category: 'classroom',
  },
  {
    id: 'phr-12',
    devanagari: 'कथमस्ति भवती?',
    iast: 'Katham asti bhavatī?',
    english: 'How are you? (to a female)',
    context: 'Friendly inquiry about someone’s well-being',
    category: 'classroom',
  },
];

export const BODHI_CONTEXT_TIPS: Record<string, BodhiContextTip> = {
  home: {
    viewId: 'home',
    title: 'Welcome to the Gurukul Campus!',
    sanskritTitle: 'गुरुकुल-प्राङ्गणम्',
    summary: 'Here you have all learning paths at your fingertips: NCERT Deepakam, interactive puzzles, grammar charts, and Vedic Math.',
    bulletPoints: [
      'New to Sanskrit? Start with the Alphabet & Syllables (वर्णमाला) to train your mouth and ear.',
      'Studying for CBSE Class 7 or 8? Jump straight into the NCERT Deepakam Living Reader.',
      'Love mental challenges? Play 6,000+ Jodo tile puzzles or learn 16 Vedic Mathematics speed sutras.',
    ],
    bodhiAdvice: 'Don’t try to rush everything in a single day. Pick one module, recite the words with warmth, and let your mind settle into the rhythm!',
    suggestedAction: {
      label: 'Open Alphabet Studio (वर्णमाला)',
      targetView: 'reader',
    },
  },
  reader: {
    viewId: 'reader',
    title: 'NCERT Deepakam Living Reader',
    sanskritTitle: 'दीपकम् - सजीव-वाचकः',
    summary: 'Explore full chapters word-by-word with instant audio pronunciation, Sandhi split, and case analysis.',
    bulletPoints: [
      'Tap any Sanskrit word in a verse to view its root (धातु), gender (लिङ्गम्), and grammatical case (विभक्ति).',
      'Listen to the sentence audio first, then mute the speaker and recite it aloud yourself.',
      'Notice the Anvaya (अन्वयः) — rearranging poetic shlokas into natural prose word order.',
    ],
    bodhiAdvice: 'Bodhi’s Golden Rule: Read aloud! The magical bind between breath, tongue, and mind happens only when your vocal cords vibrate.',
    suggestedAction: {
      label: 'Practice CBSE Worksheets',
      targetView: 'worksheets',
    },
  },
  board: {
    viewId: 'board',
    title: 'Jodo (जोडो) Tile Puzzle Studio',
    sanskritTitle: 'जोडो - पद-निर्माण-क्रीडा',
    summary: 'Combine Aksharas and syllables to build over 6,000 authentic Sanskrit words across daily themes.',
    bulletPoints: [
      'Look for the root verb or noun stem before picking your prefix (उपसर्ग) or suffix (प्रत्यय).',
      'Pay special attention to conjunct consonants (संयुक्ताक्षर) like क्ष, त्र, and ज्ञ.',
      'Listen to the completed word audio to lock its acoustic pattern into your memory.',
    ],
    bodhiAdvice: 'Puzzles are the most natural way to discover that Sanskrit words are not arbitrary labels — they are logical lego blocks built from roots!',
    suggestedAction: {
      label: 'Explore Verb Conjugations',
      targetView: 'dhatupatha',
    },
  },
  grammar: {
    viewId: 'grammar',
    title: 'Vyākaraṇa: Declensions & Conjugations',
    sanskritTitle: 'व्याकरणम् · शब्दरूपाणि धातुरूपाणि च',
    summary: 'Master noun cases (विभक्ति), verb families (गण), Sandhi formulas, and Indeclinables (अव्यय).',
    bulletPoints: [
      'Learn the 7 Vibhaktis as answers to questions: Who? Whom? By what? For whom? From where? Whose? In what?',
      'Focus on the 5 core school Lakāras: Present (लट्), Future (लृट्), Past (लङ्), Command (लोट्), Potential (विधिलिङ्).',
      'Observe recurring patterns: dual endings (-औ, -भ्याम्) often stay identical across multiple cases!',
    ],
    bodhiAdvice: 'Think of grammar not as a set of cold rules to memorize, but as Pāṇini’s precision instrument for tuning human consciousness.',
    suggestedAction: {
      label: 'Test Knowledge in Quiz',
      targetView: 'quiz',
    },
  },
  dhatupatha: {
    viewId: 'dhatupatha',
    title: 'Pāṇinian Dhātupāṭha Studio',
    sanskritTitle: 'माहेश्वर-धातुपाठ-मञ्चः',
    summary: 'The living engine of Sanskrit verbs: explore 10 Gaṇas, 5 CBSE Lakāras, and Kṛt participles.',
    bulletPoints: [
      'All Sanskrit verbs branch out from approximately 2,000 root dhātus cataloged by Maharṣi Pāṇini.',
      'Use the reverse deconstructor tool to break complex participle words like "गत्वा" back into "गम् + क्त्वा".',
      'Check whether a verb is Parasmaipada (परस्मैपद - action directed outwards) or Ātmanepada (आत्मनेपद - action directed inwards).',
    ],
    bodhiAdvice: 'Roots are living seeds! From the single root "कृ" (to do) grows karma, kartā, karaṇa, kārya, and saṁskāra.',
    suggestedAction: {
      label: 'Explore CBSE Exam Guide',
      targetView: 'cbse-guide',
    },
  },
  'vedic-maths': {
    viewId: 'vedic-maths',
    title: 'Vedic Mathematics Studio',
    sanskritTitle: 'वैदिक-गणित-शाला',
    summary: '16 ancient aphorisms (सूल्राणि) for lightning-fast mental arithmetic, algebra, and geometry.',
    bulletPoints: [
      'Master "Ekādhikena Pūrveṇa" (By one more than the previous) to square numbers ending in 5 in two seconds!',
      'Use "Nikhilam Navataścaramaṁ Daśataḥ" (All from 9 and the last from 10) for rapid base multiplication.',
      'Notice how Vedic Math works visually and symmetrically — giving your left and right brain a joyful workout.',
    ],
    bodhiAdvice: 'Math in ancient India was not separate from philosophy. Zero (शून्य) and Infinity (अनन्त) are two windows into the same mystery!',
    suggestedAction: {
      label: 'Read Philosophy Essay',
      targetView: 'philosophy',
    },
  },
  quiz: {
    viewId: 'quiz',
    title: 'Interactive Quiz Arena',
    sanskritTitle: 'प्रश्नोत्तरी-मञ्चः',
    summary: 'Test your understanding across 39+ interactive quizzes mapped to CBSE curriculum standards.',
    bulletPoints: [
      'Have the Swar · स्वर — Sanskrit Workbook? Swar Review Practice at the top of this page has 9 sets for the Review test on pages 23–24, with instant answers and book page numbers.',
      'Read both the Sanskrit prompt and the answer options carefully before clicking.',
      'Review explanation cards on every question to understand WHY an answer is grammatically correct.',
      'Replay quizzes to achieve 100% mastery and earn Gurukul merit badges!',
    ],
    bodhiAdvice: 'Mistakes are simply gentle guides showing where your attention wants to go next. Never fear a wrong answer — Bodhi celebrates every attempt!',
    suggestedAction: {
      label: 'Practice Worksheets',
      targetView: 'worksheets',
    },
  },
  certificate: {
    viewId: 'certificate',
    title: 'Swar Workbook Certificate',
    sanskritTitle: 'प्रमाणपत्रम्',
    summary: 'Finished Swar · स्वर — Sanskrit Workbook? Answer one question from each part of the book and get your certificate.',
    bulletPoints: [
      'Type the book code printed on page 149 of your workbook.',
      'There are 8 questions, one from each part of the book. Get 6 right (70%) to pass.',
      'Not yet? The page lists the book pages to read again. Try again with new questions as many times as you like.',
    ],
    bodhiAdvice: 'उद्यमेन हि सिध्यन्ति कार्याणि: work gets done by effort (page 21). You can do it!',
    suggestedAction: {
      label: 'Swar Review Practice',
      targetView: 'quiz',
    },
  },
  worksheets: {
    viewId: 'worksheets',
    title: 'Worksheets & Printables',
    sanskritTitle: 'कार्यपत्रिकाः एवं अभ्यास-पुस्तिका',
    summary: 'Printable chapter exercises, translation worksheets, and sandhi drills for school practice.',
    bulletPoints: [
      'Writing with a real pencil on paper activates motor pathways that typing on a screen cannot replace.',
      'Use the answer keys after attempting all problems independently.',
      'Bring these worksheets to your CBSE school class for exam revision!',
    ],
    bodhiAdvice: 'Keep one quiet practice off-screen. Sit with a printed worksheet, take a slow breath, and write each stroke with mindful love.',
  },
  philosophy: {
    viewId: 'philosophy',
    title: 'Darśana: Why Learn Sanskrit in the Age of AI',
    sanskritTitle: 'दर्शनम् · ए.आई. युगे किमर्थं संस्कृतम्?',
    summary: 'Deep reflections on human speech, ancient geodesy, and why AI will make language chosen rather than obsolete.',
    bulletPoints: [
      'Communication has two faces: Transfer (information) and Bind (breath, body, presence).',
      'AI optimizes transfer at lightning speed; Sanskrit preserves the sacred bind of human attention.',
      'Explore ancient geodesy: Ujjain (Avantī) as the 0° Prime Meridian, the 12-Aṅgula Śaṅku for latitude, and lunar eclipses for longitude.',
      'Explore the four levels of speech: Vaikharī (spoken), Madhyamā (mental), Paśyantī (vision), and Parā (source).',
    ],
    bodhiAdvice: 'The phone is new; your vocal cords and vagus nerve are millions of years old. Let the machine assist you, but never let it chant for you!',
  },
  'cbse-guide': {
    viewId: 'cbse-guide',
    title: 'CBSE Sanskrit Exam Blueprint',
    sanskritTitle: 'सी.बी.एस.ई. संस्कृत-परीक्षा-मार्गदर्शिका',
    summary: 'Master the 4 examination sections: Reading Comprehension, Creative Writing, Applied Grammar, and Literature.',
    bulletPoints: [
      'Memorize the "Kim-family" question words: कः (who), कम् (whom), केन (by whom), कस्मै (to whom), कस्मात् (from where), कस्य (whose), कस्मिन् (in what).',
      'In Unseen Passages (अपठित-अवबोधनम्), look for exact sentence parallels in the text for one-word answers.',
      'Keep your picture sentences (चित्र-वर्णनम्) simple: Subject + Object + Verb (लट्-लकार).',
    ],
    bodhiAdvice: 'Exam confidence comes from familiar patterns. Follow the section blueprints step-by-step, and Sanskrit will be your highest-scoring subject!',
  },
  'science-lab': {
    viewId: 'science-lab',
    title: 'Vijñāna Lab · Science Sandboxes',
    sanskritTitle: 'विज्ञान-प्रयोगशाला',
    summary: 'Hands-on sandboxes: build in the Paramāṇu Builder, balance the doṣas in the Prakṛti Biosphere, run the Jyotiṣa Gravity Sandbox through the 27 nakṣatras, play The Loom of Āruṇi, try Cosmic Sitār Tuning, lift The Veil of Māyā from appearance to atoms, sort living beings on the Chaturyoni Spawn Map, and soft-land Somayāna Mission-03 (Chandrayaan).',
    bulletPoints: [
      'Tap any Sanskrit word in a term panel to hear it.',
      'Each sandbox has quick challenges with instant feedback: try them after you play.',
      'Old texts and modern science ask different questions. Look for the labels “traditionally”, “attributed to” and “modern comparison (for fun)”.',
    ],
    bodhiAdvice: 'Play first, then name what you saw in Sanskrit. A word you have touched and moved stays with you longer!',
  },
  faq: {
    viewId: 'faq',
    title: 'Frequently Asked Questions & Care Desk',
    sanskritTitle: 'प्रायिक-प्रश्नाः एवं सहायता-केन्द्रम्',
    summary: 'Got questions about subscriptions, UPI payments, NCERT coverage, or technical features? We are here to help.',
    bulletPoints: [
      'All Chapter 1 lessons and Varṇamālā sounds are completely free for everyone.',
      'UPI and Card payments unlock all 15 chapters, full worksheets, and advanced Vedic math calculators.',
      'Need academic help? Reach out directly to our Learner Care Desk at care@ednetlearn.in.',
    ],
    bodhiAdvice: 'No question is too small. If you ever feel stuck on any concept, just tap on me or send our teachers an email!',
  },
};

export const BODHI_QA_LIBRARY: BodhiQAItem[] = [
  {
    id: 'qa-online-classes-bodhi',
    category: 'cbse',
    question: 'Does Bodhi offer Sanskrit online classes?',
    sanskritQuestion: 'बोधिः ऑनलाइन-संस्कृत-कक्षासु शिक्षयति वा?',
    shortAnswer: 'Yes! Bodhi offers 24/7 interactive Sanskrit online classes across Spoken Sanskrit, CBSE NCERT school prep, Pāṇinian Vyakaran, and Vedic Mathematics.',
    detailedAnswer:
      'Bodhi’s Virtual Gurukul Online Classes provide self-paced, audio-guided micro-learning tailored for complete beginners, children, CBSE students (Classes 6–10), and adult enthusiasts. Each class features 15-minute interactive daily sessions, authentic phonetic pronunciation across all 5 vocal zones, line-by-line CBSE Deepakam chapter breakdowns, and instant 1-on-1 doubt solving with Bodhi. You can join for free by clicking "Online Classes" in the navigation or on the homepage.',
    tip: 'Take a sample micro-class right now: click "Online Classes" on the home screen to test Bodhi’s live repeat-after-me speaking practice.',
    relatedView: 'home',
    relatedLabel: '🎓 Open Bodhi Online Classes ➔',
  },
  {
    id: 'qa-swar-certificate',
    category: 'cbse',
    question: 'How do I get my certificate for the Swar workbook?',
    sanskritQuestion: 'प्रमाणपत्रं कथं प्राप्नोमि?',
    shortAnswer: 'Go to ednetlearn.in/certificate, enter the book code from page 149 and score 70% in the 8-question book quiz.',
    detailedAnswer:
      'Finished Swar · स्वर — Sanskrit Workbook (The Darśana Series, by Sesha K Penumallu)? Open the Certificate page (or scan the QR code on page 149) and type the book code printed on page 149. You get 8 questions, one from each part of the book: Letters & Words, Festivals & Seasons, Animals, Indian Plants, the three grammar levels and Vedic maths. Get 6 right (70%) to pass. Then type your name and your colourful certificate appears with the date and your own certificate ID, ready to download as PNG or PDF, or to print. If you score less, the page shows which pages to read again, and you can try again with new questions.',
    tip: 'Signing in is not needed. Sign in if you want the same certificate ID on every device.',
    relatedView: 'certificate',
    relatedLabel: '🏅 Get my certificate ➔',
  },
  {
    id: 'qa-swar-review-practice',
    category: 'cbse',
    question: 'Where can I practise for the Swar review test?',
    sanskritQuestion: 'पुनरावृत्तिः कुत्र अभ्यसामि?',
    shortAnswer: 'Scan the QR code on page 24 or open ednetlearn.in/sanskrit-quiz/: Swar Review Practice is at the top.',
    detailedAnswer:
      'The Review test on pages 23–24 of Swar · स्वर — Sanskrit Workbook has 20 marks. On ednetlearn.in/sanskrit-quiz/, Swar Review Practice gives you 9 sets with many more questions: choose the meaning, say it in Sanskrit, match the pairs, fill in the verb (अहं पठामि, त्वं पठसि, सः पठति), सः / सा / तत्, number words १–१०, one-two-many (एकवचनम्, द्विवचनम्, बहुवचनम्), listen and pick, and complete the saying उद्यमेन हि सिध्यन्ति कार्याणि न मनोरथैः. Every answer shows the book page to look at.',
    tip: 'Play a set again for new questions. For more, try the interactive puzzles, games and worksheets.',
    relatedView: 'quiz',
    relatedLabel: '📝 Open Swar Review Practice ➔',
  },
  {
    id: 'qa-retroflex',
    category: 'pronunciation',
    question: 'How do I pronounce retroflex (मूर्धन्य) sounds like ट, ठ, ड, ढ, ण, and ष?',
    sanskritQuestion: 'मूर्धन्य-वर्णानाम् उच्चारणं कथं भवति?',
    shortAnswer: 'Curl the tip of your tongue backwards and touch the hard roof (palate) of your mouth.',
    detailedAnswer: 'In Sanskrit phonetics (शिक्षा), the mouth has 5 distinct contact zones. For dental sounds (त, थ, द) the tongue touches the back of the teeth. But for retroflex sounds (मूर्धन्य: ट, ठ, ड, ढ, ण, ष), you curl the tongue tip up and tap the domed roof of your mouth. Notice the crisp, percussive sound it creates!',
    audioDevanagari: 'ट ठ ड ढ ण',
    tip: 'Try saying the English word "train" — notice where your tongue sits. Move it slightly further back, and you have found the Mūrdhanya position!',
    relatedView: 'reader',
  },
  {
    id: 'qa-visarga',
    category: 'pronunciation',
    question: 'What does the Visarga (ः) actually sound like?',
    sanskritQuestion: 'विसर्गस्य उच्चारणं किम्?',
    shortAnswer: 'It is a soft, breathy echo of the preceding vowel, like a gentle exhale.',
    detailedAnswer: 'The Visarga (ः) is not a hard "hah". It mirrors whichever vowel comes before it! After "a" (रामः) it sounds like "raamaha". After "i" (हरिः) it sounds like "harihi". After "u" (गुरुः) it sounds like "guruhu". It is an unvoiced, breathy aspiration that marks the end of a breath group.',
    audioDevanagari: 'रामः हरिः गुरुः',
    tip: 'Think of the visarga as the wind gently rustling through leaves — keep it soft and natural!',
    relatedView: 'reader',
  },
  {
    id: 'qa-vowel-length',
    category: 'pronunciation',
    question: 'What does the symbol on top of vowels mean (like ā in [vidyā]), and what is the difference between Hrasva (ह्रस्व) and Dīrgha (दीर्घ)?',
    sanskritQuestion: 'ह्रस्व-दीर्घ-स्वरयोः कः भेदः? आ-कारे उपरि दण्डस्य कः अर्थः?',
    shortAnswer: 'The horizontal bar on top (called a macron: ā) denotes a Dīrgha (दीर्घ / long) vowel held for 2 beats (mātrās), like "aa" in father versus "u" in cut.',
    detailedAnswer: 'In Sanskrit transliteration (IAST), the horizontal bar above a vowel (like ā, ī, ū) is called a macron. In Sanskrit phonetics (शिक्षा / Śikṣā), it denotes a Dīrgha (दीर्घ) or long vowel:\n• Plain "a" (अ) = Hrasva (ह्रस्व / short, 1 mātrā), pronounced like the "u" in "cut" or "sun".\n• "ā" with a macron (आ) = Dīrgha (दीर्घ / long, 2 mātrās), held for twice the duration, sounding like "aa" in "father".\nIn [vidyā] (विद्या), "vi" has a short "i" (1 mātrā), while "dyā" has a long "ā" (2 mātrās). In Sanskrit, vowel duration changes the entire meaning of a word! For example: "शिव" (Śiva - auspicious) with short "i", but "शीत" (cold) with long "ī"; or "सुतः" (son) vs "सुता" (daughter). In the ancient Prātiśākhyas, 1 mātrā is compared to the call of a blue jay (चाष), 2 mātrās to the crowing of a rooster, and 3 mātrās (pluta) to the cry of a peacock.',
    audioDevanagari: 'अ आ इ ई उ ऊ विद्या',
    tip: 'Tap your finger on your desk: short vowels (a, i, u) get one quick tap (1 mātrā); long vowels with a macron (ā, ī, ū) get two smooth taps (2 mātrās).',
    relatedView: 'reader',
  },
  {
    id: 'qa-vibhaktis',
    category: 'grammar',
    question: 'What are the 7 Vibhaktis (noun cases) and what role do they play?',
    sanskritQuestion: 'सप्त-विभक्तयः काः? तासां किं कार्यम्?',
    shortAnswer: 'Vibhaktis are suffix endings attached to nouns that tell you exactly what job the word is doing in the sentence.',
    detailedAnswer: 'Because Sanskrit uses Vibhakti endings, word order does not change the meaning! \n1. Prathamā (प्रथमा - Nominative): The Subject (रामः गच्छति - Rama goes)\n2. Dvitīyā (द्वितीया - Accusative): The Object (पुस्तकं पठति - Reads a book)\n3. Tṛtīyā (तृतीया - Instrumental): With/By (कलमेन लिखति - Writes with a pen)\n4. Caturthī (चतुर्थी - Dative): For/To (ज्ञानाय पठति - Studies for knowledge)\n5. Pañcamī (पञ्चमी - Ablative): From (वृक्षात् फलं पतति - Fruit falls from tree)\n6. Ṣaṣṭhī (षष्ठी - Genitive): Of/Belonging to (रामस्य भ्राता - Rama’s brother)\n7. Saptamī (सप्तमी - Locative): In/On/At (वने वसति - Dwells in the forest)\nPlus Sambodhana (सम्बोधनम्) for calling or addressing someone (हे राम!).',
    audioDevanagari: 'प्रथमा द्वितीया तृतीया चतुर्थी पञ्चमी षष्ठी सप्तमी सम्बोधनम्',
    tip: 'Whenever you see a noun in a verse, ask "Which case ending is attached to it?" This unlocks the entire sentence meaning!',
    relatedView: 'grammar',
  },
  {
    id: 'qa-lat-lakara',
    category: 'grammar',
    question: 'How do present-tense verb endings (लट्-लकार) work?',
    sanskritQuestion: 'लट्-लकारस्य प्रत्ययाः के?',
    shortAnswer: 'For singular, dual, and plural across 3 persons: ति, तः, अन्ति / सि, थः, थ / मि, वः, मः.',
    detailedAnswer: 'Present tense (लट्) for standard Parasmaipada verbs uses a clean 3×3 grid:\n• Prathama Puruṣa (Third Person): पठति (he/she reads), पठतः (they two read), पठन्ति (they all read)\n• Madhyama Puruṣa (Second Person): पठसि (you read), पठथः (you two read), पठथ (you all read)\n• Uttama Puruṣa (First Person): पठामि (I read), पठावः (we two read), पठामः (we all read)\nNotice that Sanskrit first person is called "Uttama" (highest/inner) and third person is called "Prathama" (first to be observed)!',
    audioDevanagari: 'पठति पठतः पठन्ति पठसि पठथः पठथ पठामि पठावः पठामः',
    tip: 'Memorize the rhythm like a song: "ti-taḥ-anti, si-thaḥ-tha, mi-vaḥ-maḥ"!',
    relatedView: 'grammar',
  },
  {
    id: 'qa-sandhi',
    category: 'grammar',
    question: 'Why does Sandhi (संधिः) happen in Sanskrit?',
    sanskritQuestion: 'संस्कृत-भाषायां संधिः किमर्थं भवति?',
    shortAnswer: 'Sandhi happens because human vocal organs prefer smooth, effortless acoustic flow when two sounds meet.',
    detailedAnswer: 'Sandhi is not an arbitrary rule invented to torture students! It is the natural law of phonetic economy. When the final sound of one word meets the beginning sound of the next word, your tongue naturally merges them so you don’t have to pause awkwardly. For example: विद्या + आलयः becomes विद्यालयः (Dīrgha Sandhi). इति + आदि becomes इत्यादि (Yaṇ Sandhi).',
    audioDevanagari: 'विद्या आलयः विद्यालयः',
    tip: 'When reading shlokas, Sandhi splitting (पदच्छेद) is like undoing a zipper so you can see the individual words clearly.',
    relatedView: 'grammar',
  },
  {
    id: 'qa-kim-words',
    category: 'cbse',
    question: 'What are the essential Kim-family words for CBSE questions?',
    sanskritQuestion: 'सी.बी.एस.ई. परीक्षायाः प्रमुख-किम्-शब्दाः के?',
    shortAnswer: 'कः (who), किम् (what), कुत्र (where), कदा (when), कुतः (from where), कथम् (how), किमर्थम् (why).',
    detailedAnswer: 'In CBSE Sanskrit exams, question formation (प्रश्न-निर्माणम्) carries significant marks! Memorize these 7 interrogative keywords:\n1. कः / का / किम् = Who? / What?\n2. कुत्र = Where? (Location)\n3. कदा = When? (Time)\n4. कुतः = From where? (Source)\n5. कथम् = How? (Manner/State)\n6. किमर्थम् = Why / For what reason? (Purpose)\n7. कति = How many? (Count)',
    audioDevanagari: 'कः किम् कुत्र कदा कुतः कथम् किमर्थम् कति',
    tip: 'Look at the underlined word in the exam paper: its gender, number, and case will tell you which exact Kim-word form to substitute!',
    relatedView: 'cbse-guide',
  },
  {
    id: 'qa-chitra-varnana',
    category: 'cbse',
    question: 'How do I score full marks in Picture Description (चित्र-वर्णनम्)?',
    sanskritQuestion: 'चित्र-वर्णने सम्पूर्ण-अङ्कान् कथं लभेय?',
    shortAnswer: 'Write 5 simple, grammatically rock-solid sentences using words directly from the given Mañjūṣā (word box).',
    detailedAnswer: 'Follow Bodhi’s golden 4-step formula:\nSentence 1 (Setting the scene): "इदं चित्रं [Place in Genitive/Locative] अस्ति।" e.g. "इदं चित्रं विद्यालयस्य अस्ति।"\nSentence 2 & 3 (What is visible): "चित्रे [Subject] अस्ति / सन्ति।" e.g. "चित्रे वृक्षाः सन्ति। बालकाः क्रीडन्ति।"\nSentence 4 (Action taking place): "बालकाः प्रसन्नाः दृश्यन्ते।"\nSentence 5 (Concluding harmony): "चित्रं रमणीयम् अस्ति।"\nAvoid complicated compound words; keep subject and verb in agreement!',
    tip: 'Always double-check that your verb matches whether the subject is singular (अस्ति) or plural (सन्ति)!',
    relatedView: 'cbse-guide',
  },
  {
    id: 'qa-ekadhikena',
    category: 'vedic_math',
    question: 'How does the Vedic Math sutra "Ekādhikena Pūrveṇa" work for squaring?',
    sanskritQuestion: 'एकाधिकेन पूर्वेण सूत्रस्य प्रयोगः कथं क्रियते?',
    shortAnswer: 'Multiply the tens digit by (tens digit + 1), and append "25" at the end!',
    detailedAnswer: 'For any two-digit number ending in 5 (like 35, 65, 85):\nLet’s square 35²:\nStep 1: The tens digit is 3. One more than 3 is 4.\nStep 2: Multiply 3 × 4 = 12.\nStep 3: Square the last digit: 5² = 25.\nAnswer: 1225! Done in 2 seconds.\nAnother example: 75² → 7 × 8 = 56, append 25 → 5625. Magic, yet pure mathematical elegance!',
    audioDevanagari: 'एकाधिकेन पूर्वेण',
    tip: 'Try calculating 95² in your head right now: 9 × 10 = 90, append 25 = 9025!',
    relatedView: 'vedic-maths',
  },
  {
    id: 'qa-zero-concept',
    category: 'vedic_math',
    question: 'Why did ancient Indian mathematicians consider zero (शून्य) so profound?',
    sanskritQuestion: 'शून्यस्य संकल्पना किमर्थं महती?',
    shortAnswer: 'Śūnya is not mere nothingness; paired with pūrṇa, it names both void and fullness — emptiness and wholeness as one coin.',
    detailedAnswer: 'In Sanskrit thought, śūnya (शून्य) can point to void or emptiness and, with pūrṇa (पूर्ण, fullness or wholeness), to a meeting of emptiness and totality — not merely a bare absence of value. Absolute void and absolute completeness are two sides of the same coin; what arises from that unconditioned ground also dissolves back into it. In Bhūta-Saṅkhyā, digit zero is named by both words (Bhāskara’s chronogram uses Pūrṇa). Mathematically, Brahmagupta in the 7th century CE gave zero arithmetic rules (a + 0 = a, a × 0 = 0); with place value (दशगुण-पद्धति), that idea reshaped calculation and astronomy.',
    audioDevanagari: 'शून्यम् पूर्णम्',
    tip: 'Open the Bhūta-Saṅkhyā dictionary on the Algebra tab and tap digit 0 — or search śūnya pūrṇa.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'bhuta-shunya-purna',
  },
  {
    id: 'qa-four-levels-vak',
    category: 'philosophy',
    question: 'What are the 4 levels of speech (चतस्रो वाचः)?',
    sanskritQuestion: 'चत्वारि वाक्-रूपाणि कानि?',
    shortAnswer: 'Vaikharī (spoken sound), Madhyamā (mental thought), Paśyantī (inner vision), and Parā (pure unmanifest source).',
    detailedAnswer: 'The Ṛgveda (1.164.45) declares: "चत्वारि वाक् परिमिता पदानि तानि विदुर्ब्राह्मणा ये मनीषिणः".\n1. Vaikharī (वैखरी): Audible speech produced by vocal organs.\n2. Madhyamā (मध्यमा): The internal monologue or whispered thought inside your mind.\n3. Paśyantī (पश्यन्ती): The flash of intuitive visual understanding before words are chosen.\n4. Parā (परा): The silent, transcendent awareness that precedes all thought.\nSanskrit was designed so chanting in Vaikharī leads the mind all the way back to the stillness of Parā.',
    audioDevanagari: 'वैखरी मध्यमा पश्यन्ती परा',
    tip: 'Notice when you have a creative idea: first you "see" it whole (Paśyantī), then you formulate sentences (Madhyamā), then you speak it (Vaikharī)!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-bodhi-meaning',
    category: 'philosophy',
    question: 'What does the name "Bodhi" (बोधि) mean?',
    sanskritQuestion: 'बोधि-शब्दस्य कः अर्थः?',
    shortAnswer: 'Bodhi means awakening, enlightenment, luminous intelligence, and spiritual wisdom.',
    detailedAnswer: 'Derived from the Sanskrit root "बुध्" (budh — to perceive, to awaken, to understand). Words like Buddhi (intellect), Bodha (comprehension), and Buddha (the awakened one) all share this same radiant root. As your mascot and companion, Bodhi is here to awaken your natural love for sound, clarity, and knowledge!',
    audioDevanagari: 'बोधिः बुद्धः बुद्धिः बोधः',
    tip: 'Whenever you learn something with understanding rather than blind memorization, that moment is a spark of Bodhi!',
    relatedView: 'home',
  },
  {
    id: 'qa-guru-parampara',
    category: 'philosophy',
    question: 'Why is the Guru not just a faster model? What is the role of Guru-Paramparā in the age of AI?',
    sanskritQuestion: 'किं कृत्रिम-बुद्धेः युगे गुरु-परम्परायाः स्थाने यन्त्रं भवितुम् अर्हति?',
    shortAnswer: 'A generative model is an accumulator and processor of recorded traces, whereas a Guru is a living constraint and unbroken lineage who transmits Prāṇa (breath/life) into speech.',
    detailedAnswer: 'In the Sanskrit Gurukul tradition, the guru is not an informational bottleneck or a faster search engine. Machines process representations; humans live conditions. A model can print a mantra, parse its grammar, or recite a commentary, but it has no breath (prāṇa), no mortality, no fear, and no lineage commitment. The Guru is a living constraint whose voice is bonded to breath and attention, passing down the principle of a text\'s use (प्रयोग). As Bodhi\'s study note reminds us: AI can move information; only a living lineage can turn intention into invocation. The guru is why Sanskrit did not die when paper was printed, and why it will remain living in the age of AI.',
    audioDevanagari: 'गुरुः साक्षात् परं ब्रह्म तस्मै श्रीगुरवे नमः',
    tip: 'An app or AI is a wonderful scaffold or reference shelf, but never confuse the scaffold for the temple or the search engine for the living teacher.',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-gunitaakshara',
    category: 'pronunciation',
    question: 'What are Guṇitākṣarāṇi (गुणिताक्षराणि) and how do Mātrās work?',
    sanskritQuestion: 'गुणिताक्षराणि कानि? मात्राणां किं कार्यम्?',
    shortAnswer: 'Guṇitākṣarāṇi are combination letters formed when a pure consonant (marked with a Halanta, e.g. क्) merges with a vowel via its Mātrā sign.',
    detailedAnswer: 'In Sanskrit Devanagari, a pure consonant cannot be easily pronounced on its own and is written with a slanting line underneath called a Halanta (e.g., क्, ख्, ग्). When a vowel is added, it loses its independent shape and turns into a specific modifier symbol called a Mātrā:\n• क् + अ = क (inherent vowel, removes Halanta)\n• क् + आ = का (vertical bar ा)\n• क् + इ = कि (left hook ि)\n• क् + ई = की (right hook ी)\n• क् + उ = कु (curve below ु)\n• क् + ऊ = कू (loop below ू)\n• क् + ऋ = कृ (c-hook below ृ)\n• क् + ॠ = कॄ (double c-hook ॄ)\n• क् + ऌ = कॢ (sign below ॢ)\n• क् + ए = के (single stroke े)\n• क् + ऐ = कै (double stroke ै)\n• क् + ओ = को (bar + stroke ो)\n• क् + औ = कौ (bar + double stroke ौ)\n• क् + अं = कं (anusvāra dot ं)\n• क् + अः = कः (visarga dots ः).',
    audioDevanagari: 'क का कि की कु कू कृ के कै को कौ कं कः',
    tip: 'Remember: A pure consonant is like a silent instrument; the vowel Mātrā is the breath that makes it resonate!',
    relatedView: 'reader',
  },
  {
    id: 'qa-gunita-exceptions',
    category: 'pronunciation',
    question: "What are the special script exceptions for र (ra) with 'u/ū' and ह (ha) with 'ṛ'?",
    sanskritQuestion: 'रु, रू, हृ इत्येतेषां विशेष-नियमाः के?',
    shortAnswer: 'For र, vowels उ and ऊ attach directly to the middle-right side (रु, रू). For ह, the vowel ऋ sits inside its belly (हृ)!',
    detailedAnswer: "In standard Devanagari, vowels u, ū, and ṛ go underneath the consonant. But Sanskrit has two vital script exceptions:\n1. The Letter र (ra) with 'u' or 'ū':\n• र् + उ = रु (curved attach, as in गुरुः, पुरुषः, रुचिः)\n• र् + ऊ = रू (looped attach with a horizontal stem, as in रूपम्, रूढिः)\n2. The Letter ह (ha) with 'ṛ':\n• ह् + ऋ = हृ (the hook goes inside the stomach of the letter, pronounced hṛ as in हृदयम् - heart, and हृष्टः - delighted).",
    audioDevanagari: 'रु रू हृ हृदयम्',
    tip: 'Look closely at the word "हृदयम्" (heart): you can see the little curl tucked right into the belly of the letter ह!',
    relatedView: 'grammar',
  },
  {
    id: 'qa-numbers-gender',
    category: 'grammar',
    question: 'How do numbers 1 to 4 decline across genders in Sanskrit?',
    sanskritQuestion: '१ तः ४ संख्यानां लिङ्ग-भेदाः के?',
    shortAnswer: 'Numbers 1-4 act as adjectives and change forms for Masculine, Feminine, and Neuter. From 5 onwards, forms are identical in all genders.',
    detailedAnswer: 'In Sanskrit:\n• 1: एकः (M), एका (F), एकम् (N) — always singular (e.g. एकः बालकः, एका बालिका, एकम् फलम्)\n• 2: द्वौ (M), द्वे (F), द्वे (N) — always dual (e.g. द्वौ छात्रौ, द्वे बालिके, द्वे फले)\n• 3: त्रयः (M), तिस्रः (F), त्रीणि (N) — always plural (e.g. त्रयः बालकाः, तिस्रः बालिकाः, त्रीणि पुस्तकानि)\n• 4: चत्वारः (M), चतस्रः (F), चत्वारि (N) — always plural (e.g. चत्वारः वेदाः, चतस्रः नद्यः, चत्वारि पुष्पाणि)\nFrom 5 (पञ्च) onwards, the numeral remains identical for all genders: पञ्च बालकाः, पञ्च बालिकाः, पञ्च फलानि!',
    audioDevanagari: 'एकः एका एकम् द्वौ द्वे त्रयः तिस्रः त्रीणि चत्वारः चतस्रः चत्वारि पञ्च',
    tip: 'Notice that for 2, feminine and neuter have the exact same form: द्वे बालिके (two girls) and द्वे फले (two fruits)!',
    relatedView: 'reader',
  },
  {
    id: 'qa-numbers-large-scale',
    category: 'vedic_math',
    question: 'What are the ancient Sanskrit powers of 10 like Koṭi, Mahāpadma, and Parārdha?',
    sanskritQuestion: 'महा-संख्या-परिमाणं किम्?',
    shortAnswer: 'The Yajurveda, Rāmāyaṇa, and Līlāvatī name powers of 10 all the way to 10¹⁷ (Parārdham - One Hundred Quadrillion)!',
    detailedAnswer: 'Long before modern decimal prefixes, Indian mathematicians named every single power of 10:\n• 10⁰ = एकम् (1)\n• 10¹ = दश (10)\n• 10² = शतम् (100)\n• 10³ = सहस्रम् (1,000)\n• 10⁴ = अयुतम् (10,000)\n• 10⁵ = लक्षम् (1,00,000 / 1 Lakh)\n• 10⁶ = प्रयुतम् (10,00,000 / 1 Million)\n• 10⁷ = कोटिः (1,00,00,000 / 1 Crore)\n• 10⁸ = अर्बुदम् (100 Million)\n• 10⁹ = अब्जम् / पद्मम् (1 Billion)\n• 10¹⁰ = खर्वम् (10 Billion)\n• 10¹² = महापद्मम् (1 Trillion)\n• 10¹³ = शङ्कुः / शङ्खः (10 Trillion)\n• 10¹⁷ = परार्धम् (100 Quadrillion / Quintillion).\nThese were routinely calculated in astronomical texts like Āryabhaṭīya and Sūrya Siddhānta!',
    audioDevanagari: 'शतम् सहस्रम् लक्षम् कोटिः अर्बुदम् महापद्मम् परार्धम्',
    tip: 'When you hear "33 Koti Devata", remember that Koṭi in Sanskrit also meant supreme category/class, not just numerical crore!',
    relatedView: 'vedic-maths',
  },
  {
    id: 'qa-riddle-ai',
    category: 'engineering',
    question: '🧩 Riddle #1: What modern tech concept is engineered by "कृत्रिमन्त्रम्" (Kṛtrimantram)?',
    sanskritQuestion: 'कृत्रिमन्त्रम् इति नवीन-शब्देन कस्य आधुनिक-तन्त्रज्ञानस्य बोधः भवति?',
    shortAnswer: 'Artificial Intelligence (AI) — literally an engineered tool/mechanism (मन्त्र) for active thinking, reasoning, and computing!',
    detailedAnswer: 'Blueprint Breakdown:\n• Stage 1 (Raw Materials): Core Root √मन् (Man = to think, ponder, compute) + instrumental suffix "त्र" (tra = instrument/mechanism) + modifier "कृत्रिम" (artificial/man-made, from √कृ + त्रिमप्).\n• Stage 2 (Grammar Forge): √मन् + त्र = मन्त्र (Pāṇini 3.2.182: tool for thinking). Compounded via Tatpuruṣa Samāsa: कृत्रिम + मन्त्रम् = कृत्रिमन्त्रम्.\n• Stage 3 (Final Outcome): "कृत्रिमन्त्रम्" = An engineered instrument designed for synthetic intelligence and reasoning!\nWhy this is pure genius: While modern languages translate AI clumsily as "कृत्रिम बुद्धिमत्ता" (three words), classical Pāṇinian morphology creates a single, elegant nominal: कृत्रिमन्त्रम्!',
    audioDevanagari: 'कृत्रिमन्त्रम्',
    tip: 'In ancient times, "Mantra" meant an instrument of thought (मननात् त्रायते इति). Today, AI is quite literally a machine that calculates thoughts!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-riddle-internet',
    category: 'engineering',
    question: '🧩 Riddle #2: What modern communication network is engineered by "अन्तर्जालम्" (Antarjālam) / "विश्वजालम्" (Viśvajālam)?',
    sanskritQuestion: 'अन्तर्जालम् अथवा विश्वजालम् इति शब्देन किम् अभिधीयते?',
    shortAnswer: 'The Internet / World Wide Web (WWW) — an interconnected mesh (जाल) spanning the interior space between all computational nodes.',
    detailedAnswer: 'Blueprint Breakdown:\n• Stage 1 (Raw Materials): Core word "जालम्" (Jālam = net, lattice, matrix, web) + prefix "अन्तर्" (Antar = within, interconnected among entities; cognate to Latin "inter-") or "विश्व" (Viśva = universe/global).\n• Stage 2 (Grammar Forge): Tatpuruṣa Samāsa (अन्तर्गतं जालं यत् सर्वान् संबध्नाति = The inner web linking all). Compounding with Sandhi yields "अन्तर्जालम्" (The Internet) and "विश्वजालम्" (The World Wide Web).\n• Stage 3 (Final Outcome): Precise Sanskrit descriptive nominals that perfectly encapsulate nodes interconnected across global cyberspace.',
    audioDevanagari: 'अन्तर्जालम् विश्वजालम्',
    tip: 'Think of spiderwebs or fishing nets: "जाल" describes any interconnected node network where every thread holds tension with the rest!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-riddle-smartphone',
    category: 'engineering',
    question: '🧩 Riddle #3: What everyday pocket device is engineered by "चलदूरभाषः" (Caladūrabhāṣaḥ)?',
    sanskritQuestion: 'चलदूरभाषः इति शब्देन कस्य यन्त्रस्य निर्देशः भवति?',
    shortAnswer: 'Mobile Phone / Smartphone — literally a mobile (चल) instrument for speaking across long distances (दूरभाष).',
    detailedAnswer: 'Blueprint Breakdown:\n• Stage 1 (Raw Materials): Root √भाष् (to speak/voice) + Ghañ suffix = भाषः (instrument of speech) + prefix "दूर" (dūra = at a distance/tele-) + adjective "चल" (moving, portable, from √चल् = to move).\n• Stage 2 (Grammar Forge): दूर + भाषः = दूरभाषः (Telephone). Then Karmadhāraya Samāsa: चलः चासौ दूरभाषश्च = चलदूरभाषः (Mobile phone). If equipped with intelligence (Smartphone), it is engineered as "सचेतन-चलदूरभाषः"!\n• Stage 3 (Final Outcome): "चलदूरभाषः" (Mobile phone) and "चलदूरवाणी" (Cellular voice terminal).',
    audioDevanagari: 'चलदूरभाषः',
    tip: 'Notice how "Dūra" matches the Greek prefix "Tele-" (as in Telephone, Television, Telescope)!',
    relatedView: 'reader',
  },
  {
    id: 'qa-riddle-satellite',
    category: 'engineering',
    question: '🧩 Riddle #4: What aerospace technology is engineered by "कृत्रिमोपग्रहः" (Kṛtrimopagrahaḥ)?',
    sanskritQuestion: 'कृत्रिमोपग्रहः इति शब्देन कस्य आकाश-यन्त्रस्य ग्रहणं भवति?',
    shortAnswer: 'Artificial Space Satellite — a man-made (कृत्रिम) secondary companion body (उपग्रह) locked in orbital gravitation.',
    detailedAnswer: 'Blueprint Breakdown:\n• Stage 1 (Raw Materials): Root √ग्रह् (to seize/gravitate) + Ac suffix = ग्रहः (celestial body holding orbit) + prefix "उप" (secondary/orbiting companion, as in Upaniṣad) + adjective "कृत्रिम" (artificial).\n• Stage 2 (Grammar Forge): उप + ग्रहः = उपग्रहः (a moon or satellite orbiting a primary planet). Compounded with "कृत्रिम" via Karmadhāraya: कृत्रिम + उपग्रहः. Applying Guṇa Sandhi (a + u = o, Pāṇini 6.1.87 "आद्गुणः") morphs it into "कृत्रिमोपग्रहः".\n• Stage 3 (Final Outcome): "कृत्रिमोपग्रहः" = An artificial orbiting satellite orbiting Earth for GPS, weather, and telecommunications!',
    audioDevanagari: 'कृत्रिमोपग्रहः',
    tip: 'In Sanskrit astronomy, the Moon (चन्द्र) is a natural उपग्रहः, while Chandrayaan and GSAT are कृत्रिमोपग्रहाः!',
    relatedView: 'vedic-maths',
  },
  {
    id: 'qa-riddle-blockchain',
    category: 'engineering',
    question: '🧩 Riddle #5: What decentralized fintech architecture is engineered by "खण्डशृङ्खला" (Khaṇḍaśṛṅkhalā)?',
    sanskritQuestion: 'खण्डशृङ्खला इति पदं कस्याः आधुनिक-तन्त्र-व्यवस्थायाः वाचकम्?',
    shortAnswer: 'Blockchain — an immutable, cryptographically verified chain (शृङ्खला) of discrete data blocks (खण्ड).',
    detailedAnswer: 'Blueprint Breakdown:\n• Stage 1 (Raw Materials): Root √खण्ड् (to divide, partition) = खण्डः (a discrete block or ledger record) + nominal base "शृङ्खला" (an interlocking unbroken chain or series).\n• Stage 2 (Grammar Forge): Ṣaṣṭhī Tatpuruṣa Samāsa: खण्डानां शृङ्खला = खण्डशृङ्खला (A linked chain of blocks). When encrypted cryptographically with root √गुप् (to protect/hash), it is termed "गोपिता खण्डशृङ्खला" (Cryptographic Blockchain)!\n• Stage 3 (Final Outcome): "खण्डशृङ्खला" captures the distributed ledger architecture with 100% mathematical precision without borrowing a single foreign word.',
    audioDevanagari: 'खण्डशृङ्खला',
    tip: 'Sanskrit compounds (Samāsa) are naturally modular — just like software object-oriented design and cryptography blocks!',
    relatedView: 'vedic-maths',
  },
  {
    id: 'qa-ujjain-prime-meridian',
    category: 'vedic_math',
    question: 'Why was Ujjain considered the Prime Meridian (0° Longitude) of the ancient world?',
    sanskritQuestion: 'उज्जयिनी प्राचीने भारते मध्यरेखा (शून्य-देशान्तरम्) कथम् आसीत्?',
    shortAnswer: 'Centuries before Greenwich (1884), ancient Indian astronomers established Ujjain (Avantī) as the 0° Prime Meridian (मध्यरेखा), where zero longitude intersected the Tropic of Cancer!',
    detailedAnswer: 'In classical Indian astronomy (Sūrya Siddhānta, Āryabhaṭīya, Pañcasiddhāntikā), Ujjain (historically Avantī, 23° 10\' N, 75° 46\' E) served as the central reference meridian (Madhya-Rekhā / 0° Longitude) for the entire subcontinent. Ujjain was selected because it sat at the exact sacred intersection of the Prime Meridian and the Tropic of Cancer. All planetary positions (Graha-spaṣṭīkaraṇa), sunrise timings, calendar transitions (Tithis), and eclipse ephemerides were calculated relative to Ujjain local mean time.',
    audioDevanagari: 'उज्जयिनी मध्यरेखा अवन्ती सूर्यसिद्धान्तः',
    tip: 'Ujjain was to the ancient astronomical world what Greenwich became to modern shipping in 1884!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-ujjain-gps-temple',
    category: 'philosophy',
    question: 'What is the "GPS Temple" of Ujjain and how does ancient sacred geography map space and time?',
    sanskritQuestion: 'उज्जयिन्यां महाकालेश्वर-मङ्गलानाथ-मन्दिरयोः खगोल-महत्त्वं किम्?',
    shortAnswer: 'The Mahākāleśvara Jyotirlinga (Shiva as Lord of Time) and Maṅgalanātha Temple marked the exact geodetic coordinate zero of ancient India.',
    detailedAnswer: 'In Sanskrit science, temples often functioned as national geodetic benchmarks. Mahākāleśvara Temple is dedicated to Shiva as "Mahākāla" — the cosmic ruler of Kāla (both Time and Entropy), embodying the zero-meridian cosmic clock. Just north along the sacred Kshipra River sits the Maṅgalanātha Temple, revered in tradition as the birthplace of Mars (Bhauma-Janmasthāna). Ancient astronomers used its unobstructed horizon to track the 687-day synodic orbits and retrograde loops (Vakra-gati) of Mars, anchoring ancient India\'s coordinate map.',
    audioDevanagari: 'महाकालेश्वरः मङ्गलानाथः कालः देशान्तरम्',
    tip: 'At the equinox, the noon sun aligned directly with the central Shivalinga of Mahakaleshwar!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-ujjain-latitude-shanku',
    category: 'vedic_math',
    question: 'How did ancient Indian mathematicians calculate Latitude (Akṣāṁśa) using the 12-Aṅgula Śaṅku?',
    sanskritQuestion: 'द्वादशाङ्गुल-शङ्कुना अक्षांश-ज्ञानं कथं क्रियते?',
    shortAnswer: 'By measuring the noon shadow (Palabhā) of a vertical 12-unit gnomon (Śaṅku) on the equinox day: Latitude φ = arctan(Palabhā / 12).',
    detailedAnswer: 'Without modern satellite GPS or chronometers, ancient astronomers set up a standardized vertical rod of exactly 12 Aṅgulas (finger-widths) called a Śaṅku (शङ्कुः / gnomon) on a leveled stone base. On the equinox (Viṣuvan-dina), when the sun crosses the celestial equator, they measured the shadow at exact solar noon. This equinoctial noon shadow is called the Palabhā (पलभा). At Ujjain (23° 10\' N), the Palabhā measures exactly 5.14 Aṅgulas. Using right-triangle geometry: tan(Latitude) = Palabhā / 12! On the summer solstice in antiquity, because Ujjain sat directly on the Tropic of Cancer, the noon shadow shrank to zero (Zero Shadow Day)!',
    audioDevanagari: 'द्वादशाङ्गुल-शङ्कुः पलभा अक्षांशः विषुवत्',
    tip: 'Remember the formula: 12 Aṅgula Gnomon + Equinoctial Noon Shadow (Palabhā) = Exact Local Latitude anywhere on Earth!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-ujjain-longitude-eclipse',
    category: 'vedic_math',
    question: 'How did ancient Indian astronomers calculate Longitude (Deśāntara) using Lunar Eclipses?',
    sanskritQuestion: 'चन्द्रग्रहणेन देशान्तर-ज्ञानं (Longitude) कथं साधितम्?',
    shortAnswer: 'Ancient astronomers used lunar eclipses as a cosmic broadcast clock! The local time difference between Ujjain and another city gave the longitude delta.',
    detailedAnswer: 'Because the Earth rotates eastward 360° every 24 hours (1° every 4 minutes), difference in longitude translates directly to difference in local solar time. But how could two distant observatories synchronize time without telegraphs or radios? They used Lunar Eclipses (चन्द्रग्रहणम्)! Unlike solar eclipses which depend on parallax, a lunar eclipse occurs simultaneously for everyone on Earth who can see the moon. Astronomers in Ujjain and a distant station (e.g., Pāṭaliputra or Vārāṇasī) noted their exact local water-clock (Ghaṭī-yantra) time at the first touch of the shadow. Time delta Δt × (360° / 24 hrs) yielded the exact terrestrial Longitude (Deśāntaram)!',
    audioDevanagari: 'देशान्तरम् चन्द्रग्रहणम् घटीयन्त्रम् मध्यरेखा',
    tip: 'The lunar eclipse was antiquity\'s GPS synchronization pulse broadcasted across thousands of kilometers!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-ujjain-trigonometry-jya',
    category: 'vedic_math',
    question: 'How did the Ujjain mathematical school give birth to modern Trigonometry (Sine / Jyā)?',
    sanskritQuestion: 'उज्जयिनी-गणित-परम्परया त्रिकोणमितौ \'ज्या\' (Sine) कथम् आविष्कृता?',
    shortAnswer: 'Ujjain mathematicians halved Greek full-chords to create the half-chord (Ardha-jyā / Jyā), which passed through Arabic Jība into Latin Sinus — modern Sine!',
    detailedAnswer: 'Greek astronomy (Ptolemy / Hipparchus) used chords spanning full double-arcs (crd 2θ), which made spherical trigonometry clumsy. The astronomers of Ujjain (including Varāhamihira and Brahmagupta) cut the chord in half to create an embedded right triangle: Ardha-jyā (half-chord), later shortened to Jyā (or Jīvā). In the 8th century, Brahmagupta\'s works were translated into Arabic in Baghdad as Zīj al-Sindhind, where Jyā was phonetically written as Jība (jb). When Gerard of Cremona translated it into Latin in 12th-century Spain, he read jb as "Jaib" (fold/pocket/bay) and translated it into Latin as Sinus, which became our modern Sine function!',
    audioDevanagari: 'अर्धज्या ज्या जीवा भुजज्या कोटिज्या',
    tip: 'Every time you press "sin" on a scientific calculator or write code using Math.sin(), you are using Ujjain\'s Ardha-Jyā!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-ujjain-dongla-precession',
    category: 'philosophy',
    question: 'Why did the Tropic of Cancer drift from Ujjain to Doṅglā Village?',
    sanskritQuestion: 'कर्कटक्रान्तिवृत्तम् उज्जयिनीतः दोङ्गला-ग्रामं प्रति कथं संक्रान्तम्?',
    shortAnswer: 'Due to Earth\'s 41,000-year axial obliquity cycle, the Tropic of Cancer drifts southward at ~14.5 meters per year, shifting from Ujjain to Doṅglā Village today.',
    detailedAnswer: 'Earth\'s axial tilt (obliquity) fluctuates between 22.1° and 24.5° over an approximate 41,000-year cycle (Milankovitch cycles). In antiquity (~300 BCE), the Tropic of Cancer passed directly through the center of Ujjain (23° 10\' N) across the Mahākāleśvara Temple. Over the centuries, this line has drifted southwards at about 14.5 meters per year. Today, the Tropic of Cancer (23° 26\' 11" N) cleanly cuts through Doṅglā Village, 30 km north of Ujjain, where India\'s modern Varāhamihira Astronomical Observatory stands today!',
    audioDevanagari: 'दोङ्गला कर्कटक्रान्तिवृत्तम् अयनांशः उज्जयिनी',
    tip: 'Visit Doṅglā on June 21st (Summer Solstice) to experience the true Zero Shadow Day where shadows vanish at local solar noon!',
    relatedView: 'philosophy',
  },
  {
    id: 'qa-even-prime-baudhayana',
    category: 'vedic_math',
    question: 'Where can I read about the even prime, Baudhāyana, and the Śulba Sūtras?',
    sanskritQuestion: 'सम-अभाज्य-संख्या बौधायन-शुल्बसूत्रं च कुत्र पठनीयम्?',
    shortAnswer: 'Open “The Ancient Roots of the Even Prime” on the Vedic Maths page.',
    detailedAnswer: 'That article shows why 2 is the only even prime, how the Taittirīya Saṃhitā chants even (Yugma) and odd (Ayugma), and how Baudhāyana’s Śulba Sūtra stated the diagonal theorem and the sacred triples long before Pythagoras.',
    tip: 'Search the Gurukul for even prime, baudhayana, or sulba.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-baudhayana-even-prime-geometry',
  },
  {
    id: 'qa-birth-of-the-grid',
    category: 'vedic_math',
    question: 'Where is the article on the birth of the grid, al-Khwarizmi, and Fibonacci?',
    sanskritQuestion: 'ग्रिड-जन्म अल-ख्वारिज्मी फिबोनाच्चि-कथा च कुत्र?',
    shortAnswer: 'Open “The Birth of the Grid” on the Vedic Maths page.',
    detailedAnswer: 'It follows the decimal place-value grid and śūnya from India through al-Khwarizmi’s Arabic treatises — the source of the word algorithm — to Fibonacci’s Liber Abaci, which carried the system into Europe.',
    tip: 'Try searching grid, khwarizmi, or fibonacci.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-birth-of-the-grid',
  },
  {
    id: 'qa-shaka-chronology',
    category: 'vedic_math',
    question: 'Where can I read the historiographical framework and Shaka (Saka) chronology?',
    sanskritQuestion: 'इतिहासदर्शनं शक-कालक्रमश्च कुत्र वर्णितौ?',
    shortAnswer: 'Open “Historiographical Framework & Shaka Chronology” on the Vedic Maths page.',
    detailedAnswer: 'The article sets out the pillars of Indian mathematical history — oral paramparā, palm-leaf decay, and the dating floor — and the Shaka-to-Common-Era conversion, CE = Shaka + 78. Shaka is also spelled Saka.',
    tip: 'Search shaka, saka, or chronology.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-historiographical-framework-and-shaka-chronology',
  },
  {
    id: 'qa-universal-engine-algebra',
    category: 'vedic_math',
    question: 'Where is the Universal Engine of Algebra article?',
    sanskritQuestion: 'बीजगणितस्य सार्वभौम-यन्त्रं कुत्र पठनीयम्?',
    shortAnswer: 'Open “The Universal Engine of Algebra” on the Vedic Maths page.',
    detailedAnswer: 'It treats algebra as generalized arithmetic: base 10 for numbers and base x for polynomials, so 12 × 13 and (x + 2)(x + 3) share one coefficient pattern.',
    tip: 'Search algebra or universal engine.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-universal-engine-of-algebra',
  },
  {
    id: 'qa-magic-of-numbers',
    category: 'vedic_math',
    question: 'Where can I read the introduction to Vedic Mathematics — the Magic of Numbers?',
    sanskritQuestion: 'सङ्ख्यानां विस्मयः वैदिक-गणित-परिचयश्च कुत्र पठनीयः?',
    shortAnswer: 'Open “The Magic of Numbers” on the Vedic Maths page.',
    detailedAnswer: 'That article introduces Vedic Mathematics as mental calculation compiled by Swami Bharati Krishna Tirtha: sixteen sūtras and thirteen sub-sūtras, faster than column arithmetic, with a digital-root (bījāṅka) check.',
    tip: 'Search magic of numbers or vedic maths introduction.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-magic-of-numbers',
  },
  {
    id: 'qa-fluid-space',
    category: 'vedic_math',
    question: 'Where is the article on place value as fluid space and Ūrdhva-Tiryagbhyām?',
    sanskritQuestion: 'स्थानमानं द्रव-आकाशवत् ऊर्ध्वतिर्यग्भ्यां च कुत्र वर्णितम्?',
    shortAnswer: 'Open “The Vedic Approach: Treating Place Value as Fluid Space” on the Vedic Maths page.',
    detailedAnswer: 'It contrasts rigid school columns with continuous place-value space, and shows how Ūrdhva-Tiryagbhyām multiplies by working all positions at once.',
    tip: 'Search fluid space or urdhva tiryagbhyam.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-fluid-space-simultaneous-processing',
  },
  {
    id: 'qa-tirtha-lineage',
    category: 'vedic_math',
    question: 'Where can I read about Swami Bharati Krishna Tirtha and the Vedic Maths lineage?',
    sanskritQuestion: 'स्वामि-भारती-कृष्ण-तीर्थस्य गुरु-परम्परा च कुत्र?',
    shortAnswer: 'Open “The Source & The Living Lineage” on the Vedic Maths page.',
    detailedAnswer: 'The article follows Swami Bharati Krishna Tirtha (1884–1960), who reconstructed the sixteen sūtras, and the teacher-student line that still teaches them.',
    tip: 'Search tirtha, bharati krishna, or living lineage.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-source-and-guru-parampara',
  },
  {
    id: 'qa-geometry-of-the-infinite',
    category: 'vedic_math',
    question: 'Where is the Geometry of the Infinite — maths and the sacred together?',
    sanskritQuestion: 'अनन्तस्य ज्यामितिः लौकिक-पारमार्थिक-मैत्री च कुत्र?',
    shortAnswer: 'Open “The Geometry of the Infinite” on the Vedic Maths page.',
    detailedAnswer: 'It takes down the wall between classroom mathematics and sacred metaphysics, and follows number and geometric symmetry from forest hermitages to modern calculation.',
    tip: 'Search geometry of the infinite or secular and sacred.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-geometry-of-the-infinite',
  },
  {
    id: 'qa-vakyapadiya-ai',
    category: 'philosophy',
    question: 'Where can I read Bhartṛhari’s Vākyapadīya and its link to modern AI?',
    sanskritQuestion: 'भर्तृहरेः वाक्यपदीयं कृत्रिमप्रज्ञा-सम्बन्धश्च कुत्र?',
    shortAnswer: 'Open “Vākyapadīya and Modern AI” on the Vedic Maths page.',
    detailedAnswer: 'The article connects Bhartṛhari’s sentence-holism (sphoṭa) and kāraka logic with Rick Briggs’ 1985 NASA paper on Sanskrit as an unambiguous knowledge language.',
    tip: 'Search vakyapadiya, bhartrhari, sphota, or rick briggs.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-vakyapadiya-and-ai',
  },
  {
    id: 'qa-indian-calendar-366',
    category: 'vedic_math',
    question: 'Where is the article on the Indian calendar and the 366-day Vedic year?',
    sanskritQuestion: 'भारतीय-पञ्चाङ्गं षट्षष्ट्यधिक-त्रिशत-दिन-वर्षं च कुत्र?',
    shortAnswer: 'Open “The Indian Calendar & 366-Day Vedic Year” on the grammar shelf.',
    detailedAnswer: 'That grammar article explains why the Vedic solar year and Adhika Māsa were already self-correcting, including Lagadha’s 366-day year, so the calendar did not need a Gregorian patch.',
    tip: 'Search 366-day, lagadha, or indian calendar.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'indian-calendar-precision',
  },
  {
    id: 'qa-jambudvipa',
    category: 'grammar',
    question: 'Why is India called Jambudvīpa? What are the seven dvīpas?',
    sanskritQuestion: 'भारतं जम्बूद्वीपम् इति किमर्थम्?',
    shortAnswer: 'Open “Jambudvīpa & the Seven Island-Continents” on the articles shelf.',
    detailedAnswer: 'Jambudvīpa — island of the jambū (jamun) tree — is the central ring of the Purāṇic Bhū-maṇḍala; Bhāratavarṣa lies in its south. The article retells Priyavrata’s chariot, lists the seven dvīpas and oceans (Bhāgavata 5), Buddhist and Jain views, Aśoka’s edict, and the saṅkalpa “jambūdvīpe bhāratavarṣe”.',
    tip: 'Search jambudvipa, sapta dvipa, or sankalpa.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'jambudvipa-sapta-dvipa',
  },
  {
    id: 'qa-sanskrit-europe',
    category: 'grammar',
    question: 'How did Sanskrit grammar reach Europe? Who were William Jones, Bopp and Max Müller?',
    sanskritQuestion: 'संस्कृत-व्याकरणं यूरोपं कथं गतम्?',
    shortAnswer: 'Open “How Sanskrit Grammar Travelled to Europe” on the articles shelf — with portraits of each scholar.',
    detailedAnswer: 'Jesuit missionaries Heinrich Roth and Johann Ernst Hanxleden wrote the first European grammars of Sanskrit; Paolino da San Bartolomeo printed the first one in Rome (1790). Sir William Jones’s 1786 Calcutta discourse linked Sanskrit with Greek and Latin, Charles Wilkins published his grammar in 1808, and Schlegel, Bopp and Max Müller made Sanskrit central to European linguistics — inspired by Pāṇini’s Aṣṭādhyāyī. The article also covers Proto-Indo-European (PIE) as a reconstruction rather than another name for Sanskrit, and Macaulay and curricular displacement. (Keywords: Sanskrit Europe, grammar travelled to Europe, Panini Europe, PIE Sanskrit, Jones Wilkins Bopp, Macaulay Sanskrit.)',
    tip: 'Search sanskrit europe, panini europe, pie sanskrit, jones wilkins bopp, or macaulay sanskrit.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'sanskrit-grammar-europe',
  },
  {
    id: 'qa-panchatantra',
    category: 'grammar',
    question: 'What is the Pañcatantra and why is it famous around the world?',
    sanskritQuestion: 'पञ्चतन्त्रं किम्?',
    shortAnswer: 'Open “Pañcatantra — Wisdom that Travelled the World” on the articles shelf.',
    detailedAnswer: 'The Pañcatantra is a Sanskrit collection of five books teaching nīti (worldly wisdom) through animal frame-stories, traditionally told by Viṣṇuśarman to princes. Translated into Middle Persian and then Arabic as Kalīla wa Dimna, it travelled into Hebrew, Greek, Latin and European languages — one of India’s most translated works. Our Jungle Council story follows its tradition.',
    tip: 'Search panchatantra, kalila wa dimna, or jungle council.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'panchatantra-niti-world',
  },
  {
    id: 'qa-sapta-varna',
    category: 'grammar',
    question: 'Why does the Sun have seven horses? Did ancient Indians know about colours of light?',
    sanskritQuestion: 'सूर्यस्य सप्त अश्वाः किमर्थम्?',
    shortAnswer: 'Open “Sapta-varṇa: Seven Horses of the Sun” on the articles shelf.',
    detailedAnswer: 'Ṛgveda 1.164 sings of one horse with seven names pulling the Sun’s chariot — today often read as one white light holding seven rainbow colours. The article adds a careful Chāndogya reading (red, white, dark glows), Ajanta pigments, Indian dyes, and a parent bedtime read-aloud.',
    tip: 'Search sapta varna, seven horses, or rainbow.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'sapta-varna-seven-horses',
  },
  {
    id: 'qa-sanskrit-in-south-indian-languages',
    category: 'grammar',
    question: 'How do Sanskrit words appear in Telugu, Kannada, Malayalam and Tamil?',
    sanskritQuestion: 'दक्षिणभाषासु संस्कृतशब्दाः कथम्?',
    shortAnswer: 'Open “Sanskrit Words Across Telugu, Kannada, Malayalam & Tamil” on the grammar shelf.',
    detailedAnswer: 'A side-by-side table of eleven words — Vidyā, Akṣara, Sūrya, Candra, Ākāśa, Prāṇa, Hṛdaya, Kavi, Bhakti, Kāla and Sneha — showing each form in Telugu, Kannada, Malayalam and Tamil script with Latin transliteration.',
    tip: 'Search vidya, akshara, or cognates.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'sanskrit-in-south-indian-languages',
  },
  {
    id: 'qa-many-names-of-sanskrit',
    category: 'grammar',
    question: 'Why is Sanskrit also called Samskritam, Samskrutam or Samskruthamu?',
    sanskritQuestion: 'संस्कृतस्य कति नामानि?',
    shortAnswer: 'Open “The Many Names of the Perfected Language” on the grammar shelf.',
    detailedAnswer: 'The article traces Saṃskṛta (“well-formed”), the neuter -am suffix of Samskritam, the ṛ vowel spoken as “ri” in the North & East and “ru” in the South & West, Telugu’s -mu in Samskruthamu, and titles like Devavāṇi and Gīrvāṇabhāratī.',
    tip: 'Search samskritam, samskruthamu, or devavani.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'many-names-of-sanskrit',
  },
  {
    id: 'qa-legacy-metrology',
    category: 'vedic_math',
    question: 'Where can I read about Indian metrology, Muziris bullion, and Harappan weights?',
    sanskritQuestion: 'तुलामानं धातुशुद्धिः हडप्पा-मानं च कुत्र?',
    shortAnswer: 'Open “The Legacy of Indian Metrology” on the grammar shelf.',
    detailedAnswer: 'The article covers Harappan 4:2:1 bricks and binary weights, Roman coins treated as bullion at Muziris, and Kauṭilya’s rules for checking market scales.',
    tip: 'Search muziris, bullion, harappan bricks, or metrology.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'legacy-of-indian-metrology',
  },
  {
    id: 'qa-animal-names-yoga-sounds',
    category: 'vedic_math',
    question: 'Where can I read about yoga poses named for animals, and singing notes linked to animals?',
    sanskritQuestion: 'पशु-नामकानि आसनानि गान-स्वराश्च कुत्र पठनीयाः?',
    shortAnswer: 'Open “Animal Names: Yoga Shapes and Singing Notes” on the Vedic Maths page.',
    detailedAnswer: 'That page is for young readers. It says which animal poses a book actually describes (lion, cobra, peacock, and others), and which classroom names are newer (cat, cow, downward-facing dog). It also gives the seven singing notes the Nāradīya Śikṣā says animals call. It does not say alphabet letters come from animals.',
    tip: 'Search cat pose, cobra, naradiya, or singing notes.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-animal-names-yoga-sounds',
  },
  {
    id: 'qa-roots-of-algebra',
    category: 'vedic_math',
    question: 'Where can I read The Roots of Algebra?',
    sanskritQuestion: 'बीजगणितस्य मूलानि कुत्र पठनीयानि?',
    shortAnswer: 'Open “The Roots of Algebra” on the Vedic Maths page.',
    detailedAnswer: 'That article follows the variable from altar geometry into place value, śūnya, debts and fortunes (ṛṇa and dhana), and the balanced equation (samīkaraṇa).',
    tip: 'Search roots of algebra or bija.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-roots-of-algebra',
  },
  {
    id: 'qa-before-the-carat',
    category: 'vedic_math',
    question: 'Where is the article on seeds, slate, and weight before the carat?',
    sanskritQuestion: 'रत्न-मानं तुलामानं च कुत्र वर्णितम्?',
    shortAnswer: 'Open “Before the Carat — Seeds, Slate, and the Metrology of Ancient India” on the Vedic Maths page.',
    detailedAnswer: 'It sets seed-weights, gem colors, the touchstone, and market scales next to one another, using the measures the article itself names.',
    tip: 'Search carat, ratti, or touchstone.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-before-the-carat',
  },
  {
    id: 'qa-ganita-calendar',
    category: 'vedic_math',
    question: 'Where can I read Gaṇita and the calendar?',
    sanskritQuestion: 'गणितं पञ्चाङ्गं च कुत्र पठनीयम्?',
    shortAnswer: 'Open “Gaṇita and the Calendar — The Precision of Indian Astronomy & The 366-Day Vedic Year” on the Vedic Maths page.',
    detailedAnswer: 'The page walks through the 366-day solar year, the five limbs of a pañcāṅga, and Adhika Māsa as that article states them.',
    tip: 'Search ganita, panchanga, or 366-day.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-ganita-and-the-calendar',
  },
  {
    id: 'qa-naming-the-colossal-vedic',
    category: 'vedic_math',
    question: 'Where is the Vedic Maths article on naming huge numbers?',
    sanskritQuestion: 'महासङ्ख्यानां नामानि कुत्र?',
    shortAnswer: 'Open “Naming the Colossal — How Indian Texts Counted Past a Million” on the Vedic Maths page.',
    detailedAnswer: 'It lines up the named powers the texts give, from the Vedic decuple rungs through the Rāmāyaṇa census, the Lalitavistara, the Līlāvatī, and the Jaina names for the uncountable.',
    tip: 'Search parardha, ayuta, or colossal.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-naming-the-colossal',
  },
  {
    id: 'qa-dhanurveda',
    category: 'vedic_math',
    question: 'Where can I read about Dhanurveda, the bow, and the five stances?',
    sanskritQuestion: 'धनुर्वेदः पञ्च-स्थानानि च कुत्र?',
    shortAnswer: 'Open “Dhanurveda: The Sacred Science of the Bow — Phonetics, Ballistics & The 5 Geometric Stances” on the Vedic Maths page.',
    detailedAnswer: 'The article pairs the mouth-as-bow with the five stances it names: Ālīḍha, Pratyālīḍha, Samapada, Vaiśākha, and Maṇḍala, each with the shape given there.',
    tip: 'Search dhanurveda, alidha, or mandala stance.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-dhanurveda-geometry-phonetics',
  },
  {
    id: 'qa-kerala-school',
    category: 'vedic_math',
    question: 'Where is the Kerala School article on Madhava and the Yuktibhāṣā?',
    sanskritQuestion: 'केरल-गणितं माधवः युक्तिभाषा च कुत्र?',
    shortAnswer: 'Open “The Kerala School of Mathematics: Madhava, Yuktibhāṣā & The Invention of Calculus” on the Vedic Maths page.',
    detailedAnswer: 'It sets out Madhava’s series for pi, sine, and cosine, the Yuktibhāṣā octant, the correction terms, and Nīlakaṇṭha’s planetary model, as that page describes them.',
    tip: 'Search madhava, yuktibhasa, or kerala school.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-kerala-school-calculus-and-infinite-series',
  },
  {
    id: 'qa-bhaskara-dob',
    category: 'vedic_math',
    question: 'Where can I read Bhāskarācārya’s date cipher and the two algebras?',
    sanskritQuestion: 'भास्कराचार्यस्य जन्मवर्षं व्यक्त-अव्यक्तगणितं च कुत्र?',
    shortAnswer: 'Open “Bhāskarācārya: Cryptographic D.O.B., The Two Algebras & Cosmic Consciousness” on the Vedic Maths page.',
    detailedAnswer: 'The page reads Rasa-Guṇa-Pūrṇa-Mahī from the right as Shaka 1036, adds 78 to reach 1114 CE, and sets Vyakta beside Avyakta with the color names Kālaka, Nīlaka, Pītaka, and Haritaka. For zero, it also notes that śūnya (void) and pūrṇa (fullness) name the same digit.',
    tip: 'Search bhaskara, bhuta sankhya, śūnya pūrṇa, or 1114.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-bhaskaracharya-dob-algebra-and-cosmic-consciousness',
  },
  {
    id: 'qa-cosmic-bridge',
    category: 'vedic_math',
    question: 'Where is the Cosmic Bridge article?',
    sanskritQuestion: 'विश्व-मानव-संस्कृत-सेतुः कुत्र?',
    shortAnswer: 'Open “The Cosmic Bridge: Mathematics (Universe) ⇄ Humans (Sanskrit)” on the Vedic Maths page.',
    detailedAnswer: 'It draws the triad of universe, human, and Sanskrit, then the five vocal places from Kaṇṭhya to Oṣṭhya with the elements the page names.',
    tip: 'Search cosmic bridge, shiksha, or pingala.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-the-cosmic-bridge-mathematics-universe-humans-sanskrit',
  },
  {
    id: 'qa-evolution-of-sound',
    category: 'grammar',
    question: 'Where can I read The Evolution of Sound?',
    sanskritQuestion: 'ध्वनेः विकासः कुत्र पठनीयः?',
    shortAnswer: 'Open “The Evolution of Sound” on the grammar shelf.',
    detailedAnswer: 'That grammar article follows Sanskrit sound from mantra recitation into the analytical study of speech.',
    tip: 'Search evolution of sound.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'evolution-of-sound',
  },
  {
    id: 'qa-secret-code-accents',
    category: 'pronunciation',
    question: 'Where is the article on the secret code of accents?',
    sanskritQuestion: 'स्वर-चिह्नानां गुप्त-सङ्केतः कुत्र?',
    shortAnswer: 'Open “How a 2,500-Year-Old Sound Game Built the Secret Code of All World Accents” on the grammar shelf.',
    detailedAnswer: 'The page connects the old mouth-map of Sanskrit sounds with the accent marks used in the IPA.',
    tip: 'Search IPA or accents.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'ipa-secret-code',
  },
  {
    id: 'qa-mouth-gym',
    category: 'grammar',
    question: 'Where can I read The Mouth Gym?',
    sanskritQuestion: 'मुख-व्यायामः शिवसूत्राणि च कुत्र?',
    shortAnswer: 'Open “The Mouth Gym: How Ancient Sound Science Became a Phonetic Programming Language” on the grammar shelf.',
    detailedAnswer: 'It shows Pāṇini’s Śiva Sūtras, pratyāhāras, and sandhi as a way of grouping sounds.',
    tip: 'Search shiva sutras or pratyahara.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'mouth-gym-shiva-sutras',
  },
  {
    id: 'qa-karakas-framework',
    category: 'grammar',
    question: 'Where is the article on kārakas and vibhaktis?',
    sanskritQuestion: 'कारकाणि विभक्तयश्च कुत्र वर्णितानि?',
    shortAnswer: 'Open “The Core Framework: Kārakas and Vibhaktis” on the grammar shelf.',
    detailedAnswer: 'The article explains how the eight noun cases keep a sentence clear even when the words change order.',
    tip: 'Search karaka or vibhakti framework.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'karakas-and-vibhaktis',
  },
  {
    id: 'qa-dna-dhatus',
    category: 'grammar',
    question: 'Where can I read The DNA of Sanskrit?',
    sanskritQuestion: 'धातु-विज्ञानं कुत्र पठनीयम्?',
    shortAnswer: 'Open “The DNA of Sanskrit: Understanding Dhātus (Verbal Roots)” on the grammar shelf.',
    detailedAnswer: 'It covers verbal roots, the ten gaṇas, and how prefixes change a root’s meaning.',
    tip: 'Search dhatu or gana.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'dna-of-sanskrit-dhatus',
  },
  {
    id: 'qa-sandhi-article',
    category: 'grammar',
    question: 'Where is the Sandhi article on how sounds join?',
    sanskritQuestion: 'सन्धिः कुत्र व्याख्यातः?',
    shortAnswer: 'Open “सन्धि · Sandhi: How Sounds Join” on the grammar shelf.',
    detailedAnswer: 'The page walks through vowel sandhi: what happens when one sound meets the next.',
    tip: 'Search sandhi or svara sandhi.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'sandhi-how-sounds-join',
  },
  {
    id: 'qa-upsarg-pratyaya',
    category: 'grammar',
    question: 'Where can I read about upsarg, pratyaya, and the 21 stickers?',
    sanskritQuestion: 'उपसर्गः प्रत्ययः सुप्-प्रत्ययाश्च कुत्र?',
    shortAnswer: 'Open “The Ultimate Secret Code of Words: Upsarg, Pratyaya, and the Magic 21 Stickers” on the grammar shelf.',
    detailedAnswer: 'It treats prefixes, suffixes, and the 21 sup endings as pieces that build a word.',
    tip: 'Search upsarg, pratyaya, or sup.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'secret-code-upsarg-pratyaya',
  },
  {
    id: 'qa-sanskrit-symbols',
    category: 'grammar',
    question: 'Where is the article on Sanskrit symbols and punctuation?',
    sanskritQuestion: 'चिह्न-परिचयः कुत्र?',
    shortAnswer: 'Open “चिह्न-परिचयः · The Sacred Symbols of Sanskrit” on the grammar shelf.',
    detailedAnswer: 'The page names mātrās, anusvāra, visarga, halanta, daṇḍa, double daṇḍa, and avagraha.',
    tip: 'Search danda, visarga, or avagraha.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'sanskrit-symbols-punctuation',
  },
  {
    id: 'qa-linga-vachana-article',
    category: 'grammar',
    question: 'Where can I read about gender and number in Sanskrit?',
    sanskritQuestion: 'लिङ्गं वचनं च कुत्र पठनीयम्?',
    shortAnswer: 'Open “The Architecture of Gender & Number: Linga, Vachana, and Concord” on the grammar shelf.',
    detailedAnswer: 'It sets out the three genders, the three numbers, and how a verb agrees with its subject.',
    tip: 'Search linga or vachana.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'linga-vachana-foundations',
  },
  {
    id: 'qa-vibhakti-balaka-article',
    category: 'grammar',
    question: 'Where is the lesson on vibhaktis with Bālaka?',
    sanskritQuestion: 'बालक-शब्देन विभक्ति-पाठः कुत्र?',
    shortAnswer: 'Open “Sanskrit Lesson: Understanding Vibhaktis (Case Endings)” on the grammar shelf.',
    detailedAnswer: 'The lesson drills the eight cases with the word bālaka, including the question each case answers.',
    tip: 'Search balaka or case endings.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'understanding-vibhaktis-balaka',
  },
  {
    id: 'qa-katapayadi-article',
    category: 'vedic_math',
    question: 'Where can I read the Kaṭapayādi article?',
    sanskritQuestion: 'कटपयादि-सङ्केतः कुत्र?',
    shortAnswer: 'Open “कटपयादि · Kaṭapayādi: The Sacred Cipher of Sanskrit Mathematics” on the grammar shelf.',
    detailedAnswer: 'The article shows the consonant-to-digit map, including how verses store π, the golden ratio, and the 72 melakarta rāgas.',
    tip: 'Search katapayadi or melakarta.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'katapayadi-number-words',
  },
  {
    id: 'qa-sanskrit-in-english',
    category: 'grammar',
    question: 'Where is the article on Sanskrit words in English?',
    sanskritQuestion: 'आङ्ग्लभाषायां संस्कृत-पदानि कुत्र?',
    shortAnswer: 'Open “संस्कृतम् in English · Sanskrit\'s Quiet Imprint on English” on the grammar shelf.',
    detailedAnswer: 'It lists everyday English words with Sanskrit roots, and it marks which origins are still debated.',
    tip: 'Search sugar, jungle, or shampoo.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'sanskrit-in-english',
  },
  {
    id: 'qa-beginners-roadmap',
    category: 'grammar',
    question: 'Where can I read the beginner’s roadmap to Sanskrit?',
    sanskritQuestion: 'संस्कृत-मार्गदर्शिका कुत्र?',
    shortAnswer: 'Open “संस्कृत-मार्गदर्शिका · A Beginner\'s Roadmap to Learning Sanskrit” on the grammar shelf.',
    detailedAnswer: 'The roadmap orders sound, grammar, stories, and classics, with an eight-week starter plan and a short daily routine.',
    tip: 'Search roadmap or where to start.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'beginners-roadmap',
  },
  {
    id: 'qa-naming-colossal-grammar',
    category: 'vedic_math',
    question: 'Where is the grammar-shelf article on naming numbers past a million?',
    sanskritQuestion: 'दशगुणोत्तरसंज्ञाः कुत्र?',
    shortAnswer: 'Open “दशगुणोत्तरसंज्ञाः महासङ्ख्याश्च · Naming the Colossal: How Indian Texts Counted Past a Million” on the grammar shelf.',
    detailedAnswer: 'That grammar article names the decuple rungs and the larger counts given in the Yajurveda, the Vālmīki Rāmāyaṇa, the Lalitavistara, the Līlāvatī, and Jaina texts.',
    tip: 'Search dashagunottara or parardha.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'naming-the-colossal',
  },
  {
    id: 'qa-vakyapadiya-grammar',
    category: 'philosophy',
    question: 'Where is the grammar-shelf page on the Vākyapadīya and AI?',
    sanskritQuestion: 'व्याकरण-फलके वाक्यपदीय-लेखः कुत्र?',
    shortAnswer: 'Open “वाक्यपदीयं कृत्रिमप्रज्ञा च · Computational Optimization in Classical Semantics: Connecting Bhartṛhari’s Vākyapadīya to Modern AI” on the grammar shelf.',
    detailedAnswer: 'The grammar-shelf page connects Bhartṛhari’s sphoṭa and kāraka roles with Rick Briggs’ 1985 NASA paper.',
    tip: 'Search vakyapadiya on the grammar shelf.',
    relatedView: 'grammar',
    relatedGrammarArticleId: 'vakyapadiya-and-ai',
  },
  {
    id: 'qa-lit-grammar-basic',
    category: 'grammar',
    question: 'Where is the basic quiz on sandhi, vibhakti, gender, and verb agreement?',
    sanskritQuestion: 'सन्धि-विभक्ति-लिङ्ग-वचन-प्रश्नोत्तरी कुत्र?',
    shortAnswer: 'Open “Basic: Sandhi, vibhakti, gender, and verb agreement” on the quiz page.',
    detailedAnswer: 'That quiz has four questions. The lines are the ones preserved in Baudhāyana Śulbasūtra 1.12 and Kauṭilīya Arthaśāstra 1.1.1. The Deepakam lesson quiz is still there beside it.',
    tip: 'Search lit-grammar-basic, sulbasutra, or arthashastra.',
    relatedView: 'quiz',
    relatedQuizAnchor: 'lit-grammar-basic',
  },
  {
    id: 'qa-lit-grammar-middle',
    category: 'grammar',
    question: 'Where is the middle quiz on kāraka, samāsa, and lakāra?',
    sanskritQuestion: 'कारक-समास-लकार-प्रश्नोत्तरी कुत्र?',
    shortAnswer: 'Open “Middle: Kāraka, samāsa, and lakāra” on the quiz page.',
    detailedAnswer: 'That quiz has four questions, from Arthaśāstra 1.1.1, Baudhāyana Śulbasūtra 1.12, and Nāradīya Śikṣā 1.5.3.',
    tip: 'Search lit-grammar-middle, karaka, or lakara.',
    relatedView: 'quiz',
    relatedQuizAnchor: 'lit-grammar-middle',
  },
  {
    id: 'qa-lit-grammar-higher',
    category: 'grammar',
    question: 'Where is the higher quiz on a Pāṇini sūtra, a Śikṣā note, and a Līlāvatī line?',
    sanskritQuestion: 'आद्गुणः शिक्षा लीलावती-प्रश्नोत्तरी च कुत्र?',
    shortAnswer: 'Open “Higher: A Pāṇini sūtra in use, a Śikṣā note, and a Līlāvatī line” on the quiz page.',
    detailedAnswer: 'That quiz has three questions: आद्गुणः as the Nāradīya Śikṣā prints चर्षभम्, the throat-and-chest note in the same Śikṣā, and स्यात् in the Līlāvatī line on खहर.',
    tip: 'Search lit-grammar-higher, adgunah, or lilavati.',
    relatedView: 'quiz',
    relatedQuizAnchor: 'lit-grammar-higher',
  },
  {
    id: 'qa-logic-and-language',
    category: 'vedic_math',
    question: 'Where can I read The Logic and Language of Vedic Mathematics?',
    sanskritQuestion: 'वैदिक-गणितस्य तर्कः भाषा च कुत्र पठनीया?',
    shortAnswer: 'Open “The Logic and Language of Vedic Mathematics: Reconstructing Ancient Sound for Modern Calculation” on the Vedic Maths page.',
    detailedAnswer: 'That essay is on the Logic & Language tab. It shows how the wording of the sūtras turns arithmetic into a visual, rhythmic calculation, as Swami Bharati Krishna Tirtha set them out.',
    tip: 'Search logic and language, or ancient sound.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'logic',
  },
  {
    id: 'qa-absolute-zero-essay',
    category: 'vedic_math',
    question: 'Where is the essay on the architecture of absolute zero and place value?',
    sanskritQuestion: 'शून्यस्य स्थान-मूल्यस्य च लेखः कुत्र?',
    shortAnswer: 'Open “The Architecture of Absolute Zero: How Vedic Math Explores the Universal Power of Place Value” on the Vedic Maths page.',
    detailedAnswer: 'That essay is the Numerical Grid & Zero tab. It explains positional place value and śūnya, past the speed tricks.',
    tip: 'Search absolute zero, shunya, or place value.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'zero',
  },
  {
    id: 'qa-speed-math-challenge',
    category: 'vedic_math',
    question: 'Where is the Speed Math Challenge quiz?',
    sanskritQuestion: 'द्रुत-गणित-परीक्षा कुत्र?',
    shortAnswer: 'Open “Speed Math Challenge” on the Vedic Maths page.',
    detailedAnswer: 'That quiz is six timed questions on the 16 sūtras, mental shortcuts, and astronomical timekeeping.',
    tip: 'Search speed math challenge, or vedic quiz.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'quiz',
  },
  {
    id: 'qa-solver-ekadhikena',
    category: 'vedic_math',
    question: 'Where is the solver for squaring a number that ends in 5?',
    sanskritQuestion: 'एकाधिकेन पूर्वेण साधनं कुत्र?',
    shortAnswer: 'Open “एकाधिकेन पूर्वेण · Squaring numbers ending in 5” in the Vedic solvers.',
    detailedAnswer: 'That solver uses Ekādhikena Pūrveṇa. For 75² the left part is 7 × 8 and the right part is 25.',
    tip: 'Search ekadhikena, or square ending in 5.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'solver-ekadhikena',
  },
  {
    id: 'qa-solver-nikhilam-sub',
    category: 'vedic_math',
    question: 'Where is the Nikhilam subtraction solver?',
    sanskritQuestion: 'निखिलम् घटन-साधनं कुत्र?',
    shortAnswer: 'Open “निखिलम् (घटनम्) · All from 9, last from 10 (Subtraction)” in the Vedic solvers.',
    detailedAnswer: 'That solver subtracts from a power of 10 by taking all digits from 9 and the last from 10.',
    tip: 'Search nikhilam subtraction, or all from 9.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'solver-nikhilam-sub',
  },
  {
    id: 'qa-solver-nikhilam-mul',
    category: 'vedic_math',
    question: 'Where is the Nikhilam base-multiplication solver?',
    sanskritQuestion: 'निखिलम् गुणन-साधनं कुत्र?',
    shortAnswer: 'Open “निखिलम् (गुणनम्) · Base multiplication near 100” in the Vedic solvers.',
    detailedAnswer: 'That solver multiplies numbers near a base such as 100. The sample on the page is 96 × 93.',
    tip: 'Search nikhilam multiplication, or 96 times 93.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'solver-nikhilam-mul',
  },
  {
    id: 'qa-solver-urdhva',
    category: 'vedic_math',
    question: 'Where is the vertically-and-crosswise multiplication solver?',
    sanskritQuestion: 'ऊर्ध्वतिर्यग्भ्याम् साधनं कुत्र?',
    shortAnswer: 'Open “ऊर्ध्वतिर्यग्भ्याम् · Universal 2-digit multiplication” in the Vedic solvers.',
    detailedAnswer: 'That solver multiplies two 2-digit numbers by the sūtra Ūrdhva-Tiryagbhyām, vertically and crosswise.',
    tip: 'Search urdhva, or vertically and crosswise.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'solver-urdhva',
  },
  {
    id: 'qa-solver-ekanyunena',
    category: 'vedic_math',
    question: 'Where is the solver for multiplying by 99 or 999?',
    sanskritQuestion: 'एकन्यूनेन पूर्वेण साधनं कुत्र?',
    shortAnswer: 'Open “Ekanyūnena Pūrveṇa · Multiply by 99 or 999” in the Vedic solvers.',
    detailedAnswer: 'That solver multiplies by a string of nines. Take one less than the number, then the nines-complement.',
    tip: 'Search ekanyunena, or multiply by 99.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'solver-ekanyunena',
  },
  {
    id: 'qa-solver-antya',
    category: 'vedic_math',
    question: 'Where is the solver for numbers whose units add to 10?',
    sanskritQuestion: 'अन्त्ययोर्दशकेऽपि साधनं कुत्र?',
    shortAnswer: 'Open “अन्त्ययोर्दशकेऽपि · Units sum to 10, tens equal” in the Vedic solvers.',
    detailedAnswer: 'That solver is for pairs such as 43 × 47, where the tens digits match and the units add to 10.',
    tip: 'Search antyayordasake, or 43 times 47.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'solver-antya',
  },
  {
    id: 'qa-solver-beejank',
    category: 'vedic_math',
    question: 'Where is the digital-root check solver?',
    sanskritQuestion: 'बीजाङ्क-साधनं कुत्र?',
    shortAnswer: 'Open “Bījāṅka · Digital Root Check (Guṇitasamuccayaḥ)” in the Vedic solvers.',
    detailedAnswer: 'That solver reduces two numbers to digital roots and checks that the product’s root matches. The check is called Guṇitasamuccayaḥ.',
    tip: 'Search beejank, digital root, or gunita samuccaya.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'solver-beejank',
  },
  {
    id: 'qa-sutra-1',
    category: 'vedic_math',
    question: 'Where is sūtra 1, Ekādhikena Pūrveṇa?',
    sanskritQuestion: 'एकाधिकेन पूर्वेण सूत्रं कुत्र?',
    shortAnswer: 'Open “एकाधिकेन पूर्वेण · Ekādhikena Pūrveṇa” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 1. It means “By one more than the previous one.”',
    tip: 'Search ekādhikena pūrveṇa, or sutra 1.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-1',
  },
  {
    id: 'qa-sutra-2',
    category: 'vedic_math',
    question: 'Where is sūtra 2, Nikhilaṁ Navataścaramaṁ Daśataḥ?',
    sanskritQuestion: 'निखिलं नवतश्चरमं दशतः सूत्रं कुत्र?',
    shortAnswer: 'Open “निखिलं नवतश्चरमं दशतः · Nikhilaṁ Navataścaramaṁ Daśataḥ” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 2. It means “All from 9 and the last from 10.”',
    tip: 'Search nikhilaṁ navataścaramaṁ daśataḥ, or sutra 2.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-2',
  },
  {
    id: 'qa-sutra-3',
    category: 'vedic_math',
    question: 'Where is sūtra 3, Ūrdhva-Tiryagbhyām?',
    sanskritQuestion: 'ऊर्ध्वतिर्यग्भ्याम् सूत्रं कुत्र?',
    shortAnswer: 'Open “ऊर्ध्वतिर्यग्भ्याम् · Ūrdhva-Tiryagbhyām” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 3. It means “Vertically and crosswise.”',
    tip: 'Search ūrdhva-tiryagbhyām, or sutra 3.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-3',
  },
  {
    id: 'qa-sutra-4',
    category: 'vedic_math',
    question: 'Where is sūtra 4, Parāvartya Yojayet?',
    sanskritQuestion: 'परावर्त्य योजयेत् सूत्रं कुत्र?',
    shortAnswer: 'Open “परावर्त्य योजयेत् · Parāvartya Yojayet” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 4. It means “Transpose and apply.”',
    tip: 'Search parāvartya yojayet, or sutra 4.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-4',
  },
  {
    id: 'qa-sutra-5',
    category: 'vedic_math',
    question: 'Where is sūtra 5, Śūnyaṁ Sāmyasamuccaye?',
    sanskritQuestion: 'शून्यं साम्यसमुच्चये सूत्रं कुत्र?',
    shortAnswer: 'Open “शून्यं साम्यसमुच्चये · Śūnyaṁ Sāmyasamuccaye” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 5. It means “When the collection is the same, it is zero.”',
    tip: 'Search śūnyaṁ sāmyasamuccaye, or sutra 5.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-5',
  },
  {
    id: 'qa-sutra-6',
    category: 'vedic_math',
    question: 'Where is sūtra 6, (Ānurūpye) Śūnyamanyat?',
    sanskritQuestion: '(आनुरूप्ये) शून्यमन्यत् सूत्रं कुत्र?',
    shortAnswer: 'Open “(आनुरूप्ये) शून्यमन्यत् · (Ānurūpye) Śūnyamanyat” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 6. It means “If one is in ratio, the other is zero.”',
    tip: 'Search (ānurūpye) śūnyamanyat, or sutra 6.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-6',
  },
  {
    id: 'qa-sutra-7',
    category: 'vedic_math',
    question: 'Where is sūtra 7, Saṅkalana-Vyavakalanābhyām?',
    sanskritQuestion: 'सङ्कलनव्यवकलनाभ्याम् सूत्रं कुत्र?',
    shortAnswer: 'Open “सङ्कलनव्यवकलनाभ्याम् · Saṅkalana-Vyavakalanābhyām” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 7. It means “By addition and by subtraction.”',
    tip: 'Search saṅkalana-vyavakalanābhyām, or sutra 7.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-7',
  },
  {
    id: 'qa-sutra-8',
    category: 'vedic_math',
    question: 'Where is sūtra 8, Pūraṇāpūraṇābhyām?',
    sanskritQuestion: 'पूरणापूरणाभ्याम् सूत्रं कुत्र?',
    shortAnswer: 'Open “पूरणापूरणाभ्याम् · Pūraṇāpūraṇābhyām” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 8. It means “By the completion or non-completion.”',
    tip: 'Search pūraṇāpūraṇābhyām, or sutra 8.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-8',
  },
  {
    id: 'qa-sutra-9',
    category: 'vedic_math',
    question: 'Where is sūtra 9, Calana-Kalanābhyām?',
    sanskritQuestion: 'चलनकलनाभ्याम् सूत्रं कुत्र?',
    shortAnswer: 'Open “चलनकलनाभ्याम् · Calana-Kalanābhyām” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 9. It means “By calculus (differential) and accumulation.”',
    tip: 'Search calana-kalanābhyām, or sutra 9.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-9',
  },
  {
    id: 'qa-sutra-10',
    category: 'vedic_math',
    question: 'Where is sūtra 10, Yāvadūnam?',
    sanskritQuestion: 'यावदूनम् सूत्रं कुत्र?',
    shortAnswer: 'Open “यावदूनम् · Yāvadūnam” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 10. It means “By the deficiency.”',
    tip: 'Search yāvadūnam, or sutra 10.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-10',
  },
  {
    id: 'qa-sutra-11',
    category: 'vedic_math',
    question: 'Where is sūtra 11, Vyaṣṭisamaṣṭiḥ?',
    sanskritQuestion: 'व्यष्टिसमष्टिः सूत्रं कुत्र?',
    shortAnswer: 'Open “व्यष्टिसमष्टिः · Vyaṣṭisamaṣṭiḥ” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 11. It means “Specific and general (Individual and Aggregate).”',
    tip: 'Search vyaṣṭisamaṣṭiḥ, or sutra 11.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-11',
  },
  {
    id: 'qa-sutra-12',
    category: 'vedic_math',
    question: 'Where is sūtra 12, Śeṣāṇyaṅkena Carameṇa?',
    sanskritQuestion: 'शेषाण्यङ्केन चरमेण सूत्रं कुत्र?',
    shortAnswer: 'Open “शेषाण्यङ्केन चरमेण · Śeṣāṇyaṅkena Carameṇa” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 12. It means “The remainders by the last digit.”',
    tip: 'Search śeṣāṇyaṅkena carameṇa, or sutra 12.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-12',
  },
  {
    id: 'qa-sutra-13',
    category: 'vedic_math',
    question: 'Where is sūtra 13, Sopāntyadvayamantyam?',
    sanskritQuestion: 'सोपान्त्यद्वयमन्त्यम् सूत्रं कुत्र?',
    shortAnswer: 'Open “सोपान्त्यद्वयमन्त्यम् · Sopāntyadvayamantyam” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 13. It means “The ultimate and twice the penultimate.”',
    tip: 'Search sopāntyadvayamantyam, or sutra 13.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-13',
  },
  {
    id: 'qa-sutra-14',
    category: 'vedic_math',
    question: 'Where is sūtra 14, Ekanyūnena Pūrveṇa?',
    sanskritQuestion: 'एकन्यूनेन पूर्वेण सूत्रं कुत्र?',
    shortAnswer: 'Open “एकन्यूनेन पूर्वेण · Ekanyūnena Pūrveṇa” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 14. It means “By one less than the previous one.”',
    tip: 'Search ekanyūnena pūrveṇa, or sutra 14.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-14',
  },
  {
    id: 'qa-sutra-15',
    category: 'vedic_math',
    question: 'Where is sūtra 15, Samuccayaguṇitaḥ?',
    sanskritQuestion: 'समुच्चयगुणितः सूत्रं कुत्र?',
    shortAnswer: 'Open “समुच्चयगुणितः · Samuccayaguṇitaḥ” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 15. It means “The product of the sum is the sum of the products.”',
    tip: 'Search samuccayaguṇitaḥ, or sutra 15.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-15',
  },
  {
    id: 'qa-sutra-16',
    category: 'vedic_math',
    question: 'Where is sūtra 16, Guṇakasamuccayaḥ?',
    sanskritQuestion: 'गुणाकसमुच्चयः सूत्रं कुत्र?',
    shortAnswer: 'Open “गुणाकसमुच्चयः · Guṇakasamuccayaḥ” on the Vedic Maths page.',
    detailedAnswer: 'That card is sūtra 16. It means “The all-factor factor.”',
    tip: 'Search guṇakasamuccayaḥ, or sutra 16.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sutra-16',
  },
  {
    id: 'qa-vedic-learning-path',
    category: 'vedic_math',
    question: 'Where is the step-by-step path from Numbers into Vedic Maths?',
    sanskritQuestion: 'संख्यातः वैदिक-गणित-मार्गः कुत्र?',
    shortAnswer: 'Open “Step-by-step path: Numbers → Vedic Maths (Classes 6–8)” on the Vedic Maths page.',
    detailedAnswer: 'That path sits on the Sūtras tab. It orders place value, then the sūtras a Class 6–8 reader meets next.',
    tip: 'Search vedic path, or step by step.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'vedic-path',
  },
  {
    id: 'qa-fluid-space-studio',
    category: 'vedic_math',
    question: 'Where is the studio that treats place value as fluid space?',
    sanskritQuestion: 'स्थानमानस्य सातत्य-प्रयोगशाला कुत्र?',
    shortAnswer: 'Open “Treating Place Value as Fluid Space” on the Vedic Maths page.',
    detailedAnswer: 'That studio is the Fluid Space & Mental Math tab. It is the interactive page, not the separate article of the same idea.',
    tip: 'Search fluid space, or simultaneous processing.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'fluid',
  },
  {
    id: 'qa-kerala-studio',
    category: 'vedic_math',
    question: 'Where is the Kerala School studio on the invention of calculus?',
    sanskritQuestion: 'केरल-गणित-प्रयोगशाला कुत्र?',
    shortAnswer: 'Open “The Kerala School & The Invention of Calculus” on the Vedic Maths page.',
    detailedAnswer: 'That studio is the Kerala School & Calculus tab. The masterclass article is a separate page.',
    tip: 'Search kerala school, or kerala studio.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'kerala',
  },
  {
    id: 'qa-yuktibhasa',
    category: 'vedic_math',
    question: 'Where is Jyeṣṭhadeva’s Yuktibhāṣā proof of calculus?',
    sanskritQuestion: 'युक्तिभाषा-प्रमाणं कुत्र?',
    shortAnswer: 'Open “Jyeṣṭhadeva’s Yuktibhāṣā: The First Systematic Proof of Calculus” on the Vedic Maths page.',
    detailedAnswer: 'That lab is on the Kerala School tab. It walks the square octant, micro-segments, projection, and the summation limit.',
    tip: 'Search yuktibhasa, or jyeshthadeva.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'yuktibhasa',
  },
  {
    id: 'qa-kerala-astronomy',
    category: 'vedic_math',
    question: 'Where is Nīlakaṇṭha Somayājī’s geo-heliocentrism and parallax lab?',
    sanskritQuestion: 'नीलकण्ठ-खगोल-प्रयोगशाला कुत्र?',
    shortAnswer: 'Open “Nīlakaṇṭha Somayājī: Geo-Heliocentrism & Parallax Calculus” on the Vedic Maths page.',
    detailedAnswer: 'That lab is on the Kerala School tab. It shows the planetary model and eclipse parallax.',
    tip: 'Search nilakantha, or kerala astronomy.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'astronomy-kerala',
  },
  {
    id: 'qa-parampara-studio',
    category: 'vedic_math',
    question: 'Where is the studio on the historiographical framework and the living lineage?',
    sanskritQuestion: 'गुरु-परम्परा-प्रयोगशाला कुत्र?',
    shortAnswer: 'Open “Historiographical Framework & The Living Lineage” on the Vedic Maths page.',
    detailedAnswer: 'That studio is the Guru Paramparā & Lineage tab. The masterclass article on the lineage is a separate page.',
    tip: 'Search parampara, or living lineage.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'parampara',
  },
  {
    id: 'qa-historiography-pillars',
    category: 'vedic_math',
    question: 'Where are the five historiographical pillars of Indian mathematics?',
    sanskritQuestion: 'पञ्च इतिहास-स्तम्भाः कुत्र?',
    shortAnswer: 'Open “The 5 Historiographical Pillars of Indian Mathematics” on the Vedic Maths page.',
    detailedAnswer: 'That section is on the Guru Paramparā tab. It sets out the five pillars used to date the tradition.',
    tip: 'Search historiographical pillars, or five pillars.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'historiography',
  },
  {
    id: 'qa-shaka-matrix',
    category: 'vedic_math',
    question: 'Where is the chronological matrix from the Shaka era to the Common Era?',
    sanskritQuestion: 'शक-संवत्-सारणी कुत्र?',
    shortAnswer: 'Open “Chronological Matrix: Shaka Era to Common Era (CE)” on the Vedic Maths page.',
    detailedAnswer: 'That matrix is on the Guru Paramparā tab. The epoch used on the page is the Śālivāhana Śaka era, which began in 78 CE.',
    tip: 'Search shaka matrix, or shaka to ce.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'shaka-matrix',
  },
  {
    id: 'qa-vedic-geometry-shapes',
    category: 'vedic_math',
    question: 'Where is Vedic Geometry: The Science of Shapes?',
    sanskritQuestion: 'रेखागणितं शुल्बसूत्राणि च कुत्र?',
    shortAnswer: 'Open “Vedic Geometry: The Science of Shapes” on the Vedic Maths page.',
    detailedAnswer: 'That page is the Vedic Geometry & Śulba tab. It starts from the Śulba Sūtras and the fire-altar constructions.',
    tip: 'Search science of shapes, or vedic geometry.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'geometry',
  },
  {
    id: 'qa-bhaskara-dob-section',
    category: 'vedic_math',
    question: 'Where is the section on how Bhāskarācārya encrypted his date of birth?',
    sanskritQuestion: 'भास्कराचार्यस्य जन्म-तिथि-गुप्तिः कुत्र?',
    shortAnswer: 'Open “How Bhāskarācārya Encrypted His Date of Birth (D.O.B.)” on the Vedic Maths page.',
    detailedAnswer: 'That section is on the Algebra tab. It decodes the Bhūta-Saṅkhyā verse for his birth date.',
    tip: 'Search bhaskara date of birth, or bhuta sankhya.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'bhaskara-dob',
  },
  {
    id: 'qa-bhuta-shunya-purna',
    category: 'vedic_math',
    question: 'In Bhūta-Saṅkhyā, how can zero be both śūnya and pūrṇa?',
    sanskritQuestion: 'भूतसङ्ख्यायां शून्यं पूर्णं च कथं एकम्?',
    shortAnswer: 'Śūnya names void/emptiness and pūrṇa names fullness/wholeness — two sides of the same zero.',
    detailedAnswer: 'In the Bhūta-Saṅkhyā word-numeral system, digit zero is mapped to both śūnya (शून्य — void, emptiness) and pūrṇa (पूर्ण — fullness, wholeness). Sanskrit thought treats absolute emptiness and absolute totality as meeting in one idea: emptiness is completeness; what arises from that unconditioned fullness–void also dissolves back into it. This is a philosophical pairing of the two terms already used for zero in the article — not a claim that zero is only “nothing.” Bhāskara’s birth chronogram uses Pūrṇa for that digit.',
    tip: 'Search śūnya pūrṇa, or bhuta sankhya zero — then tap digit 0 in the word-numeral dictionary.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'bhuta-shunya-purna',
  },
  {
    id: 'qa-vyakta-avyakta-section',
    category: 'vedic_math',
    question: 'Where is Vyakta Gaṇitam versus Avyakta Gaṇitam?',
    sanskritQuestion: 'व्यक्तगणितम् अव्यक्तगणितं च कुत्र?',
    shortAnswer: 'Open “Vyakta Gaṇitam (Arithmetic) vs. Avyakta Gaṇitam (Algebra)” on the Vedic Maths page.',
    detailedAnswer: 'That section is on the Algebra tab. It sets expressed arithmetic beside unmanifest algebra and the color variables.',
    tip: 'Search vyakta, or avyakta ganitam.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'vyakta-avyakta',
  },
  {
    id: 'qa-surya-siddhanta-section',
    category: 'vedic_math',
    question: 'Where is the mathematician’s view of cosmic reality and consciousness?',
    sanskritQuestion: 'सूर्यसिद्धान्तस्य मङ्गलाचरणं कुत्र?',
    shortAnswer: 'Open “A Mathematician’s View of Cosmic Reality & Consciousness” on the Vedic Maths page.',
    detailedAnswer: 'That section is on the Algebra tab. It reads the Sūrya Siddhānta opening verse, Acintyāvyaktarūpāya.',
    tip: 'Search surya siddhanta, or acintya avyakta.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'surya-siddhanta',
  },
  {
    id: 'qa-melakarta-section',
    category: 'vedic_math',
    question: 'Where is the Kaṭapayādi cipher and the 72 Melakarta rāgas?',
    sanskritQuestion: 'कटपयादि मेळकर्ता च कुत्र?',
    shortAnswer: 'Open “The Kaṭapayādi Alphanumeric Cipher & The 72 Melakarta Rāgas” on the Vedic Maths page.',
    detailedAnswer: 'That lab is on the Kerala School tab. It turns consonants into digits and names the 72 parent rāgas.',
    tip: 'Search katapayadi, or melakarta.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'melakarta',
  },
  {
    id: 'qa-sacred-epistemology-section',
    category: 'vedic_math',
    question: 'Where is the section on humility, Guru-Bhakti, and Paramātmā as the source of knowledge?',
    sanskritQuestion: 'गुरुभक्तिः ज्ञान-विनयः च कुत्र?',
    shortAnswer: 'Open “Humility, Guru-Bhakti & Paramātmā as the Supreme Source of Knowledge” on the Vedic Maths page.',
    detailedAnswer: 'That section is on the Guru Paramparā tab. It treats knowledge as offered, not owned.',
    tip: 'Search guru bhakti, or sacred epistemology.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'sacred-epistemology',
  },
  {
    id: 'qa-cosmic-bridge-section',
    category: 'vedic_math',
    question: 'Where is the cosmic bridge from mathematics to humans and Sanskrit?',
    sanskritQuestion: 'विश्व-मानव-संस्कृत-सेतुः कुत्र?',
    shortAnswer: 'Open “Mathematics (Universe) ⇄ Humans (Sanskrit)” on the Vedic Maths page.',
    detailedAnswer: 'That section is on the Guru Paramparā tab, under sacred epistemology. The masterclass article is a separate page.',
    tip: 'Search cosmic bridge.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'cosmic-bridge-section',
  },
  {
    id: 'qa-vibhakti-guide',
    category: 'grammar',
    question: 'Where is the guide to understanding vibhaktis?',
    sanskritQuestion: 'विभक्ति-परिचयः कुत्र?',
    shortAnswer: 'Open “विभक्ति-परिचयः · Understanding Vibhaktis”.',
    detailedAnswer: 'That guide is the interactive Vibhakti page: the eight cases, kāraka roles, and the Bālaka table.',
    tip: 'Search vibhakti guide, or noun cases.',
    relatedView: 'grammar',
    relatedGrammarTopic: 'vibhakti',
  },
  {
    id: 'qa-linga-vachana-guide',
    category: 'grammar',
    question: 'Where is the gender and number system guide?',
    sanskritQuestion: 'लिङ्गं वचनं च कुत्र?',
    shortAnswer: 'Open “लिङ्गं वचनं च · Gender & Number System”.',
    detailedAnswer: 'That guide is the interactive Liṅga and Vacana page: three genders, three numbers, and subject-verb agreement.',
    tip: 'Search linga vachana, or gender and number.',
    relatedView: 'grammar',
    relatedGrammarTopic: 'linga-vachana',
  },
  {
    id: 'qa-numbers-masterclass',
    category: 'grammar',
    question: 'Where is the Sanskrit numbers guide?',
    sanskritQuestion: 'संस्कृत-संख्या-परिचयः कुत्र?',
    shortAnswer: 'Open “संस्कृत-संख्या-परिचयः”.',
    detailedAnswer: 'That guide is the Numbers masterclass: 1 to 100, gender rules for 1–4, ordinals, and the large powers of ten.',
    tip: 'Search sanskrit numbers, or sankhya.',
    relatedView: 'grammar',
    relatedGrammarTopic: 'numbers',
  },
  {
    id: 'qa-conjunct-games',
    category: 'grammar',
    question: 'Where are the conjunct games?',
    sanskritQuestion: 'संयुक्त-क्रीडाः कुत्र?',
    shortAnswer: 'Open “संयुक्त · Conjunct Games”.',
    detailedAnswer: 'That page is Conjunct Games: three posters for how half-letters and ligatures are built.',
    tip: 'Search conjunct games, or samyukta.',
    relatedView: 'grammar',
    relatedGrammarTopic: 'samyukta',
  },
  {
    id: 'qa-sound-teams',
    category: 'grammar',
    question: 'Where are the five sound teams?',
    sanskritQuestion: 'पञ्च वर्ण-टीमाः कुत्र?',
    shortAnswer: 'Open “Five Sound Teams · पञ्च वर्ण-टीमें”.',
    detailedAnswer: 'That page sorts every letter into a sound team: vowels, consonants, sliders, hissers, and fusion blocks.',
    tip: 'Search sound teams, or varna teams.',
    relatedView: 'grammar',
    relatedGrammarTopic: 'sound-teams',
  },
  {
    id: 'qa-science-of-sound',
    category: 'grammar',
    question: 'Where is Sanskrit: the science of sound?',
    sanskritQuestion: 'ध्वनि-विज्ञानम् कुत्र?',
    shortAnswer: 'Open “संस्कृतम् · ध्वनि-विज्ञानम्”.',
    detailedAnswer: 'That page is the science-of-sound masterclass: the five articulation points and the embedded film.',
    tip: 'Search science of sound, or dhvani vijnanam.',
    relatedView: 'grammar',
    relatedGrammarTopic: 'science-of-sound',
  },
  {
    id: 'qa-dhatupatha-studio',
    category: 'grammar',
    question: 'Where is the Pāṇinian Dhātupāṭha studio?',
    sanskritQuestion: 'पाणिनीय-धातुपाठ-प्रयोगशाला कुत्र?',
    shortAnswer: 'Open “पाणिनीय-धातुपाठ-प्रयोगशाला”.',
    detailedAnswer: 'That studio conjugates roots, compares paradigms, and opens the Dhātupāṭha articles.',
    tip: 'Search dhatupatha, or paninian studio.',
    relatedView: 'dhatupatha',
  },
  {
    id: 'qa-animal-yoga-shapes',
    category: 'grammar',
    question: 'Why are so many Yoga Asanas named after animals (Cobra, Cat, Dog, Tortoise, Peacock)?',
    sanskritQuestion: 'योगासनानि पशूनां नामभिः किमर्थं प्रसिद्धाः सन्ति?',
    shortAnswer: 'Ancient yogis practiced biomimicry: observing that wild animals never suffer from spinal degeneration, they modeled asanas (Cobra, Cat, Dog, Tortoise, Peacock) to heal human posture and still the mind.',
    detailedAnswer: 'Sitting in chairs for long hours causes thoracic kyphosis, psoas shortening, and shallow panic breathing. The ancient masters of Haṭha Yoga observed that wild animals maintain dynamic spinal health through natural kinetic waves: Bhujaṅgāsana (Cobra) strengthens the multifidi and erector spinae without wrist loading; Mārjārāsana (Cat) pumps synovial fluid across intervertebral discs; Adho Mukha Śvānāsana (Downward Dog) decompresses the posterior chain; Mayūrāsana (Peacock) compresses the abdominal aorta to flush digestive organs and burn metabolic toxins (āma); and Kūrmāsana (Tortoise) embodies sensory withdrawal (Pratyāhāra, Gītā 2.58).',
    audioDevanagari: 'भुजङ्गासनम् मार्जारासनम् कूर्मासनम् मयूरासनम् सिंहासनम्',
    tip: 'Read Article 18 in the Grammar section to explore the complete interactive matrix of animal names and yoga shapes!',
    relatedView: 'grammar',
  },
  {
    id: 'qa-animal-singing-notes-sapta-swara',
    category: 'philosophy',
    question: 'How did the 7 musical notes (Sa Re Ga Ma Pa Dha Ni) originate from animal cries?',
    sanskritQuestion: 'सप्तस्वराः (स रे ग म प ध नि) पशूनां पक्षिणां च ध्वनेः कथम् आविर्भूताः?',
    shortAnswer: 'According to the Nāradīya Śikṣā and Saṅgīta Ratnākara, the 7 swaras originated from nature: Sa from the Peacock, Re from the Bull, Ga from the Goat, Ma from the Crane, Pa from the Cuckoo, Dha from the Horse, and Ni from the Elephant.',
    detailedAnswer: 'Classical Indian musicology (Gāndharva Veda) did not separate human music from the acoustic frequencies of nature. The foundational shloka declares: "षड्जं वदति मयूरो गावो रम्भन्ति चर्षभम् । अजाविकं तु गान्धारं क्रौञ्चः क्वणति मध्यमम् ॥ पुष्पसाधारणे काले पिकः कूजति पञ्चमम् । धैवतं हेषते वाजी निषादं बृंहते गजः ॥" Sa (Ṣaḍja) is the rain call of the Peacock; Re (Ṛṣabha) is the lowing of the Bull/Cow; Ga (Gāndhāra) is the bleat of the Goat; Ma (Madhyama) is the mournful cry of the Crane; Pa (Pañcama) is the spring whistle of the Cuckoo; Dha (Dhaivata) is the spirited neigh of the Horse; and Ni (Niṣāda) is the seismic infrasound trumpet of the Elephant!',
    audioDevanagari: 'षड्जं वदति मयूरो गावो रम्भन्ति चर्षभम् अजाविकं तु गान्धारं क्रौञ्चः क्वणति मध्यमम्',
    tip: 'Try the interactive sound synthesizer in Article 18 to hear the exact frequency of each animal note in real time!',
    relatedView: 'grammar',
  },
  {
    id: 'qa-vyakarana-beginner-course',
    category: 'grammar',
    question: 'Where can an absolute beginner start learning Sanskrit grammar systematically?',
    sanskritQuestion: 'संस्कृत-व्याकरणस्य आरम्भिक-पाठ्यक्रमः कुत्र प्राप्यते?',
    shortAnswer: 'Open the "10-Part Beginner Course" under the Vyākaraṇa section for a sequential 10-lesson track from phonetics to sentence construction.',
    detailedAnswer: 'The Vyākaraṇa section features a systematic 10-lesson curriculum divided into 4 sequential modules: Module 1 covers Devanāgarī, the 13 vowels, 33 consonants, and roots vs stems; Module 2 explores noun declensions (Vibhakti) with Bālaka, Latā, and Nadī; Module 3 covers present tense verb conjugations (Laṭ Lakāra) and the 3 persons (Puruṣa); and Module 4 synthesizes active voice sentence structure (Kartari Prayoga) and essential conversational indeclinables (Avyayas).',
    audioDevanagari: 'व्याकरण-पाठ्यक्रमः वर्णाः विभक्तयः लकाराः वाक्य-रचना',
    tip: 'Click on Lesson 1: Introduction to Devanāgarī & Vowels under the Vyākaraṇa tab to start your journey!',
    relatedView: 'grammar',
  },
  {
    id: 'qa-lab-vijnana',
    category: 'engineering',
    question: 'What is the Vijñāna Lab (विज्ञान-प्रयोगशाला)?',
    sanskritQuestion: 'विज्ञान-प्रयोगशाला का?',
    shortAnswer: 'A free set of science sandboxes with Sanskrit terms beside them, at /science-lab.',
    detailedAnswer: 'Vijñāna Lab has eight interactive sandboxes: the Paramāṇu Builder (#paramanu), Prakṛti Biosphere (#prakriti), Jyotiṣa Gravity Sandbox (#jyotisha), The Loom of Āruṇi · Sṛṣṭi · Sthiti · Laya (#srishti-sthiti-laya), Cosmic Sitār Tuning · Nāda Brahman (#nada-brahman), The Veil of Māyā · Asato mā (#asato-ma), the Chaturyoni Spawn Map · ancient Indian biology (#chaturyoni) and Somayāna Mission-03 · Chandrayaan (#chandrayaan). Each has a core idea, the sandbox and a Sanskrit term panel you can tap to hear; most also have quick challenges. It is open to guests.',
    audioDevanagari: 'विज्ञान-प्रयोगशाला',
    tip: 'Open it from the home page card or the साधनानि (tools) page, or search “science lab”.',
    relatedView: 'science-lab',
  },
  {
    id: 'qa-lab-paramanu',
    category: 'philosophy',
    question: 'What is a paramāṇu, and is it the same as an atom?',
    sanskritQuestion: 'परमाणुः कः?',
    shortAnswer: 'In the Vaiśeṣika Sūtra, attributed to Kaṇāda, a paramāṇu is an indivisible, imperceptible particle. It is an ancient philosophical idea, not the same as the modern atom.',
    detailedAnswer: 'In the classical Nyāya-Vaiśeṣika account, two paramāṇus join into a dvyaṇuka, and three dvyaṇukas join into a tryaṇuka (also called trasareṇu), the first perceptible size, often compared to a mote of dust in a sunbeam. Paramāṇus come in four kinds: pṛthivī, ap, tejas and vāyu. Ākāśa is one of the five mahābhūtas but is not atomic: it is one and all-pervading. It is a fascinating early way of thinking about tiny building blocks, not modern atomic theory.',
    audioDevanagari: 'परमाणु द्व्यणुक त्र्यणुक',
    tip: 'Try the Paramāṇu Builder in the Vijñāna Lab and join two paramāṇus yourself.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'paramanu',
  },
  {
    id: 'qa-vaisesika-sutra-4-1-1',
    category: 'philosophy',
    question: 'What do Vaiśeṣika Sūtra 4.1.1 (sadakāraṇavan nityam) and 4.1.2 (tasya kāryaṃ liṅgam) mean?',
    sanskritQuestion: 'सदकारणवन्नित्यम्, तस्य कार्यं लिङ्गम् इत्यनयोः कः अर्थः?',
    shortAnswer: 'Two sūtras of Sage Kaṇāda. 4.1.1, सदकारणवन्नित्यम् (sad-akāraṇavan-nityam): “That which is existent and yet uncaused is eternal.” 4.1.2, तस्य कार्यं लिङ्गम् (tasya kāryaṃ liṅgam): “Its effect is the evidence (of its existence).”',
    detailedAnswer: 'Word by word, 4.1.1: sat = existent/real; akāraṇavat = uncaused (a- “not” + kāraṇa “cause” + -vat “having”); nityam = eternal. A clay pot is caused (by clay), so it is temporary; the paramāṇu exists but is not made from any prior part, so in Vaiśeṣika it is eternal. 4.1.2: tasya = of that (the paramāṇu); kāryam = effect/product; liṅgam = mark/sign/evidence. Single paramāṇus are invisible; visible effects such as a pot, cloth or mountain are the inferential proof that they exist. This is the Vaiśeṣika view, not modern physics: modern atoms are divisible. The Paramāṇu Builder’s ākāśa-sūtra board cites 4.1.1 at the opening and clay-pot steps and 4.1.2 at the inference steps; the Veil of Māyā Matrix walkthrough has a Kaṇāda’s Sūtras card tying 4.1.1 to Sat and 4.1.2 to the Viveka scanner.',
    audioDevanagari: 'सदकारणवन्नित्यम् । तस्य कार्यं लिङ्गम् ।',
    tip: 'Open the Paramāṇu Builder and play the ākāśa-sūtra board; the sūtra card appears at steps 1, 2, 4, 6 and 14.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'paramanu',
  },
  {
    id: 'qa-lab-prakriti',
    category: 'philosophy',
    question: 'What are vāta, pitta and kapha, and how does the Prakṛti Biosphere use them?',
    sanskritQuestion: 'वात-पित्त-कफाः के?',
    shortAnswer: 'They are the three doṣas of Āyurveda: vāta (vāyu + ākāśa), pitta (tejas + ap) and kapha (pṛthivī + ap). The game asks you to bring all three into sama (balance).',
    detailedAnswer: 'Vāta is described as dry, cold, light and mobile; pitta as hot and sharp; kapha as heavy, cold, moist and stable. In the Prakṛti Biosphere you change season, heat, cold, dryness, moisture and activity and watch the three gauges move. It also has a bhūta cell map, clearly marked as a teaching analogy. It is a traditional way of describing balance and a learning game, not medical advice.',
    audioDevanagari: 'वात पित्त कफ',
    tip: 'Open the Prakṛti Biosphere and try the “damp, rainy, lazy day” scenario.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'prakriti',
  },
  {
    id: 'qa-lab-jyotisha',
    category: 'philosophy',
    question: 'How many nakṣatras does the Moon pass through in one orbit?',
    sanskritQuestion: 'चन्द्रः कति नक्षत्राणि गच्छति?',
    shortAnswer: 'All 27: Candra goes round the starry sky in about 27.3 days (a sidereal month), so roughly one nakṣatra per day.',
    detailedAnswer: 'The 27 nakṣatras run from Aśvinī to Revatī. In the Jyotiṣa Gravity Sandbox, Sūrya, Pṛthivī and Candra move under real Newtonian gravity. You can change masses and Pṛthivī’s starting speed: too slow and it falls in, faster than about √2 times circular speed and it escapes. The panel also lists the graha names; Rāhu and Ketu are the lunar nodes, points rather than bodies.',
    audioDevanagari: 'नक्षत्र चन्द्र सूर्य पृथिवी',
    tip: 'Run the Jyotiṣa Gravity Sandbox and watch the “Nakṣatras crossed” counter.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'jyotisha',
  },
  {
    id: 'qa-lab-srishti',
    category: 'philosophy',
    question: 'What is The Loom of Āruṇi (Sṛṣṭi · Sthiti · Laya)?',
    sanskritQuestion: 'सृष्टि-स्थिति-लयाः के?',
    shortAnswer: 'A four-step quest: gather a scattered cloud (sṛṣṭi), hold it steady (sthiti), then let it dissolve (laya), with Sage Uddālaka Āruṇi’s 4 empirical experiments from Chāndogya Upaniṣad 6.',
    detailedAnswer: 'Sṛṣṭi–sthiti–laya is the cosmological cycle of emergence, sustained equilibrium (ṛta), and absorption. In Chāndogya Upaniṣad chapter 6, sage Uddālaka Āruṇi teaches his son Śvetaketu that Being (Sat) alone was in the beginning, rejecting creation from nothing (katham asataḥ saj jāyeta). He demonstrates reality through 4 empirical labs: the clay/gold substratum, the dissolution of salt in water, the banyan seed cleavage, and a 15-day fasting test demonstrating that consciousness is biologically nourished by food. The Mahāvākya "Tat Tvam Asi" crowns this empirical realization of universal oneness. Aruni is historically credited as one of the first thinkers in human history to propose an atomic theory of matter and the concept of conservation of matter. He famously taught the core premise that complex matter is built from microscopic constituent parts (atoms/particles) that cannot be created or destroyed, perfectly aligning with the game\'s objective: "atom count stays the same."',
    audioDevanagari: 'सृष्टि स्थिति लय उद्दालक आरुणि तत्त्वमसि',
    tip: 'Switch between the Cosmic Wave Loom and Āruṇi’s 4 Empirical Labs in the Vijñāna Lab.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'srishti-sthiti-laya',
  },
  {
    id: 'qa-uddalaka-fasting-neuro',
    category: 'philosophy',
    question: 'How did Sage Uddālaka Āruṇi prove the human mind is biological via a 15-day fast?',
    sanskritQuestion: 'पञ्चदशाह-उपवासेन कथं मनः अन्नमयं सिद्धम्?',
    shortAnswer: 'Śvetaketu fasted for 15 days (drinking only water). His memory collapsed until he could not recite a single Vedic verse. Upon eating rice on day 16, his memory rushed back, proving "Anna-mayaṃ hi saumya manaḥ".',
    detailedAnswer: 'In Chāndogya Upaniṣad 6.7, Uddālaka explains that man possesses 16 cognitive faculties (Kalās). Life-breath (Prāṇa) is nourished by water, but the mind (Manas) is nourished by food (Anna). During the 15-day food fast, Śvetaketu retained breath by drinking water, but his memory and intellect broke down completely. When he ate warm food on day 16, food parted into three: coarsest into waste, middle into flesh, and the subtlest essence (aṇiṣṭha) rekindled his brain. This is humanity’s earliest recorded empirical neuroscience experiment.',
    audioDevanagari: 'अन्नमयं हि सोम्य मन आपोमयः प्राणः',
    tip: 'Try the 15-day fasting slider in Sector 4 of the Science Lab to see Śvetaketu’s 16 Kalās collapse and restore!',
    relatedView: 'science-lab',
    relatedLabAnchor: 'srishti-sthiti-laya',
  },
  {
    id: 'qa-tat-tvam-asi-experiments',
    category: 'philosophy',
    question: 'What do the Salt-in-Water and Banyan Seed experiments prove in Chāndogya 6?',
    sanskritQuestion: 'लवण-न्यग्रोध-दृष्टान्तैः किं सिद्ध्यति?',
    shortAnswer: 'They demonstrate that invisible does not mean absent. Subtle reality (Sat / Aṇiman) permeates every drop and commands all manifest growth: "Tat Tvam Asi" (That Thou Art).',
    detailedAnswer: 'In the salt experiment (6.13), salt dissolved in water is invisible to the eyes, yet sipping from the surface, middle, or bottom tastes 100% salty—proving that subtle Being (Sat) invisibly saturates every atom of existence. In the banyan seed test (6.12), breaking a tiny seed reveals apparent "nothingness" (Na kiñcana), yet from that exact unmanifest subtle potency (Aṇiman), a colossal 100-foot multi-rooted banyan tree arises. Both experiments prove that manifest diversity shares an uncreated, all-pervading subtle essence.',
    audioDevanagari: 'स य एषोऽणिमैतदात्म्यमिदं सर्वं तत्सत्यं स आत्मा तत्त्वमसि',
    tip: 'Sample the beaker at 3 depths and cleave the seed in the Vijñāna Lab.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'srishti-sthiti-laya',
  },
  {
    id: 'qa-lab-nada-brahman',
    category: 'philosophy',
    question: 'What is nāda-brahman, and what is Cosmic Sitār Tuning?',
    sanskritQuestion: 'नादब्रह्म किम्?',
    shortAnswer: 'Nāda-brahman is the idea of ultimate reality as sound, found in later yoga, music and tantra texts such as Śārṅgadeva’s Saṅgīta-ratnākara (13th century). Cosmic Sitār Tuning lets you play with a real vibrating string.',
    detailedAnswer: 'The Saṅgīta-ratnākara praises nāda and names two kinds: āhata (struck sound, like a plucked string) and anāhata (unstruck sound). Much older, the Upaniṣads treat praṇava, the syllable oṃ, as the primal sound; the Māṇḍūkya Upaniṣad opens “om ity etad akṣaram idaṃ sarvam”. In the lab, a string vibrates as a standing wave between two fixed ends. Harmonic buttons n = 1 to 5 carry the five Sāṅkhya tanmātras (śabda, sparśa, rūpa, rasa, gandha) with their mahābhūtas (ākāśa, vāyu, tejas, ap, pṛthivī), and the frequency is the real f = n/(2L)·√(T/μ). The quest is to match three target notes by tuning the tension and choosing a harmonic. String theory, a modern and unproven idea, appears only in a “modern comparison (for fun)” box; the texts do not describe strings or extra dimensions.',
    audioDevanagari: 'नादब्रह्म आहत अनाहत प्रणव',
    tip: 'Open the lab, keep n = 1 and tighten the string until it reaches 110 Hz.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'nada-brahman',
  },
  {
    id: 'qa-basic-workbook',
    category: 'pronunciation',
    question: 'Is there a beginner Sanskrit workbook I can preview, download and print?',
    sanskritQuestion: 'किं निःशुल्का प्रारम्भिक-अभ्यास-पुस्तिका अस्ति?',
    shortAnswer: 'Yes. The 28-page A4 Beginner Workbook is free to preview online (every page) in the workbook previews on the homepage and the Worksheets page; the printable PDF download is for members.',
    detailedAnswer: 'The beginner workbook has a cover and how-to page; the vowels (svara) अ to अः with transliteration and tracing boxes; the consonants (vyañjana) grouped by varga, plus य र ल व, श ष स ह and क्ष त्र ज्ञ; the vowel signs (mātrā) on क (का कि की … कः); 15 first words such as गजः (elephant), फलम् (fruit) and जलम् (water); numbers एकम् to दश with Sanskrit digits १–१०; greetings such as नमस्ते, सुप्रभातम्, मम नाम … and धन्यवादः; small exercises (match, fill in the blank, write in Devanagari); colours (रक्तः, नीलः …); body parts (शिरः, नेत्रम्, हस्तः …); animals and birds (सिंहः, धेनुः, मयूरः …); family words (माता, पिता, भ्राता, भगिनी …); the days of the week from रविवासरः to शनिवासरः; this and that (सः, सा, तत्, एषः, एषा, एतत्); अहं पठामि, त्वं पठसि, सः पठति (laṭ, singular); 12 action words such as गच्छति, खादति, पिबति, लिखति; the Hitopadeśa subhāṣita उद्यमेन हि सिध्यन्ति कार्याणि न मनोरथैः with meaning; a word search; a 20-mark review test; a certificate; and an answer key. Do one page a day.',
    audioDevanagari: 'नमस्ते',
    tip: 'Print it, trace the grey letters, then check the answer key on the last page.',
    relatedView: 'worksheets',
  },
  {
    id: 'qa-nature-workbooks',
    category: 'pronunciation',
    question: 'Is there a Sanskrit workbook about animals or plants?',
    sanskritQuestion: 'किं पशूनां वृक्षाणां च संस्कृत-अभ्यास-पुस्तिका अस्ति?',
    shortAnswer: 'Yes. Two Nature Workbooks, free to preview online (download for members), are in the workbook previews on the homepage and the Worksheets page: Animals · पशवः पक्षिणश्च (25 pages) and Indian Plants · वृक्षाः ओषधयश्च (24 pages).',
    detailedAnswer: 'The Animals workbook covers domestic and wild animals, birds, water creatures and insects with cute pictures, young ones and female forms (with a cow and calf painted in Odisha Pattachitra style), animal sounds (सिंहः गर्जति, गजः बृंहति), simple sentences, the vāhanas of the deities (मूषकः, मयूरः, नन्दी, गरुडः, हंसः, सिंहः, उलूकः), the Pañcatantra story of the lion and the hare in simple Sanskrit, the subhāṣitas उद्यमेन हि सिध्यन्ति… and काकः कृष्णः पिकः कृष्णः…, a word search and a colouring page. The Indian Plants workbook covers plant parts (मूलम्, काण्डः, पर्णम्, पुष्पम्, फलम्, बीजम्), sacred and Indian trees such as तुलसी, वटः, अश्वत्थः, निम्बः, आम्रः, बिल्वः, कमलम्, शमी and दूर्वा with English and botanical names and their traditional and cultural uses, a four-page Flowers · पुष्पाणि section (कमलम्/पद्मम्, उत्पलम्, जपा, मल्लिका, जातिः, कुन्दम्, चम्पकः, पारिजातः, केतकी, अशोकः, करवीरः, बकुलः) with a match, trace-and-write and colouring activity, spices and grains, the ślokas मूलतो ब्रह्मरूपाय… and यन्मूले सर्वतीर्थानि…, a grow-a-tulasī log, a leaf match and a colouring page. Both have answer keys.',
    audioDevanagari: 'पशवः पक्षिणश्च',
    tip: 'Keep the tulasī log for four weeks and write one new Sanskrit word each day.',
    relatedView: 'worksheets',
  },
  {
    id: 'qa-flowers-sanskrit',
    category: 'pronunciation',
    question: 'What are the Sanskrit names of Indian flowers like lotus, hibiscus and jasmine?',
    sanskritQuestion: 'पुष्पाणां संस्कृतनामानि कानि?',
    shortAnswer: 'Lotus is कमलम् or पद्मम्, hibiscus is जपा, Arabian jasmine is मल्लिका and night jasmine is पारिजातः. The Indian Plants Workbook has a four-page Flowers · पुष्पाणि section with 12 flowers.',
    detailedAnswer: 'The Flowers · पुष्पाणि section of the Indian Plants Workbook (free preview online, PDF for members) shows 12 flowers with pictures, transliteration, English and botanical names: कमलम् · पद्मम् (lotus, Nelumbo nucifera), उत्पलम् (blue water-lily, Nymphaea nouchali), जपा (hibiscus), मल्लिका (Arabian jasmine, Jasminum sambac), जातिः (jasmine, Jasminum grandiflorum), कुन्दम् (star jasmine, Jasminum multiflorum), चम्पकः (champak, Magnolia champaca), पारिजातः (night jasmine, Nyctanthes arbor-tristis), केतकी (screw-pine flower, Pandanus odorifer), अशोकः (Saraca asoca), करवीरः (oleander) and बकुलः (bakul, Mimusops elengi). Flower words: पुष्पम् / कुसुमम् (flower), कलिका (bud), दलम् (petal), मकरन्दः (nectar), परागः (pollen), माला (garland), सुगन्धः (fragrance), उद्यानम् (garden), भ्रमरः (bee). Activities: match the picture, trace and write, colour the flowers, and make a garland, with answers in the answer key.',
    audioDevanagari: 'पुष्पाणि',
    tip: 'Say each flower name aloud, then trace it: कमलम्, जपा, मल्लिका.',
    relatedView: 'worksheets',
  },
  {
    id: 'qa-festival-workbook',
    category: 'pronunciation',
    question: 'Is there a Sanskrit workbook about Indian festivals and seasons?',
    sanskritQuestion: 'किम् उत्सवानां ऋतूनां च संस्कृत-अभ्यास-पुस्तिका अस्ति?',
    shortAnswer: 'Yes. The 34-page A4 Festival Workbook is free to preview online (every page) on the homepage and the Worksheets page; the printable PDF download is for members.',
    detailedAnswer: 'The festival workbook has two pages for each festival: a short intro with 8–10 Sanskrit words, then a śloka with its meaning and small exercises. Dīpāvalī: दीपः, प्रकाशः, रङ्गवल्ली, लक्ष्मीपूजा and शुभं करोति कल्याणम्…; Gaṇeśa Caturthī: मोदकः, मूषकः, एकदन्तः and वक्रतुण्ड महाकाय…; Navarātri: या देवी सर्वभूतेषु… and गरबा; Dasarā / Vijayadaśamī: शमीवृक्षः, आयुधपूजा, विद्यारम्भः and शमी शमयते पापम्…; Oṇam: महाबलिः, वामनः, पुष्परङ्गवल्ली (pookkalam) and सर्वे भवन्तु सुखिनः…; Makara Saṅkrānti: उत्तरायणम्, तिलगुडम्, पतङ्गः and the Sūrya-namaskāra names; Holi: होलिका, प्रह्लादः and असतो मा सद्गमय…; Mahāśivarātri: बिल्वपत्रम्, जागरणम्, ॐ नमः शिवाय and कर्पूरगौरं करुणावतारम्…; Rakṣābandhana: रक्षा, सूत्रम् and येन बद्धो बली राजा…; Rāma Navamī: श्रीराम राम रामेति…; Kṛṣṇa Janmāṣṭamī: वेणुः, नवनीतम्, दधिभाण्डम् and वसुदेवसुतं देवम्…. A festive adornment section (शृङ्गारः / अलङ्कारः) names शाटी, बिन्दुः, कज्जलम्, कुण्डलम्, हारः, कङ्कणम्, नूपुरम्, मेन्धिका and वेणी, with a label-the-picture exercise and a colour-the-rangoli page. A seasons section covers the six ṛtus with their lunar months: वसन्तः (Caitra–Vaiśākha), ग्रीष्मः (Jyeṣṭha–Āṣāḍha), वर्षाः (Śrāvaṇa–Bhādrapada), शरत् (Āśvina–Kārtika), हेमन्तः (Mārgaśīrṣa–Pauṣa) and शिशिरः (Māgha–Phālguna), with festivals and nature words, and a months page lists चैत्रः, वैशाखः, ज्येष्ठः, आषाढः, श्रावणः, भाद्रपदः, आश्विनः, कार्तिकः, मार्गशीर्षः, पौषः, माघः and फाल्गुनः with their ṛtu and main festival, plus the 12 solar (rāśi) months from मेषः to मीनः. It ends with a mixed quiz, festival greetings such as दीपावलीशुभाशयाः and an answer key.',
    audioDevanagari: 'दीपावलीशुभाशयाः',
    tip: 'Print one festival at a time and learn its śloka line by line.',
    relatedView: 'worksheets',
  },
  {
    id: 'qa-grammar-workbooks',
    category: 'grammar',
    question: 'Are there Sanskrit grammar workbooks I can preview and print?',
    sanskritQuestion: 'किं निःशुल्काः व्याकरण-अभ्यास-पुस्तिकाः सन्ति?',
    shortAnswer: 'Yes: three graded Grammar Workbooks (Levels 1–3) with exercises and answer keys, free to preview online (download for members), on the homepage and the Worksheets page.',
    detailedAnswer: 'Level 1 Basics (22 pages) covers gender and number, the seven cases with simple uses, the full tables of राम (रामः रामौ रामाः … रामेषु), लता and फल, the pronouns अस्मद्, युष्मद् and तद्, the present tense (laṭ) of पठ् and गम्, avyayas such as च, अपि, कुत्र, अत्र, तत्र, कदा, and sentence building. Level 2 Intermediate (21 pages) covers laṅ (अपठत्), lṛṭ (पठिष्यति), loṭ (पठतु) and vidhiliṅ (पठेत्), svara sandhi (dīrgha, guṇa, vṛddhi, yaṇ) and visarga sandhi, ktvā and lyap (पठित्वा, आगत्य), tumun (पठितुम्), kta (पठितः), the upasargas and number words. Level 3 Advanced (22 pages) covers samāsa types with vigraha, vyañjana sandhi, active and passive voice (रामेण ग्रन्थः पठ्यते), parasmaipada and ātmanepada (सेवते), śatṛ and śānac participles (पठन्, वर्धमानः), and the analysis of two subhāṣitas.',
    audioDevanagari: 'रामः रामौ रामाः',
    tip: 'Start with Level 1 and recite each table aloud before filling in the practice pages.',
    relatedView: 'worksheets',
  },
  {
    id: 'qa-lab-asato-ma',
    category: 'philosophy',
    question: 'What does “asato mā sad gamaya” mean, and what is The Veil of Māyā lab?',
    sanskritQuestion: 'असतो मा सद्गमय इत्यस्य कः अर्थः?',
    shortAnswer: 'It is a prayer from the Bṛhadāraṇyaka Upaniṣad (1.3.28): lead me from the unreal to the real, from darkness to light, from death to immortality. The Upaniṣad itself explains asat as death and sat as immortality.',
    detailedAnswer: 'Right after the three lines, the Upaniṣad glosses them: “mṛtyur vā asat, sad amṛtam” (the unreal is death, the real is immortality) and “mṛtyur vai tamo, jyotir amṛtam” (darkness is death, light is immortality); of the third line it says the meaning is plain. In the lab, a playful Asat/Sat switch (the Viveka Scanner) moves from the everyday look of gold, wood, water or ice to its atoms. You heat gold until it glows (approximate colour) and burn a log while the C, H and O counts stay the same: C₆H₁₀O₅ + 6 O₂ → 6 CO₂ + 5 H₂O. Māyā and avidyā are ideas from later Vedānta and get their own card (Lore Terminal Beta), kept apart from the Upaniṣad’s gloss (Lore Terminal Alpha); the Matrix Overwatch panel is a living two-mass spring lattice where you switch between acoustic (neighbours in phase) and optical (heavy and light atoms out of phase) vibrations, the two real kinds of phonon; links to conservation of mass or quantum physics sit in a “modern comparison (for fun)” box. A short Matrix walkthrough (for fun), clearly labelled as a teaching analogy, runs in three levels: Level 01, many forms, one reality: what we see looks different only because of different names and forms (nāma-rūpa, नाम-रूप), but underneath it is one uniform, unchanging reality (sat, सत्), just as a pot, a plate and a lamp are different forms yet all are clay; the Sealed Crucible (Andha-Mūṣā), where water at 281 K (7 °C) looks calm but its molecules move at about 623 m/s (v_rms) when you switch on the Viveka Scanner; and the Phonon Lattice at 2,252 K, where bonds break past the Lindemann melt limit, likened to Neo bending the spoon. The walkthrough now animates each level (colourful falling letters, pots, cloths, trees, plates and lamps that, with Viveka on, dissolve and settle into one calm, even, unmoving field of the same सत् with a large glowing सत् in the centre, a scan line over calm water revealing about 60 colliding molecules with a live v_rms readout, and a lattice with a 281–2,252 K slider where 29 bonds snap and stability falls to 0%, plus a “bend the spoon” moment). A short Codex frames Māyā as reality versus illusion: Sat (सत्) is what is truly real and unchanging, Asat (असत्) the changing appearance, and Māyā (माया) the power that makes appearance look like the whole of reality. In Ādi Śaṅkarācārya’s Advaita, the rope-and-snake example (rajju-sarpa, रज्जु-सर्प) is not merely one object appearing as another: in dim light an object with coiled (covered-up) information appears as a snake, but when the light of viveka arrives, it unravels into an unraveled, uniform, repetitive unit-based object like a braided rope. In the lab, macroscopic surface appearance is the coiled snake with covered-up information, and the colliding molecules in their uniform lattice are the unraveled rope.',
    audioDevanagari: 'असतो मा सद्गमय तमसो मा ज्योतिर्गमय मृत्योर्मा अमृतं गमय',
    tip: 'Open the lab, switch to Sat, then try melting ice past 273 K.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'asato-ma',
  },
  {
    id: 'qa-lab-chaturyoni',
    category: 'philosophy',
    question: 'What is Chaturyoni (चतुर्योनि), the four kinds of birth?',
    sanskritQuestion: 'चतुर्योनिः का?',
    shortAnswer: 'An old fourfold way of grouping living beings by how they come into being: jarāyuja (born from a womb), aṇḍaja (from an egg), svedaja (from moisture and heat) and udbhijja (sprouting from the earth). The four are listed in Manusmṛti 1.43–46 and named in Aitareya Upaniṣad 3.3; the Chāndogya Upaniṣad (6.3.1) gives only three.',
    detailedAnswer: 'Manusmṛti 1.43 puts domestic and wild animals and humans among the womb-born; 1.44 puts birds, snakes, crocodiles, fish and tortoises among the egg-born; 1.45 says gnats, mosquitoes, lice, flies and bugs are sweat-born, arising from heat; 1.46 says all stationary (sthāvara) plants are udbhijja. The Chāndogya Upaniṣad (6.3.1) names only three seeds: āṇḍaja, jīvaja and udbhijja. Modern check: insects hatch from eggs; life does not arise on its own from sweat or heat (Redi 1668, Pasteur 1859–62). In the Chaturyoni Spawn Map you match specimens to the four groups, switch to Sthāvara / Jaṅgama (stationary or moving), and fill a plant shelf with Manusmṛti’s vanaspati (fruit without visible flowers, 1.47; figs fit, their tiny flowers hide inside the fruit), oṣadhi, creepers and grasses. Each specimen shows its Sanskrit taxonomy (e.g. jaṅgama → jarāyuja) beside its Linnaean counterpart (e.g. class Mammalia), and each quadrant has field notes with Manu’s own examples. A “modern comparison (for fun)” box sets the old groups beside today’s biology, which classifies by shared ancestry.',
    audioDevanagari: 'जरायुज अण्डज स्वेदज उद्भिज्ज',
    tip: 'Open the lab, pick the mosquito, and see what the modern check says.',
    relatedView: 'science-lab',
        relatedLabAnchor: 'chaturyoni',
  },
  {
    id: 'qa-lab-chandrayaan',
    category: 'engineering',
    question: 'What is Somayāna Mission-03 / the Chandrayaan lab?',
    sanskritQuestion: 'सोमयानं किम्?',
    shortAnswer: 'A free lunar-landing sandbox at /science-lab#chandrayaan: circularise orbit, soft-land Vikram, drive Pragyan and LIBS-scan minerals with Sanskrit HUD labels.',
    detailedAnswer: 'Three phases. Vyoma Insertion: burn Prāṇodana to match a ~100 km Candra-Kakṣyā. Vikram Descent: land near the south pole with lunar g ≈ 1.62 m/s²; soft-land if vertical Vega is under 2 m/s (inside ISRO’s Chandrayaan-3 touchdown spec of ≤ 2 m/s) and Samatva is within ±12° — otherwise a crash interrupt (विक्षेप दोषः) with a recalibration checklist. An optional Guidance assist adds a little braking in the last 30 m but never flies for you. Pragyan Probe: drive the rover (real Pragyan moved about 1 cm/s) while the Tithi sets the Sūrya window at the site, leave wheel tracks past Vikram, and fire a LIBS-style scan at glowing element spots to collect Sanskrit-named data shards. Pragyan’s real LIBS confirmed sulphur near the south pole (ISRO, 28 Aug 2023) and reported other elements; hydrogen/ice investigation was described as underway — the lab tags ap-tattva as a hunt, not as a confirmed ice find. Soft landing was 23 Aug 2023; the site was named Shiv Shakti Point. Sanskrit HUD words are teaching parallels, not a claim that Jyotiṣa secretly contains rocket equations.',
    audioDevanagari: 'सोमयानं विक्रमः प्रज्ञानम्',
    tip: 'Circularise first, then open Vikram Descent. Soft-land under 2 m/s vertical.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'chandrayaan',
  },
  {
    id: 'qa-lab-rituparna',
    category: 'philosophy',
    question: 'What is King Rituparna’s Sampling Lab, and how does it relate to statistics?',
    sanskritQuestion: 'ऋतुपर्णस्य साङ्ख्यान-विधिः कः?',
    shortAnswer: 'The Mahābhārata’s Nalopākhyāna records humanity’s earliest documented statistical sampling: estimating canopy leaves and fruits from a counted sample branch.',
    detailedAnswer: 'In the Mahābhārata (Vana Parva), King Rituparna encounters a massive Vibhītaka tree and calculates a remarkably close statistical approximation of its total leaf and nut count by counting a single branch and multiplying by the canopy volume ratio. Prince Nala fells the tree and meticulously counts every fruit, discovering Rituparna’s estimate was indeed a remarkably close statistical approximation! Rituparna explicitly credits Saṅkhyāna (quantitative calculation and empirical sampling) and links it directly to dice game probabilities (Akṣa-vidyā). In parallel, Kautilya’s Arthaśāstra conducted systematic census and tiered risk management, while Pingala’s Chandaḥśāstra formulated binary combinatorics (Meruprastāra / Pascal’s triangle).',
    audioDevanagari: 'ऋतुपर्ण साङ्ख्यान विभीतक अक्षविद्या नल',
    tip: 'Try the branch multiplier in the Rituparna Sampling Lab to see sample error drop as sample size grows.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'rituparna',
  },
  {
    id: 'qa-lab-puranic-astronomy',
    category: 'philosophy',
    question: 'What is the Purāṇic mode in Jyotiṣa (Śiśumāra, Sūrya’s chariot, and Dhruva)?',
    sanskritQuestion: 'पौराणिक-ज्योतिष-कल्पना का?',
    shortAnswer: 'Purāṇas like the Bhāgavata (5.21–23) and Viṣṇu Purāṇa employ allegorical cosmography: the starry dome as the cosmic porpoise Śiśumāra, anchored to Dhruva (the Pole Star) by Pravaha winds.',
    detailedAnswer: 'In Bhāgavata Purāṇa 5.23, the revolving stellar vault is visualised as a celestial dolphin/porpoise (Śiśumāra-saṃsthāna) with the Pole Star (Dhruva) at the tip of its tail, turning all heavenly bodies via invisible cords of wind (Pravaha-vāyu). Sūrya’s single-wheeled chariot (representing the 1-year cycle) is drawn by seven horses named after Vedic metres (Gāyatrī, Bṛhatī, Uṣṇik, Jagaī, Triṣṭubh, Anuṣṭubh, Paṅkti) — allegorising the 7 spectral wavelengths of light. Rāhu and Ketu represent the astronomical lunar ascending and descending nodes that produce solar and lunar eclipses.',
    audioDevanagari: 'शिशुमार ध्रुव प्रवाहमण्डल सूर्य सप्तच्छन्द',
    tip: 'Toggle "Purāṇic Mode" in the Jyotiṣa Orbit sandbox to see the Śiśumāra arc and Sūrya’s 7 metre-horses.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'jyotisha',
  },
  {
    id: 'qa-lab-ayurveda-attractions',
    category: 'philosophy',
    question: 'How do Prakṛti, Dinacaryā, and the Six Tastes (Ṣaḍ-Rasa) govern health?',
    sanskritQuestion: 'प्रकृति-दिनचर्या-षड्रसाः कथं वर्तन्ते?',
    shortAnswer: 'Caraka and Suśruta teach that every person has a unique baseline constitution (Prakṛti), governed by daily circadian rhythms (Dinacaryā) and balanced through the 6 Rasas.',
    detailedAnswer: 'Ayurveda identifies three biological energies (Doṣas): Vāta (wind/space), Pitta (fire/water), and Kapha (water/earth). The 24-hour day naturally peaks in each doṣa twice (Kapha in morning & early night, Pitta at midday & midnight, Vāta in dawn & late afternoon). The six tastes (Madhura, Amla, Lavaṇa, Kaṭu, Tikta, Kaṣāya) either aggravate or pacify these doṣas. Balancing your daily routine (Dinacaryā) and diet restores "Samadoṣaḥ samāgniś ca samadhātumalakriyaḥ" — the state of holistic health (Svāsthya).',
    audioDevanagari: 'वात पित्त कफ दिनचर्या षड्रस स्वास्थ्य',
    tip: 'Check out the Prakṛti Diagnostic, 24h Dinacaryā Clock, and Ṣaḍ-Rasa Balancer inside the Prakṛti Biosphere.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'prakriti',
  },
  {
    id: 'qa-lab-agnibana-fireworks',
    category: 'engineering',
    question: 'How did Indian fireworks and rockets evolve from Deepavali Ulkā-Dāna to Mysorean war rocketry?',
    sanskritQuestion: 'अग्निबाणानां दीपावली-उल्कादानानां च विकासः कथम् अभवत्?',
    shortAnswer: 'From 12th-century Hoysaleśvara Agnibāṇa temple reliefs and the Skanda Purāṇa’s Deepavali Ulkā-Dāna to the 5:1:1 Śukranīti formula and Mysore’s hammered iron rockets that defeated British forces at Pollilur (1780).',
    detailedAnswer: 'Deepavali historically evolved from silent clay oil lamps (diyas) into festive pyrotechnics through the medieval practice of Ulkā-Dāna (offering of handheld fire tubes to illuminate the heavens for ancestral spirits/Pitṛs, as recorded in the Skanda Purāṇa). In Sanskrit treatises like the Śukranīti, Agnicūrṇa (fire-powder) is formulated using a 5:1:1 stoichiometric ratio: 5 parts Suvarcilavaṇa (KNO₃ oxidizer), 1 part Gandhaka (sulphur igniter), and 1 part Aṅgāra (porous charcoal from Arka/Calotropis wood). In the 18th century, Hyder Ali and Tipu Sultan replaced rupturing bamboo with hammered iron casings that sustained 80 atm chamber pressure, stretching range to 2.5 km. Captured specimens sent to London’s Woolwich Arsenal were reverse-engineered by William Congreve (inspiring the "rockets red glare" in the US Anthem), and this high-pressure metallic casing logic directly informs modern ISRO solid propellant boosters.',
    audioDevanagari: 'अग्निबाण उल्कादान सुवर्चिलवण गन्धक अङ्गार मैसूर',
    tip: 'Open the Agnibāṇa Lab to test the 5:1:1 chemical forge, launch bamboo vs iron casings, and detonate Kautuka Cintāmaṇi fireworks in the night sky.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'agnibana',
  },
  {
    id: 'qa-kautuka-cintamani-fireworks',
    category: 'philosophy',
    question: 'What are the historical Sanskrit fireworks named in the Kautuka Cintāmaṇi?',
    sanskritQuestion: 'कौतुकचिन्तामणौ के के अग्निक्रीडा-भेदाः वर्णिताः?',
    shortAnswer: 'Authored by King Gajapati Pratāparudradeva of Odisha (c. 1500 CE), it describes Candrajyoti (moonlight flare), Chuchundarī (mole squib), Cāmarabāṇa (whisk fountain), and Puṣpavarti (sparkler).',
    detailedAnswer: 'King Gajapati Pratāparudradeva of Odisha (1497–1539 CE) catalogued distinct pyrotechnic formulas in Kautuka Cintāmaṇi: Candrajyoti (bright silvery flare resembling moonlight), Chuchundarī-rasabāṇa (erratic ground-zipping mole squib), Cāmarabāṇa (tall golden shower of sparks shaped like the royal yak-tail whisk), and Puṣpavarti ("flower wick", the direct ancestor of modern handheld phooljhadi sparklers). Foreign travelers like Abdur Razzaq (1443 CE, Vijayanagara) and Ludovico di Varthema (c. 1500 CE) described south Indian artisans as master pyrotechnicians.',
    audioDevanagari: 'कौतुकचिन्तामणि चन्द्रज्योति छुछुन्दरी चामरबाण पुष्पवर्ति',
    tip: 'Launch Candrajyoti and Puṣpavarti in the Deepavali night sky canvas inside the Agnibāṇa lab.',
    relatedView: 'science-lab',
    relatedLabAnchor: 'agnibana',
  },
  {
    id: 'qa-spheres-of-certainty-indus-metrology',
    category: 'vedic_math',
    question: 'What do the 3.5 cm terracotta spheres of Bhirrana and Mohenjo-daro prove about Indus metrology?',
    sanskritQuestion: '॥ प्रमाण-गोलकौ · पञ्च-सहस्र-वर्ष-मानशास्त्रम् ॥',
    shortAnswer: 'They prove a 5,000-year continuous civilization standard of exactly 3.5 cm (2 Indus Angulas on the Lothal ivory scale) across distant eras and regions.',
    detailedAnswer: 'Two excavated terracotta spheres separated by hundreds of miles and millennia—the early-horizon perforated sphere from Bhirrana (Haryana) and the mature-horizon spiral-incised sphere from Mohenjo-daro (Sindh)—both measure precisely 3.5 cm in diameter. This 3.5 cm diameter equals exactly 2 Indus Aṅgulas (1.763 cm per Aṅgula, as verified on the calibrated Lothal ivory scale). The same 16-Aṅgula ratio (28.2 cm) defines Harappan architectural baked bricks, while standardized binary chert stone weights anchored on 13.63 g (16 annas / karṣa) calibrated commercial trade and gaming track pavements.',
    audioDevanagari: 'प्रमाण-गोलकौ मानशास्त्रम् अङ्गुलम् लोथल-दन्तशलाका',
    tip: 'Explore the interactive Indus Metrology System & 3.5 cm Spherical Invariance schematic in our research articles reader.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-the-spheres-of-certainty-5000-years-indus-metrology',
    relatedLabel: '📏 Read The Spheres of Certainty Masterclass ➔',
  },
  {
    id: 'qa-terracotta-matrix-swat-bkg',
    category: 'vedic_math',
    question: 'What is the Terracotta Matrix discovered at Bīr-koṭ-ghwaṇḍai (BKG) in the Swat Valley?',
    sanskritQuestion: '॥ मृत्पात्रे रेखागणितम् मानशास्त्रं च ॥',
    shortAnswer: 'An excavated spherical terracotta whorl proving rotational symmetry, concentric equidistant division, and dynamic balancing in ancient artisan hands.',
    detailedAnswer: 'Excavated from Bīr-koṭ-ghwaṇḍai (BKG) in the Swat Valley, this spherical terracotta artifact features precise concentric bands of punched indentations. Executing this on wet clay required mechanical dividers or rotating turntables to divide curved 3D surfaces into equal spatial intervals. The central piercing aligns perfectly with the center of gravity, ensuring wobble-free dynamic rotation for spinning whorls and serving as a standardized 3D metrological matrix.',
    audioDevanagari: 'मृत्पात्रे रेखागणितम् भ्रमणाक्षः मानशास्त्रम्',
    tip: 'View the high-resolution Swat Valley artifact photography and rotating matrix schematic in the Vedic articles shelf.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-the-terracotta-matrix-metrology-clay',
    relatedLabel: '🏺 Read The Terracotta Matrix Masterclass ➔',
  },
  {
    id: 'qa-nana-moutou-le-gentil-1768',
    category: 'vedic_math',
    question: 'How did Tamil calculator Nana Moutou outpace French Royal Astronomer Guillaume Le Gentil in 1768?',
    sanskritQuestion: '॥ वाक्यकरणम् · वराटिका-यन्त्रेण ग्रहण-साधनम् (१७६८) ॥',
    shortAnswer: 'Using the Vākyakaraṇa algorithmic verses, Kaṭapayādi cipher, and cowrie-shell RAM on a dirt floor in under 45 minutes, matching the Royal Paris Ephemerides.',
    detailedAnswer:
      'In December 1768 at Pondicherry, Guillaume Le Gentil of the French Royal Academy of Sciences challenged Tamil astronomer Nana Moutou to calculate an upcoming lunar eclipse. Nana Moutou sat on the dirt floor with cowrie shells and palm-leaf booklets. Reciting mnemonic Vākyas from the Vākyakaraṇa tradition (247 Cāndravākyas formulated by Vararuci and Mādhava) encoded via the Kaṭapayādi alphanumeric cipher, he used the cowrie shells as dynamic registers (ephemeral RAM), wiping the dirt after each calculation. In under 45 minutes, he delivered the eclipse phases with minute-level precision, matching the Connaissance des Temps.',
    audioDevanagari: 'वाक्यकरणम् कटपयादि वराटिका-गणनम् ली जेन्तिल नाना मुतु',
    tip: 'Read the full masterclass comparing Western external hardware scaling with Indian internal cognitive software scaling.',
    relatedView: 'vedic-maths',
    relatedVedicAnchor: 'article-the-astronomer-and-the-astrologer-tamil-scholar-1768',
    relatedLabel: '🌌 Read The Astronomer & The Astrologer (1768) ➔',
  },
  {
    id: 'qa-sanskrit-dictionary-koshashri',
    category: 'grammar',
    question: 'How do I search words in a classical Sanskrit Dictionary like Kōshaśrī or Amarakośa?',
    sanskritQuestion: 'संस्कृत-शब्दकोशः · कोशश्री (Kōshaśrī) अमरकोशश्च',
    shortAnswer: 'Search root dhātus or prātipadikas via Kōshaśrī DC catalog, Amarakośa, or our interactive Dhātupāṭha browser.',
    detailedAnswer: 'Sanskrit lexicography organizes vocabulary by primary roots (Dhātus), prefixes (Upasargas), and nominal bases (Prātipadikas). You can look up etymological meanings in classical lexicons like Amarasimha’s Amarakośa (the world’s oldest thesaurus organized by cosmic categories), Rādhākāntadeva’s encyclopaedic Śabdakalpadruma, or search the modern digital Kōshaśrī DC catalog (koshashri-dc.ac.in). In this app, explore over 2,000 Paninian verb roots directly in our Dhātupāṭha Browser.',
    audioDevanagari: 'संस्कृत-शब्दकोशः अमरकोशः धातुपाठः',
    tip: 'Need a specific root conjugation? Open the Dhātupāṭha Browser to explore all 10 Gaṇas, Lakāras, and English equivalents.',
    relatedView: 'dhatupatha',
    relatedLabel: '📖 Open Dhātupāṭha Root Browser ➔',
  },
  {
    id: 'qa-deepakam-class7-class8-solutions',
    category: 'cbse',
    question: 'Where can I find NCERT Class 7 and Class 8 Sanskrit Deepakam solutions and interactive readers?',
    sanskritQuestion: 'दीपकम् · सप्तम-अष्टम-कक्ष्या-पाठ्यपुस्तकम्',
    shortAnswer: 'Explore our unified Deepakam Textbook Reader with physical book page mapping, word-to-word translations, audio pronunciation, quizzes, and worksheets.',
    detailedAnswer: 'Our Deepakam Textbook Reader brings NCERT Sanskrit to life for Class 7 and Class 8 students. Every chapter features explicit physical book page mapping, verse-by-verse recitation audio, word-by-word (पदच्छेद) translations, grammar breakdowns of interrogative roots (क-कार शब्दाः: किम्, कुत्र, कदा, कुतः, कथम्, किमर्थम्), interactive 10-question quizzes, and downloadable chapter practice worksheets.',
    audioDevanagari: 'दीपकम् पाठ्यपुस्तकम् शब्दार्थः अभ्यासः',
    tip: 'Tap any Sanskrit word in the Deepakam Reader to hear Bodhi speak its authentic pronunciation and reveal its grammatical role.',
    relatedView: 'reader',
    relatedLabel: '📚 Open Deepakam Textbook Reader ➔',
  },
];

export const BODHI_WORD_SUGGESTIONS: BodhiWordSuggestion[] = [
  // Tech / Neologisms
  { id: 'w-ai', devanagari: 'कृत्रिमन्त्रम्', iast: 'Kṛtrimantram', english: 'Artificial Intelligence (AI)', category: 'tech', modernConcept: 'AI / Thinking Machine', breakdown: 'कृत्रिम (artificial) + √मन् (to think) + त्र (mechanism)' },
  { id: 'w-net', devanagari: 'अन्तर्जालम्', iast: 'Antarjālam', english: 'The Internet / Cyberspace', category: 'tech', modernConcept: 'Internet', breakdown: 'अन्तर् (inter-) + जालम् (net / web)' },
  { id: 'w-phone', devanagari: 'चलदूरभाषः', iast: 'Caladūrabhāṣaḥ', english: 'Mobile Phone / Smartphone', category: 'tech', modernConcept: 'Smartphone', breakdown: 'चल (mobile) + दूर (tele) + √भाष् (to speak)' },
  { id: 'w-sat', devanagari: 'कृत्रिमोपग्रहः', iast: 'Kṛtrimopagrahaḥ', english: 'Artificial Space Satellite', category: 'tech', modernConcept: 'Satellite', breakdown: 'कृत्रिम (man-made) + उपग्रहः (satellite orbiter)' },
  { id: 'w-chain', devanagari: 'खण्डशृङ्खला', iast: 'Khaṇḍaśṛṅkhalā', english: 'Blockchain / Distributed Ledger', category: 'tech', modernConcept: 'Blockchain', breakdown: 'खण्ड (block) + शृङ्खला (interlocked chain)' },
  { id: 'w-comp', devanagari: 'सङ्गणकम्', iast: 'Saṅgaṇakam', english: 'Computer / Calculating Machine', category: 'tech', modernConcept: 'Computer', breakdown: 'सम् (well/together) + √गण् (to compute) + ल्युट् (noun suffix)' },
  { id: 'w-vr', devanagari: 'आभासीय-यथार्थता', iast: 'Ābhāsīya-Yathārthatā', english: 'Virtual Reality (VR)', category: 'tech', modernConcept: 'Virtual Reality', breakdown: 'आभास (simulated appearance) + यथार्थता (reality)' },
  { id: 'w-email', devanagari: 'विद्युत्पत्रम्', iast: 'Vidyut-patram', english: 'Electronic Mail (E-mail)', category: 'tech', modernConcept: 'Email', breakdown: 'विद्युत् (electric) + पत्रम् (letter/message)' },

  // Roots (Dhātus)
  { id: 'w-dhatu-man', devanagari: '√मन्', iast: '√man', english: 'To think, reason, ponder, calculate', category: 'root', breakdown: 'Divādi Gaṇa (मन्यते) — Root of mantra, manas, manana' },
  { id: 'w-dhatu-kr', devanagari: '√कृ', iast: '√kṛ', english: 'To do, create, make, perform', category: 'root', breakdown: 'Tanādi Gaṇa (करोति) — Root of karma, kārya, kartā, kṛtrima' },
  { id: 'w-dhatu-jna', devanagari: '√ज्ञा', iast: '√jñā', english: 'To know, understand, cognize', category: 'root', breakdown: 'Kryādi Gaṇa (जानाति) — Root of jñāna, vijñāna, saṁjñā' },
  { id: 'w-dhatu-gam', devanagari: '√गम्', iast: '√gam', english: 'To go, move, progress', category: 'root', breakdown: 'Bhavādi Gaṇa (गच्छति) — Root of gati, āgama, jaṅgama' },
  { id: 'w-dhatu-path', devanagari: '√पठ्', iast: '√paṭh', english: 'To read, study, recite aloud', category: 'root', breakdown: 'Bhavādi Gaṇa (पठति) — Root of pāṭha, pāṭhaśālā' },
  { id: 'w-dhatu-likh', devanagari: '√लिख्', iast: '√likh', english: 'To write, scratch, record, sketch', category: 'root', breakdown: 'Tudādi Gaṇa (लिखति) — Root of lekha, lipi, lekhaka' },
  { id: 'w-dhatu-drs', devanagari: '√दृश्', iast: '√dṛś', english: 'To see, perceive, realize', category: 'root', breakdown: 'Bhavādi Gaṇa (पश्यति) — Root of darśana, dṛṣṭi' },

  // Core Vocabulary & Grammar
  { id: 'w-vidya', devanagari: 'विद्या', iast: 'Vidyā', english: 'Sacred Knowledge, Science, Wisdom', category: 'vocab', breakdown: '√विद् (to know) + क्यप् + टाप् (feminine)' },
  { id: 'w-vibhakti', devanagari: 'विभक्तिः', iast: 'Vibhaktiḥ', english: 'Noun Case Inflection (7 Cases)', category: 'grammar', breakdown: 'वि + √भज् (to divide/distribute) + क्तिन्' },
  { id: 'w-sandhi', devanagari: 'सन्धिः', iast: 'Sandhiḥ', english: 'Phonological Sound Junction / Union', category: 'grammar', breakdown: 'सम् + √धा (to place together) + कि' },
  { id: 'w-samasa', devanagari: 'समासः', iast: 'Samāsaḥ', english: 'Nominal Word Compounding', category: 'grammar', breakdown: 'सम् + √अस् (to throw/merge together) + घञ्' },
  { id: 'w-sutra', devanagari: 'सूत्रम्', iast: 'Sūtram', english: 'Aphorism, Concise Algorithmic Code', category: 'grammar', breakdown: '√सिव् (to sew) + ष्ट्रन् — An unbroken thread of rules' },
  { id: 'w-visarga', devanagari: 'विसर्गः', iast: 'Visargaḥ', english: 'Aspiration Sound (ः) Mirroring Preceding Vowel', category: 'grammar', breakdown: 'वि + √सृज् (to emit/release breath) + घञ्' },
  { id: 'w-anusvara', devanagari: 'अनुस्वारः', iast: 'Anusvāraḥ', english: 'Pure Nasal Sound (ं) Following a Vowel', category: 'grammar', breakdown: 'अनु (after) + स्वर (vowel) — That which follows a vowel' },
  { id: 'w-guru', devanagari: 'गुरुः', iast: 'Guruḥ', english: 'Living Teacher, Spiritual Guide (Heavy with Wisdom)', category: 'vocab', breakdown: '√गृ (to invoke/guide) or गु (darkness) + रु (dispeller) — The living dispeller of darkness' },
  { id: 'w-parampara', devanagari: 'परम्परा', iast: 'Paramparā', english: 'Unbroken Living Lineage / Succession', category: 'vocab', breakdown: 'परम् (beyond) + परम् (higher) — From one generation of teachers to the next' },
  { id: 'w-prana', devanagari: 'प्राणः', iast: 'Prāṇaḥ', english: 'Vital Breath, Life-Force in Speech and Being', category: 'vocab', breakdown: 'प्र + √अन् (to breathe) + घञ् — The breath that binds intention to sound' },

  // Ancient Science, Mathematics & Geodesy (Ujjain School)
  { id: 'w-ujjain', devanagari: 'उज्जयिनी', iast: 'Ujjayinī', english: 'Ujjain / Ancient 0° Prime Meridian & Astronomical Capital (Avantī)', category: 'vocab', modernConcept: 'Prime Meridian / Ancient Greenwich', breakdown: 'उद् + √जि (to conquer/triumph) — The capital of cosmic space-time coordinates' },
  { id: 'w-madhya-rekha', devanagari: 'मध्यरेखा', iast: 'Madhya-Rekhā', english: 'Prime Meridian / Zero Longitude Coordinate Baseline', category: 'vocab', modernConcept: '0° Prime Meridian / Longitude Zero', breakdown: 'मध्य (central/prime) + रेखा (meridian line) — The central cosmic baseline of longitude' },
  { id: 'w-shanku', devanagari: 'शङ्कुः', iast: 'Śaṅkuḥ', english: '12-Aṅgula Vertical Gnomon for Solar Noon Shadow & Latitude', category: 'vocab', modernConcept: 'Gnomon / Latitude Shadow Meter', breakdown: 'द्वादशाङ्गुल-शङ्कुः (standardized 12-unit vertical gnomon rod) used to determine local latitude' },
  { id: 'w-deshantara', devanagari: 'देशान्तरम्', iast: 'Deśāntaram', english: 'Geographic Longitude Offset (Difference in Terrestrial Distance/Time)', category: 'vocab', modernConcept: 'Longitude Delta / Time Difference', breakdown: 'देश (place) + अन्तरम् (difference) — Local time offset measured via simultaneous lunar eclipses' },
  { id: 'w-akshamsha', devanagari: 'अक्षांशः', iast: 'Akṣāṁśaḥ', english: 'Geographic Latitude from Solar Noon Shadow', category: 'vocab', modernConcept: 'Latitude (Angular distance from Equator)', breakdown: 'अक्ष (terrestrial axis/pivot) + अंश (degree/fraction) — Calculated via tan(φ) = Palabhā / 12' },
  { id: 'w-palabha', devanagari: 'पलभा', iast: 'Palabhā', english: 'Equinoctial Noon Shadow Length (Exactly 5.14 Aṅgulas at Ujjain)', category: 'vocab', modernConcept: 'Equinoctial Noon Shadow', breakdown: 'पल (time/latitude measure) + भा (shadow) — Benchmark shadow cast by 12-angula gnomon at equinox noon' },
  { id: 'w-jya', devanagari: 'ज्या', iast: 'Jyā', english: 'Sine Function in Trigonometry (originally Ardha-jyā / half-chord)', category: 'vocab', modernConcept: 'Trigonometric Sine (sin θ)', breakdown: 'अर्ध-ज्या (half of bowstring/chord) → Arabic Jība → Latin Sinus → Modern Sine' },

  // Archaeometrology, Indus Standards & Lexicography
  { id: 'w-pramana-golaka', devanagari: 'प्रमाण-गोलकः', iast: 'Pramāṇa-Golakaḥ', english: 'Standardized 3.5 cm Metrology Sphere (Bhirrana & Mohenjo-daro)', category: 'vocab', modernConcept: '3.5 cm Indus Metrology Sphere', breakdown: 'प्रमाण (standard measure) + गोलकः (solid sphere) — Exactly 2 Indus Aṅgulas (3.5 cm)' },
  { id: 'w-angulam', devanagari: 'अङ्गुलम्', iast: 'Aṅgulam', english: 'Indus Aṅgula / Traditional Linear Measure (1.763 cm Lothal Unit)', category: 'vocab', modernConcept: '1.763 cm Indus Angula Unit', breakdown: 'Linear base unit verified on Lothal ivory scale and 16-Aṅgula (28.2 cm) bricks' },
  { id: 'w-manashastram', devanagari: 'मानशास्त्रम्', iast: 'Mānaśāstram', english: 'Science of Weights, Measures & Metrological Standards', category: 'vocab', modernConcept: 'Ancient Indian Metrology', breakdown: 'मान (measure/weight) + शास्त्रम् (systematic science) — Standardized binary chert ratios & scales' },
  { id: 'w-mritpatram', devanagari: 'मृत्पात्रम्', iast: 'Mṛtpātram', english: 'Terracotta Matrix / Swat Valley (BKG) Balanced Whorl', category: 'vocab', modernConcept: 'Terracotta Dynamic Balance & Concentric Division', breakdown: 'मृद् (clay/earth) + पात्रम् (vessel/artifact) — 3D geometric matrix from Bīr-koṭ-ghwaṇḍai' },
  { id: 'w-tulamanam', devanagari: 'तुलामानम्', iast: 'Tulāmānam', english: 'Binary Balance System & Chert Stone Weights (13.63 g Base)', category: 'vocab', modernConcept: 'Indus Binary Weight Scale (1:2:4:8:16)', breakdown: 'तुला (balance scales) + मानम् (calibration weight) — Foundation of the 16-anna commercial standard' },
  { id: 'w-koshashri', devanagari: 'कोशश्री', iast: 'Kośaśrī', english: 'Classical Sanskrit Dictionary & Lexicographical Catalog', category: 'vocab', modernConcept: 'Sanskrit Lexicon / Kōshaśrī DC Search', breakdown: 'कोश (lexicon/thesaurus) + श्री (revered knowledge) — Gateway to Paninian roots and meanings' },
  { id: 'w-shabdakosha', devanagari: 'शब्दकोशः', iast: 'Śabdakośaḥ', english: 'Sanskrit Word Treasury, Lexicon & Etymological Dictionary', category: 'vocab', modernConcept: 'Sanskrit Dictionary / Thesaurus', breakdown: 'शब्द (sound/word) + कोशः (treasury/sheath) — Preserved in Amarakośa and Śabdakalpadruma' },
  { id: 'w-deepakam', devanagari: 'दीपकम्', iast: 'Dīpakam', english: 'NCERT Sanskrit Textbook for Classes 7 & 8', category: 'vocab', modernConcept: 'Class 7 & 8 Deepakam Reader & Solutions', breakdown: 'दीपक (illuminator/lamp) — Interactive reader with word-by-word meanings and worksheets' },
  { id: 'w-katapayadi', devanagari: 'कटपयादि', iast: 'Kaṭapayādi', english: 'Alphanumeric Cipher Encoding Numbers into Sanskrit Verses', category: 'vocab', modernConcept: 'Alphanumeric Number Cipher (π & ϕ)', breakdown: 'क, ट, प, य represent digit 1 — Used by Madhava to encode π to 31 decimal places' },
  { id: 'w-beejank', devanagari: 'बीजाङ्कः', iast: 'Bījāṅkaḥ', english: 'Digital Root / Instant Verification Checksum (Mod 9)', category: 'vocab', modernConcept: 'Digital Root / Checksum (Beejank)', breakdown: 'बीज (core/seed) + अङ्कः (digit) — Sum of digits reduced to single digit for instant verification' },
  { id: 'w-shunyam', devanagari: 'शून्यम्', iast: 'Śūnyam', english: 'Zero, Void & Decimal Place-Value Coordinate Grid', category: 'vocab', modernConcept: 'Zero / Decimal Place Value', breakdown: 'शून्यता (void/empty space) + बिन्दु (dot coordinate) — The greatest revolution in numerical thought' },
];

/* ==========================================================================
   BODHI AI MENTOR POSITIONING, SCOPE, LIMITATIONS & SAFEGUARDS
   ========================================================================== */

export interface BodhiSampleInteraction {
  id: string;
  category: string;
  categoryBadge: string;
  tier: 'Guided Lesson Flow' | 'AI / Shastric Retrieval' | 'Interactive Solver' | 'Scope Boundary';
  studentQuestion: string;
  studentContext: string;
  bodhiAnswer: string;
  sanskritAudio?: string;
  keyTakeaway: string;
  escalationNote?: string;
}

export interface BodhiPillar {
  id: string;
  title: string;
  sanskritTitle: string;
  badge: string;
  icon: string;
  description: string;
  capabilities: string[];
  humanTouch?: string;
}

export interface BodhiScopeItem {
  feature: string;
  scopeStatus: 'supported' | 'unsupported';
  description: string;
  alternative?: string;
}

export const BODHI_PILLARS: BodhiPillar[] = [
  {
    id: 'guided-flow',
    title: '1. Guided Lesson Flow',
    sanskritTitle: 'सोपान-पाठ्यक्रम-मार्गदर्शकः',
    badge: 'On-Page Companion',
    icon: '🧭',
    description:
      'An interactive curriculum companion integrated into every page, lesson, and exercise. Bodhi guides your pace, provides slow and natural phonetic pronunciation, and highlights syllables chunk by chunk.',
    capabilities: [
      'Authentic acoustic audio for 13 vowels, 33 consonants, and 400+ barakhadi syllables',
      'Follow-along highlighted chunking for Vedic shlokas and daily spoken phrases',
      'Contextual learning tips across all 15 NCERT Deepakam chapters and 7 Jodo puzzle shelves',
      'Micro-anecdotes explaining ancient Indian astronomy, metrology, and metallurgy',
    ],
  },
  {
    id: 'ai-grounded',
    title: '2. Grounded AI Retrieval',
    sanskritTitle: 'पाणिनीय-सत्यापित-बुद्धिः',
    badge: 'Zero Hallucination',
    icon: '🤖',
    description:
      'AI-assisted semantic search and conversational guidance locked strictly to authentic Pāṇinian grammar (धातुपाठः), NCERT CBSE textbooks, and 16 Vedic Mathematics sutras.',
    capabilities: [
      'Instant answers to 150+ verified student questions on grammar, sandhi, vibhaktis & numbers',
      'Reverse word deconstructor (e.g. गत्वा ➔ गम् + क्त्वा, पठितुम् ➔ पठ् + तुमुन्)',
      'Step-by-step mental math solvers (Nikhilaṁ, Ekādhikena, Ūrdhva-Tiryagbhyām)',
      'Strict zero-hallucination guardrail: never invents non-existent sutras or fabricated shlokas',
    ],
  },
  {
    id: 'human-acharya',
    title: '3. Learner Care Desk',
    sanskritTitle: 'साहाय्य-केन्द्रम्',
    badge: 'Account & Site Help',
    icon: '📧',
    description:
      'EdNet Learn is a self-paced online resource. For account, access, or technical issues, our care desk is here to help.',
    capabilities: [
      'Login, membership, and access issues',
      'Reporting content errors or broken audio',
      'Dedicated learner care desk via email (care@ednetlearn.in)',
    ],
    humanTouch: 'Email: care@ednetlearn.in',
  },
];

export const BODHI_SCOPE_LIMITATIONS: {
  supported: BodhiScopeItem[];
  limitations: BodhiScopeItem[];
} = {
  supported: [
    {
      feature: 'Pāṇinian Grammar & Morphology',
      scopeStatus: 'supported',
      description:
        'Accurate breakdown of 7 Vibhaktis (cases), 5 Lakāra tenses (Lat, Lang, Lit, Lot, Vidhilin), Sandhi junctions, and Kṛt pratyayas with authentic sutra citations.',
    },
    {
      feature: 'NCERT Deepakam & CBSE Support',
      scopeStatus: 'supported',
      description:
        'Line-by-line shloka anvaya (श्लोकान्वयः), word-by-word glosses in Sanskrit/Hindi/English, and CBSE chapter exercise blueprints for Classes 6–10.',
    },
    {
      feature: 'Phonetic Articulation & Audio',
      scopeStatus: 'supported',
      description:
        'Native audio with slow/normal pace options, vocal tract articulation points (कण्ठ, तालु, मूर्धा, दन्त, ओष्ठ), and authentic visarga echo reflections.',
    },
    {
      feature: '16 Vedic Mathematics Sutras',
      scopeStatus: 'supported',
      description:
        'Step-by-step interactive visual solvers for mental multiplication, squaring, division, and Beejank (digital root) verification.',
    },
    {
      feature: 'Student Q&A & Anecdotes',
      scopeStatus: 'supported',
      description:
        'Searchable library of 150+ frequently asked questions and fascinating historical science anecdotes (Indus metrology, Ujjain geodesy, Tamil calculators).',
    },
  ],
  limitations: [
    {
      feature: 'Subjective Essay & Handwriting Grading',
      scopeStatus: 'unsupported',
      description:
        'Bodhi cannot automatically grade handwritten Sanskrit essays or free-form creative poetry without human oversight.',
      alternative: 'Out of scope: handwritten paper grading is not offered; type sentences into Bodhi for grammar checks.',
    },
    {
      feature: 'Accredited Board Certification',
      scopeStatus: 'unsupported',
      description:
        'Bodhi is an interactive mentor and study companion; it does not issue state board or CBSE-certified diplomas.',
      alternative: 'Prepares students for school exams with CBSE blueprint worksheets and mock tests.',
    },
    {
      feature: 'Unconstrained Open-Ended Chat',
      scopeStatus: 'unsupported',
      description:
        'Unlike generic ChatGPT, Bodhi will not hallucinate or chat about off-topic domains (e.g. pop culture, politics, unrelated subjects).',
      alternative: 'Keeps young learners safe, focused, and locked to shastric Sanskrit and Vedic Math.',
    },
    {
      feature: 'Child Data Profiling',
      scopeStatus: 'unsupported',
      description:
        'Bodhi never tracks, sells, or monetizes student conversation history for behavioral advertising.',
      alternative: '100% ad-free, COPPA/GDPR-K privacy-first architecture.',
    },
  ],
};

export const BODHI_SAMPLE_INTERACTIONS: BodhiSampleInteraction[] = [
  {
    id: 'sample-grammar-purusha',
    category: 'CBSE Class 7 Grammar',
    categoryBadge: 'Lakāra & Puruṣa',
    tier: 'AI / Shastric Retrieval',
    studentQuestion: 'Why do we say "बालकः गच्छति" (bālakaḥ gacchati) but "वयं गच्छामः" (vayaṁ gacchāmaḥ)?',
    studentContext: 'Student confused between 3rd person singular and 1st person plural verb endings.',
    bodhiAnswer:
      'In Sanskrit, the verb ending must agree with the Subject in both Puruṣa (Person) and Vacana (Number)!\n\n1. "बालकः" (a boy) is Prathama-Puruṣa (Third Person) and Eka-Vacana (Singular). The present tense (लट् लकार) ending is "-ति" ➔ गम् ➔ गच्छति।\n\n2. "वयम्" (we all) is Uttama-Puruṣa (First Person) and Bahu-Vacana (Plural). The ending is "-मः" ➔ गम् ➔ गच्छामः।\n\nRemember Bodhi’s golden rule: "ति तः अन्ति... सि थः थ... मि वः मः!" Match the subject row to the verb row!',
    sanskritAudio: 'बालकः गच्छति । वयं गच्छामः । ति तः अन्ति । मि वः मः ।',
    keyTakeaway: 'Verb endings follow a rigorous 3×3 matrix based on Puruṣa and Vacana.',
  },
  {
    id: 'sample-ncert-word-breakdown',
    category: 'NCERT Deepakam Chapter 1',
    categoryBadge: 'Morphology & Anvaya',
    tier: 'Guided Lesson Flow',
    studentQuestion: 'What does "सुभाषितानि" mean in the chapter title, and how is it constructed?',
    studentContext: 'Class 7 student starting Chapter 1: सुभाषितानि (Noble Sayings).',
    bodhiAnswer:
      'What a wonderful word to begin your journey! "सुभाषितानि" (Subhāṣitāni) breaks down into three sacred building blocks:\n\n1. सु (su) — Auspicious prefix meaning "good, noble, virtuous, graceful".\n2. √भाष् (bhāṣ) — Dhātupāṭha root meaning "to speak, utter, articulate".\n3. क्त (kta) — Past passive participle suffix forming "भाषितम्" (that which is well-spoken).\n\nWhen made Neuter Plural (नपुंसकलिङ्ग बहुवचन) like "फलानि", it becomes "सुभाषितानि" — Wise, uplifting verses crafted by ancient sages to guide daily conduct!',
    sanskritAudio: 'सुभाषितम् । सु उपसर्गः भाष् धातुः क्त प्रत्ययः । सुभाषितानि ।',
    keyTakeaway: 'Prefix (सु) + Root (भाष्) + Suffix (क्त) combine like Lego blocks to create meaning.',
  },
  {
    id: 'sample-vedic-math-speed',
    category: 'Vedic Mathematics',
    categoryBadge: '16 Sutras Solvers',
    tier: 'Interactive Solver',
    studentQuestion: 'Can I multiply 96 × 94 in my head in 3 seconds without pen and paper?',
    studentContext: 'Student looking for mental arithmetic shortcuts for school speed tests.',
    bodhiAnswer:
      'Yes, absolutely! We use Sutra 2: "Nikhilaṁ Navataścaramaṁ Daśataḥ" (All from 9 and last from 10) with Base 100:\n\nStep 1: Find the deficiencies from 100:\n• 96 is (100 - 4) ➔ -04\n• 94 is (100 - 6) ➔ -06\n\nStep 2: Cross-subtract either way for Left Part:\n• 96 - 6 = 90 (or 94 - 4 = 90)\n\nStep 3: Multiply the deficiencies for Right Part:\n• (-4) × (-6) = +24\n\nCombine both parts: 90 | 24 ➔ 9024! Faster than a calculator!',
    sanskritAudio: 'निखिलं नवतश्चरमं दशतः । षण्णवतिः गुणितं चतुर्णवतिः । चतुर्विंशत्यधिक-नवसहस्रम् । ९०२४।',
    keyTakeaway: 'Base complement arithmetic turns multi-digit multiplication into simple subtraction.',
  },
  {
    id: 'sample-escalation-essay',
    category: 'Scope Boundary & Escalation',
    categoryBadge: 'Scope Boundary',
    tier: 'Scope Boundary',
    studentQuestion: 'Can Bodhi grade my 5-sentence handwritten essay on "मम विद्यालयः" for CBSE exams?',
    studentContext: 'Student submitting a full handwritten composition with spelling and handwriting questions.',
    bodhiAnswer:
      'Here is where Bodhi’s scope ends!\n\n• What I can do instantly on-screen: I can check your Sandhi rules, verify dictionary spellings, and confirm case endings (e.g. "मम विद्यालये एका विशाला ग्रन्थालयः अस्ति" ➔ correction to "एकम् विशालं ग्रन्थागारम् अस्ति").\n\n• For your handwritten essay grading: Automated tools cannot accurately evaluate handwritten scripts or CBSE subjective marking schemes. Type your sentences into Bodhi one by one for grammar checks, and use the CBSE guide’s marking blueprint to self-assess.',
    sanskritAudio: 'मम विद्यालयः ।',
    keyTakeaway: 'Bodhi verifies grammar rules sentence by sentence; handwritten grading is outside its scope.',
  },
];
